// src/schemas/common.ts
import { z } from "zod";
import { ALL_ERROR_CODES } from "@restaurant/types";
var CuidSchema = z.string().regex(/^[a-z0-9]{20,32}$/, "Invalid id");
var EmailSchema = z.email();
var PhoneSchema = z.string().min(1).max(20);
var DateTimeSchema = z.iso.datetime();
var DateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
var TimeSchema = z.string().regex(/^\d{2}:\d{2}$/);
var PositiveIntSchema = z.number().int().positive();
var NonNegativeIntSchema = z.number().int().nonnegative();
var CursorSchema = z.string().optional();
var LimitSchema = z.number().int().min(1).max(100).default(20);
var LocalizedTextSchema = z.record(z.string(), z.string());
var PaginationQuerySchema = z.object({
  cursor: CursorSchema,
  limit: LimitSchema
});
var PaginatedResponseSchema = (itemSchema) => z.object({
  items: z.array(itemSchema),
  nextCursor: z.string().nullable(),
  total: z.number().int().nonnegative().optional()
});
var LanguageSchema = z.enum(["en", "vi", "de", "pl"]);
var ErrorResponseSchema = z.object({
  code: z.enum(ALL_ERROR_CODES),
  message: z.string(),
  params: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
  fieldErrors: z.record(z.string(), z.string()).optional()
});
var normalizeAddressPart = (v) => v.trim().replace(/\s+/g, " ").replace(/^[,;]+|[,;]+$/g, "").trim();
var addressPart = (max) => z.string().max(max).transform(normalizeAddressPart);
var requiredAddressPart = (max) => z.string().max(max).transform(normalizeAddressPart).refine((v) => v.length > 0, { error: "Required" });

// src/schemas/auth.ts
import { FranchiseRole, RestaurantRole, SystemRole } from "@restaurant/types";
import { z as z2 } from "zod";
var SystemRoleSchema = z2.enum(SystemRole);
var FranchiseRoleSchema = z2.enum(FranchiseRole);
var RestaurantRoleSchema = z2.enum(RestaurantRole);
var MeRestaurantAccessSchema = z2.object({
  restaurantId: CuidSchema,
  restaurantName: z2.string(),
  role: RestaurantRoleSchema.nullable()
});
var UserContextSchema = z2.object({
  userId: CuidSchema,
  email: EmailSchema,
  language: LanguageSchema.optional(),
  systemRole: SystemRoleSchema.nullable(),
  // One entry per franchise the user can access, from any source: Worker
  // memberships, partner-group membership, or system-admin (all franchises).
  workers: z2.array(
    z2.object({
      // null when the franchise is reached via partner-group / system-admin
      // access rather than a Worker record.
      workerId: CuidSchema.nullable(),
      franchiseId: CuidSchema,
      franchiseName: z2.string(),
      franchiseRole: FranchiseRoleSchema.nullable(),
      partnerGroupAccess: z2.boolean().default(false),
      restaurants: z2.array(MeRestaurantAccessSchema)
    })
  ),
  rules: z2.array(z2.tuple([z2.string(), z2.string()]).rest(z2.unknown())).optional()
});
var UpdateMeSchema = z2.object({
  language: LanguageSchema
});
var UpdateMeResponseSchema = z2.object({
  language: LanguageSchema
});

// src/schemas/franchise.ts
import { z as z3 } from "zod";
var FranchiseSchema = z3.object({
  id: CuidSchema,
  name: z3.string(),
  slug: z3.string(),
  description: z3.string().nullable(),
  timezone: z3.string(),
  currency: z3.string().length(3),
  defaultLanguage: LanguageSchema,
  isActive: z3.boolean(),
  restaurantCount: z3.number().int().nonnegative(),
  createdAt: z3.iso.datetime(),
  updatedAt: z3.iso.datetime()
});
var FranchiseUpdateSchema = z3.object({
  name: z3.string().min(1).max(100).optional(),
  description: z3.string().max(500).nullable().optional(),
  timezone: z3.string().optional(),
  currency: z3.string().length(3).optional(),
  defaultLanguage: LanguageSchema.optional(),
  isActive: z3.boolean().optional()
});
var FranchiseParamsSchema = z3.object({ franchiseId: CuidSchema });
var FranchiseListQuerySchema = z3.object({
  limit: z3.coerce.number().int().min(1).max(100).optional()
});
var FranchiseListResponseSchema = z3.object({
  items: z3.array(FranchiseSchema),
  nextCursor: z3.string().nullable(),
  total: z3.number().int().nonnegative()
});
var FranchiseHardDeleteResultSchema = z3.object({
  success: z3.boolean(),
  deletedUsers: z3.number().int().nonnegative()
});

// src/schemas/restaurant.ts
import { DayOfWeek, THEME_IDS, DEFAULT_THEME_ID } from "@restaurant/types";
import { z as z4 } from "zod";
var ServiceTypeSchema = z4.enum(["BASE", "PICKUP", "DELIVERY", "RESERVATION"]);
var ExceptionTypeSchema = z4.enum(["CLOSED", "MODIFIED"]);
var ThemeIdSchema = z4.enum(THEME_IDS);
var DayOfWeekSchema = z4.union([
  z4.literal(DayOfWeek.SUNDAY),
  z4.literal(DayOfWeek.MONDAY),
  z4.literal(DayOfWeek.TUESDAY),
  z4.literal(DayOfWeek.WEDNESDAY),
  z4.literal(DayOfWeek.THURSDAY),
  z4.literal(DayOfWeek.FRIDAY),
  z4.literal(DayOfWeek.SATURDAY)
]);
var RestaurantSchema = z4.object({
  id: CuidSchema,
  franchiseId: CuidSchema,
  name: z4.string().min(1).max(100),
  slug: z4.string().min(1).max(50),
  logo: z4.url().nullable(),
  coverImage: z4.url().nullable(),
  logoKey: z4.string().nullable(),
  coverImageKey: z4.string().nullable(),
  galleryKeys: z4.array(z4.string()),
  // Stored on the Prisma row as `email` / `phone`; the read path maps them to
  // these contract names (the write path already speaks contactEmail/Phone).
  contactEmail: EmailSchema.nullable(),
  contactPhone: PhoneSchema.nullable(),
  // Structured postal address. Nullable — legacy rows and map-pin-only setups
  // may not have every part filled (mirrors the Prisma columns, which are all
  // optional except `country`, so the response never over-promises).
  address: z4.string().max(200).nullable(),
  city: z4.string().max(100).nullable(),
  postalCode: z4.string().max(20).nullable(),
  country: z4.string().length(2),
  latitude: z4.number().min(-90).max(90).nullable(),
  longitude: z4.number().min(-180).max(180).nullable(),
  /**
   * Public web/social presence. Always a full absolute URL when set — the write
   * edge normalizes handles ("@genji") and host-less pastes before storing — so
   * readers can render an <a href> straight from this value. Null = the owner
   * hasn't set that channel.
   */
  facebookUrl: z4.url().nullable(),
  instagramUrl: z4.url().nullable(),
  tiktokUrl: z4.url().nullable(),
  websiteUrl: z4.url().nullable(),
  timezone: z4.string(),
  /**
   * Storefront visual theme id. Validated against the registry in
   * `apps/restaurant-storefront/lib/themes.ts` at the edges; unknown values
   * fall back to the default theme at render time.
   */
  themeId: z4.string().default(DEFAULT_THEME_ID),
  currency: z4.string().length(3),
  isActive: z4.boolean(),
  /** Whether the AI assistant is activated for this restaurant (managed in the partner portal). */
  aiChatEnabled: z4.boolean().default(false),
  createdAt: z4.iso.datetime(),
  updatedAt: z4.iso.datetime()
});
var RestaurantUpdateSchema = z4.object({
  name: z4.string().min(1).max(100).optional(),
  logoKey: z4.string().max(500).nullable().optional(),
  coverImageKey: z4.string().max(500).nullable().optional(),
  galleryKeys: z4.array(z4.string().max(500)).max(9).refine((arr) => new Set(arr).size === arr.length, "duplicate keys").optional(),
  contactEmail: EmailSchema.nullable().optional(),
  contactPhone: PhoneSchema.nullable().optional(),
  /**
   * Web/social presence. Deliberately looser than the read schema's `.url()`:
   * the owner may type a bare handle ("@genji") or a host-less paste
   * ("instagram.com/genji"), and the backend normalizes those to a full URL
   * before storing, enforcing the per-platform host allowlist as it goes.
   * `null` clears the link; omitting the key leaves it unchanged.
   */
  facebookUrl: z4.string().max(300).nullable().optional(),
  instagramUrl: z4.string().max(300).nullable().optional(),
  tiktokUrl: z4.string().max(300).nullable().optional(),
  websiteUrl: z4.string().max(300).nullable().optional(),
  address: z4.string().max(200).optional(),
  city: z4.string().max(100).optional(),
  postalCode: z4.string().max(20).optional(),
  country: z4.string().length(2).optional(),
  latitude: z4.number().min(-90).max(90).nullable().optional(),
  longitude: z4.number().min(-180).max(180).nullable().optional(),
  timezone: z4.string().optional(),
  isActive: z4.boolean().optional(),
  // Operator-chosen storefront theme. Rejected here if not a known preset.
  themeId: ThemeIdSchema.optional()
});
var RestaurantAccessStateSchema = z4.object({
  suspended: z4.boolean(),
  reason: z4.string().nullable(),
  contactPhone: z4.string().nullable()
});
var SuspendRestaurantBodySchema = z4.object({
  reason: z4.string().trim().min(1).max(1e3),
  contactPhone: z4.string().trim().max(40).nullable().optional()
});
var ShiftSchema = z4.object({
  open: TimeSchema,
  close: TimeSchema
});
var OpeningHoursSchema = z4.object({
  id: CuidSchema,
  restaurantId: CuidSchema,
  service: ServiceTypeSchema,
  dayOfWeek: DayOfWeekSchema,
  shifts: z4.array(ShiftSchema),
  isEnabled: z4.boolean()
});
var DayOverrideStateSchema = z4.enum(["NONE", "CLOSED", "MODIFIED", "HOLIDAY"]);
var DayOverrideSchema = z4.object({
  type: DayOverrideStateSchema,
  // Shifts for MODIFIED and HOLIDAY; null for NONE and CLOSED.
  shifts: z4.array(ShiftSchema).nullable(),
  // For NONE: null. CLOSED/MODIFIED: exception reason. HOLIDAY: holiday name.
  label: z4.string().nullable()
});
var WeekOfDayOverridesSchema = z4.array(DayOverrideSchema).length(7);
var WeekOverridesSchema = z4.object({
  BASE: WeekOfDayOverridesSchema,
  PICKUP: WeekOfDayOverridesSchema,
  DELIVERY: WeekOfDayOverridesSchema,
  RESERVATION: WeekOfDayOverridesSchema
});
var PublicRestaurantSchema = RestaurantSchema.extend({
  reservationsEnabled: z4.boolean(),
  // True while the operator has temporarily paused new online bookings
  // (ReservationConfig.pausedUntil is set and still in the future). The
  // storefront shows a "not available right now" notice instead of the form.
  reservationsPaused: z4.boolean(),
  // Booking-form field gates (from ReservationConfig) so the storefront can
  // decide whether to show the occasion / seating-preference pickers.
  occasionEnabled: z4.boolean(),
  seatingPreferenceEnabled: z4.boolean(),
  // Largest party the storefront may book online (ReservationConfig.maxPartySize).
  // The server rejects guest bookings above this, so the storefront caps the party
  // stepper here and shows largePartyMsg once the limit is reached.
  maxPartySize: z4.number().int().min(1),
  // How many days ahead guests may book (ReservationConfig.advanceWindow). The
  // storefront sizes its date strip to this so the bookable horizon matches what
  // the server enforces, instead of a fixed window.
  advanceWindow: z4.number().int().min(1),
  // Operator's "for larger parties, call us" message (ReservationConfig.largePartyMsg).
  // Null when the operator cleared it — the storefront falls back to a generic note.
  largePartyMsg: z4.string().nullable(),
  onlineOrderingEnabled: z4.boolean(),
  // Per-channel orderability, so the storefront can grey out / disable a single
  // channel (pickup OR delivery) that's turned off or currently paused — instead
  // of only knowing the aggregate `onlineOrderingEnabled`. `available` = enabled
  // AND not paused right now. The backend still enforces this authoritatively on
  // POST /orders (a tampered client can't bypass it); this is purely for UX.
  orderChannels: z4.array(
    z4.object({
      orderType: z4.enum(["PICKUP", "DELIVERY"]),
      available: z4.boolean(),
      enabled: z4.boolean(),
      paused: z4.boolean(),
      pausedUntil: z4.string().nullable(),
      // Open for orders RIGHT NOW (within an open window in the restaurant tz AND
      // available). Drives the storefront's "accept ASAP / closed today" gate.
      openNow: z4.boolean(),
      // Orderable at all today = open now OR a later shift still starts today
      // (same-day pre-order). False = fully closed today → the storefront blocks
      // entry (hides/disables "Start order", shows a closed notice) instead of
      // only failing at checkout. No multi-day scheduling exists.
      orderableToday: z4.boolean(),
      // Minimum order value for this channel (minor units; 0 = no minimum). The
      // storefront blocks checkout below it (delivery may be further raised by a
      // zone-specific minimum from the delivery quote).
      minOrderCents: z4.number().int().nonnegative(),
      // Set when a holiday/closure exception (the universal Exceptions setting)
      // shuts this channel for the whole day today; null otherwise. The
      // storefront uses it to block entry/checkout with a holiday explanation
      // (`reason` is the operator's note, or null). This is why orderableToday
      // may be false even though the channel is enabled and not paused.
      holidayClosure: z4.object({
        reason: z4.string().nullable()
      }).nullable()
    })
  ),
  // Mode-aware delivery-fee breakdown shown on the storefront order header (the
  // info card, between the address and the opening times). `mode` mirrors the
  // DELIVERY OrderTypeConfig.deliveryPriceMode: DISTANCE = haversine distance
  // tiers (each zone carries its `maxDistanceKm` upper bound); ZONE = named
  // polygon areas (`maxDistanceKm` is null). Null when delivery isn't configured
  // or has no active zones — the storefront then shows nothing. This is a
  // preview only: the server still authoritatively quotes the exact fee (and any
  // zone-specific minimum) once the guest enters their address.
  deliveryFee: z4.object({
    mode: z4.enum(["DISTANCE", "ZONE"]),
    zones: z4.array(
      z4.object({
        label: z4.string(),
        feeCents: z4.number().int().nonnegative(),
        minOrderCents: z4.number().int().nonnegative(),
        // Upper distance bound of this tier in km (DISTANCE mode); null for ZONE mode.
        maxDistanceKm: z4.number().nonnegative().nullable()
      })
    )
  }).nullable(),
  openingHours: z4.array(OpeningHoursSchema),
  // Today's active "holiday hours" override per order channel (0–2 entries).
  // When present for a channel, the storefront shows these hours instead of the
  // weekly opening hours. See docs/ONLINE_ORDER_CAPACITY.md.
  orderHolidayHoursToday: z4.array(
    z4.object({
      orderType: z4.enum(["PICKUP", "DELIVERY"]),
      name: z4.string(),
      openTime: z4.string(),
      closeTime: z4.string()
    })
  ),
  // Today's general (walk-in / BASE) full-day closure from an opening-hours
  // exception, if any; null when the restaurant follows its normal weekly hours.
  // The storefront surfaces `reason` (the operator's note, or null) on the info
  // card's hours header so a holiday closure explains itself instead of the
  // header wrongly reading "Open"/"Closed" straight from the weekly schedule.
  todayClosure: z4.object({
    reason: z4.string().nullable()
  }).nullable(),
  // Per-day exception/holiday overrides for the current Mon–Sun week in the
  // restaurant's timezone. Keyed by service type; each array is indexed by
  // dayOfWeek (0=Sun…6=Sat). NONE entries mean "use base openingHours".
  weekOverrides: WeekOverridesSchema
});
var BulkOpeningHoursSchema = z4.object({
  service: ServiceTypeSchema,
  schedule: z4.array(
    z4.object({
      dayOfWeek: DayOfWeekSchema,
      shifts: z4.array(ShiftSchema),
      isEnabled: z4.boolean()
    })
  )
});
var ExceptionSchema = z4.object({
  id: CuidSchema,
  restaurantId: CuidSchema,
  // The services this exception applies to (never empty). "All services" is all
  // four values present — no null/empty magic value.
  services: z4.array(ServiceTypeSchema),
  type: ExceptionTypeSchema,
  startDate: DateSchema,
  endDate: DateSchema,
  shifts: z4.array(ShiftSchema).nullable(),
  // null for CLOSED type
  reason: z4.string().max(200).nullable(),
  createdAt: z4.iso.datetime()
});
var ExceptionCreateSchema = z4.object({
  // At least one service; the dashboard defaults to all four ("all services").
  services: z4.array(ServiceTypeSchema).min(1),
  type: ExceptionTypeSchema,
  startDate: DateSchema,
  endDate: DateSchema,
  shifts: z4.array(ShiftSchema).nullable().optional(),
  reason: z4.string().max(200).optional()
});
var ExceptionUpdateSchema = z4.object({
  // At least one service when changing scope; omit to leave it unchanged.
  services: z4.array(ServiceTypeSchema).min(1).optional(),
  type: ExceptionTypeSchema.optional(),
  startDate: DateSchema.optional(),
  endDate: DateSchema.optional(),
  shifts: z4.array(ShiftSchema).nullable().optional(),
  reason: z4.string().max(200).nullable().optional()
});
var EXCEPTION_CHANGESET_MAX = 50;
var ExceptionValueSchema = ExceptionCreateSchema.extend({
  shifts: z4.array(ShiftSchema).nullable(),
  reason: z4.string().max(200).nullable()
});
var ExceptionChangeSchema = z4.discriminatedUnion("kind", [
  ExceptionValueSchema.extend({
    kind: z4.literal("exceptionCreated"),
    exceptionId: CuidSchema
  }),
  z4.object({
    kind: z4.literal("exceptionUpdated"),
    exceptionId: CuidSchema,
    before: ExceptionValueSchema,
    after: ExceptionValueSchema
  }),
  z4.object({
    kind: z4.literal("exceptionRemoved"),
    exceptionId: CuidSchema,
    before: ExceptionValueSchema
  })
]);
var ExceptionChangesSchema = z4.array(ExceptionChangeSchema).max(EXCEPTION_CHANGESET_MAX);
var ExceptionChangesetStaleSchema = z4.object({ stale: ExceptionChangesSchema });
var RestaurantListQuerySchema = z4.object({
  franchiseId: CuidSchema,
  limit: z4.coerce.number().int().min(1).max(100).optional()
});
var RestaurantListResponseSchema = z4.object({
  items: z4.array(RestaurantSchema),
  nextCursor: z4.string().nullable(),
  total: z4.number().int().nonnegative()
});
var RestaurantParamsSchema = z4.object({ restaurantId: CuidSchema });
var RestaurantSlugParamsSchema = z4.object({
  franchiseSlug: z4.string(),
  restaurantSlug: z4.string()
});
var OpeningHoursServiceParamsSchema = RestaurantParamsSchema.extend({
  service: ServiceTypeSchema
});
var ExceptionParamsSchema = RestaurantParamsSchema.extend({ exceptionId: CuidSchema });
var OpeningHoursQuerySchema = z4.object({ service: ServiceTypeSchema.optional() });
var ExceptionListQuerySchema = z4.object({
  service: ServiceTypeSchema.optional(),
  fromDate: DateSchema.optional(),
  toDate: DateSchema.optional()
});

// src/schemas/menu.ts
import { Allergen, PRODUCT_CODE_MAX_LENGTH, ProductLabel } from "@restaurant/types";
import { z as z5 } from "zod";
var AllergenSchema = z5.enum(Allergen);
var ProductLabelSchema = z5.enum(ProductLabel);
var AllergenCodeKindSchema = z5.enum(["ALLERGEN", "ADDITIVE", "DIETARY"]);
var ALLERGEN_SHORTKEY_MAX = 24;
var AllergenShortKeySchema = z5.string().trim().min(1).max(ALLERGEN_SHORTKEY_MAX);
var AllergenTableEntrySchema = z5.object({
  shortKey: AllergenShortKeySchema,
  description: z5.string().trim().max(1e3).describe("Meaning of the code; may be empty until the owner fills their printed legend"),
  kind: AllergenCodeKindSchema
});
var AllergenTableSchema = z5.array(AllergenTableEntrySchema).max(200);
var SetAllergenTableSchema = z5.object({ entries: AllergenTableSchema });
var AllergenTableEntryViewSchema = z5.object({
  shortKey: z5.string(),
  description: z5.string(),
  kind: AllergenCodeKindSchema
});
var AllergenTableViewSchema = z5.array(AllergenTableEntryViewSchema);
var AllergenSheetConfigSchema = z5.object({
  sheetText: z5.string().nullable(),
  sheetImageKey: z5.string().nullable(),
  sheetImageUrl: z5.string().nullable(),
  advanced: z5.boolean()
});
var SetAllergenSheetSchema = z5.object({
  sheetText: z5.string().max(5e3).nullable().optional(),
  sheetImageKey: z5.string().max(500).nullable().optional(),
  advanced: z5.boolean().optional()
});
var ParseAllergenSheetSchema = z5.object({
  text: z5.string().max(2e4).nullable().optional(),
  imageBase64: z5.string().nullable().optional(),
  imageMimeType: z5.string().max(100).nullable().optional()
});
var AllergenSheetParseResultSchema = z5.object({ entries: AllergenTableSchema });
var MenuChannelSchema = z5.enum(["DINE_IN", "PICKUP", "DELIVERY"]);
var MenuLanguageSchema = LanguageSchema;
var LocalizedStringSchema = LocalizedTextSchema.refine(
  (v) => typeof v.en === "string" && v.en.length > 0,
  { error: 'must include a non-empty English ("en") value' }
);
var LocalizedStringOptionalSchema = LocalizedTextSchema;
var ProductCodeSchema = z5.string().trim().max(PRODUCT_CODE_MAX_LENGTH).regex(/^[\p{L}0-9 ._\-/]*$/u, "Code may use letters, digits, spaces and . _ - /");
var CategoryScheduleWindowSchema = z5.object({
  /** JS-style day numbers, 0 = Sunday … 6 = Saturday. */
  days: z5.array(z5.number().int().min(0).max(6)).min(1),
  startTime: z5.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  endTime: z5.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)
});
var CategoryScheduleSchema = z5.object({ windows: z5.array(CategoryScheduleWindowSchema) });
var ReorderSchema = z5.object({ orderedIds: z5.array(CuidSchema).min(1) });
var SuccessSchema = z5.object({ success: z5.boolean() });
var MenuRestaurantParamsSchema = z5.object({ restaurantId: CuidSchema });
var MenuParamsSchema = z5.object({ menuId: CuidSchema });
var MenuActivationParamsSchema = z5.object({
  restaurantId: CuidSchema,
  channel: MenuChannelSchema
});
var CategoryParamsSchema = z5.object({ categoryId: CuidSchema });
var ProductParamsSchema = z5.object({ productId: CuidSchema });
var OptionParamsSchema = z5.object({ optionId: CuidSchema });
var ChoiceParamsSchema = z5.object({ choiceId: CuidSchema });
var PriceListParamsSchema = z5.object({ priceListId: CuidSchema });
var PriceListEntryParamsSchema = z5.object({ entryId: CuidSchema });
var CustomerMenuQuerySchema = z5.object({
  lang: MenuLanguageSchema.default("en"),
  channel: MenuChannelSchema.default("DINE_IN")
});
var StoredIntSchema = z5.number().int();
var MenuSchema = z5.object({
  id: CuidSchema,
  restaurantId: CuidSchema,
  franchiseId: CuidSchema,
  name: LocalizedTextSchema,
  /** Channels this menu is currently the LIVE menu for (derived from activations). */
  liveChannels: z5.array(MenuChannelSchema),
  /** Convenience: live on at least one channel. */
  isActive: z5.boolean(),
  createdAt: z5.iso.datetime(),
  updatedAt: z5.iso.datetime()
});
var MenuActivationSchema = z5.object({
  channel: MenuChannelSchema,
  menuId: CuidSchema
});
var SetMenuActivationSchema = z5.object({ menuId: CuidSchema.nullable() });
var CategorySchema = z5.object({
  id: CuidSchema,
  menuId: CuidSchema,
  name: LocalizedTextSchema,
  description: LocalizedTextSchema.nullable(),
  imageUrl: LocalizedTextSchema.nullable(),
  schedule: z5.unknown().optional(),
  sortOrder: StoredIntSchema,
  isActive: z5.boolean(),
  // Order channels this section is HIDDEN on (e.g. ["DELIVERY"] = shown for
  // pickup/dine-in only). Empty = visible everywhere.
  hiddenChannels: z5.array(MenuChannelSchema)
});
var OptionChoiceSchema = z5.object({
  id: CuidSchema,
  optionId: CuidSchema,
  name: LocalizedTextSchema,
  price: StoredIntSchema,
  allergens: z5.array(z5.string()),
  allergenCodes: z5.array(z5.string()),
  isDefault: z5.boolean(),
  isActive: z5.boolean(),
  inStock: z5.boolean(),
  sortOrder: StoredIntSchema
});
var ProductOptionSchema = z5.object({
  id: CuidSchema,
  productId: CuidSchema,
  name: LocalizedTextSchema,
  isRequired: z5.boolean(),
  isMultiple: z5.boolean(),
  minSelect: StoredIntSchema,
  maxSelect: StoredIntSchema.nullable(),
  sortOrder: StoredIntSchema,
  choices: z5.array(OptionChoiceSchema)
});
var ProductSchema = z5.object({
  id: CuidSchema,
  categoryId: CuidSchema,
  code: z5.string().nullable(),
  name: LocalizedTextSchema,
  description: LocalizedTextSchema.nullable(),
  imageUrl: LocalizedTextSchema.nullable(),
  schedule: z5.unknown().optional(),
  price: StoredIntSchema,
  allergens: z5.array(z5.string()),
  allergenCodes: z5.array(z5.string()),
  labels: z5.array(z5.string()),
  sortOrder: StoredIntSchema,
  isActive: z5.boolean(),
  inStock: z5.boolean(),
  options: z5.array(ProductOptionSchema)
});
var MenuTreeSchema = MenuSchema.extend({
  categories: z5.array(CategorySchema.extend({ products: z5.array(ProductSchema) }))
});
var ArchivedProductSchema = z5.object({
  id: CuidSchema,
  categoryId: CuidSchema,
  name: LocalizedTextSchema,
  price: StoredIntSchema,
  archivedAt: z5.iso.datetime()
});
var MENU_CHANGESET_MAX = 200;
var MENU_TEXT_MAX = 5e3;
var MENU_ORDER_MAX = 2e3;
var MenuTextSchema = z5.string().max(MENU_TEXT_MAX);
var MenuNameSchema = MenuTextSchema.min(1);
var OrderSchema = z5.array(CuidSchema).max(MENU_ORDER_MAX);
var CodesSchema = z5.array(AllergenShortKeySchema).max(100);
var LabelsSchema = z5.array(ProductLabelSchema);
var ScheduleValueSchema = CategoryScheduleSchema.nullable();
var ChannelPriceSchema = NonNegativeIntSchema.nullable();
var MenuOverrideChannelSchema = z5.enum(["PICKUP", "DELIVERY"]);
var OptionRulesSchema = z5.object({
  isRequired: z5.boolean(),
  isMultiple: z5.boolean(),
  minSelect: NonNegativeIntSchema,
  maxSelect: PositiveIntSchema.nullable()
});
var MenuChangeSchema = z5.discriminatedUnion("kind", [
  z5.object({
    kind: z5.literal("productPrice"),
    productId: CuidSchema,
    before: NonNegativeIntSchema,
    after: NonNegativeIntSchema
  }),
  z5.object({
    kind: z5.literal("productAvailability"),
    productId: CuidSchema,
    before: z5.boolean(),
    after: z5.boolean()
  }),
  z5.object({
    kind: z5.literal("productActive"),
    productId: CuidSchema,
    before: z5.boolean(),
    after: z5.boolean()
  }),
  z5.object({
    kind: z5.literal("productName"),
    productId: CuidSchema,
    before: MenuNameSchema,
    after: MenuNameSchema
  }),
  z5.object({
    kind: z5.literal("productDescription"),
    productId: CuidSchema,
    before: MenuTextSchema,
    after: MenuTextSchema
  }),
  z5.object({
    kind: z5.literal("productAllergenCodes"),
    productId: CuidSchema,
    before: CodesSchema,
    after: CodesSchema
  }),
  z5.object({
    kind: z5.literal("productLabels"),
    productId: CuidSchema,
    before: LabelsSchema,
    after: LabelsSchema
  }),
  z5.object({
    kind: z5.literal("productSchedule"),
    productId: CuidSchema,
    before: ScheduleValueSchema,
    after: ScheduleValueSchema
  }),
  z5.object({
    kind: z5.literal("productRemoved"),
    productId: CuidSchema,
    before: z5.boolean(),
    after: z5.boolean()
  }),
  z5.object({
    kind: z5.literal("productChannelPrice"),
    productId: CuidSchema,
    channel: MenuOverrideChannelSchema,
    before: ChannelPriceSchema,
    after: ChannelPriceSchema
  }),
  z5.object({
    kind: z5.literal("productCreate"),
    productId: CuidSchema,
    categoryId: CuidSchema,
    name: MenuNameSchema,
    description: MenuTextSchema,
    price: NonNegativeIntSchema,
    allergenCodes: CodesSchema,
    labels: LabelsSchema
  }),
  z5.object({
    kind: z5.literal("productOrder"),
    categoryId: CuidSchema,
    before: OrderSchema,
    after: OrderSchema
  }),
  z5.object({
    kind: z5.literal("categoryName"),
    categoryId: CuidSchema,
    before: MenuNameSchema,
    after: MenuNameSchema
  }),
  z5.object({
    kind: z5.literal("categoryActive"),
    categoryId: CuidSchema,
    before: z5.boolean(),
    after: z5.boolean()
  }),
  z5.object({
    kind: z5.literal("categorySchedule"),
    categoryId: CuidSchema,
    before: ScheduleValueSchema,
    after: ScheduleValueSchema
  }),
  z5.object({
    kind: z5.literal("categoryRemoved"),
    categoryId: CuidSchema,
    before: z5.boolean(),
    after: z5.boolean()
  }),
  z5.object({
    kind: z5.literal("categoryCreate"),
    categoryId: CuidSchema,
    name: MenuNameSchema
  }),
  z5.object({
    kind: z5.literal("categoryOrder"),
    menuId: CuidSchema,
    before: OrderSchema,
    after: OrderSchema
  }),
  z5.object({
    kind: z5.literal("optionName"),
    optionId: CuidSchema,
    before: MenuNameSchema,
    after: MenuNameSchema
  }),
  z5.object({
    kind: z5.literal("optionRules"),
    optionId: CuidSchema,
    before: OptionRulesSchema,
    after: OptionRulesSchema
  }),
  z5.object({
    kind: z5.literal("optionRemoved"),
    optionId: CuidSchema,
    before: z5.boolean(),
    after: z5.boolean()
  }),
  z5.object({
    kind: z5.literal("optionCreate"),
    optionId: CuidSchema,
    productId: CuidSchema,
    name: MenuNameSchema,
    rules: OptionRulesSchema
  }),
  z5.object({
    kind: z5.literal("optionOrder"),
    productId: CuidSchema,
    before: OrderSchema,
    after: OrderSchema
  }),
  z5.object({
    kind: z5.literal("optionChoicePrice"),
    optionChoiceId: CuidSchema,
    before: NonNegativeIntSchema,
    after: NonNegativeIntSchema
  }),
  z5.object({
    kind: z5.literal("optionChoiceName"),
    optionChoiceId: CuidSchema,
    before: MenuNameSchema,
    after: MenuNameSchema
  }),
  z5.object({
    kind: z5.literal("optionChoiceAvailability"),
    optionChoiceId: CuidSchema,
    before: z5.boolean(),
    after: z5.boolean()
  }),
  z5.object({
    kind: z5.literal("optionChoiceActive"),
    optionChoiceId: CuidSchema,
    before: z5.boolean(),
    after: z5.boolean()
  }),
  z5.object({
    kind: z5.literal("optionChoiceDefault"),
    optionChoiceId: CuidSchema,
    before: z5.boolean(),
    after: z5.boolean()
  }),
  z5.object({
    kind: z5.literal("optionChoiceRemoved"),
    optionChoiceId: CuidSchema,
    before: z5.boolean(),
    after: z5.boolean()
  }),
  z5.object({
    kind: z5.literal("optionChoiceChannelPrice"),
    optionChoiceId: CuidSchema,
    channel: MenuOverrideChannelSchema,
    before: ChannelPriceSchema,
    after: ChannelPriceSchema
  }),
  z5.object({
    kind: z5.literal("optionChoiceCreate"),
    optionChoiceId: CuidSchema,
    optionId: CuidSchema,
    name: MenuNameSchema,
    price: NonNegativeIntSchema,
    isDefault: z5.boolean()
  }),
  z5.object({
    kind: z5.literal("optionChoiceOrder"),
    optionId: CuidSchema,
    before: OrderSchema,
    after: OrderSchema
  })
]);
var MenuChangesSchema = z5.array(MenuChangeSchema).max(MENU_CHANGESET_MAX);
function menuChangeTarget(change) {
  switch (change.kind) {
    case "productPrice":
    case "productAvailability":
    case "productActive":
    case "productName":
    case "productDescription":
    case "productAllergenCodes":
    case "productLabels":
    case "productSchedule":
    case "productRemoved":
    case "productChannelPrice":
    case "productCreate":
    case "optionOrder":
      return change.productId;
    case "productOrder":
    case "categoryName":
    case "categoryActive":
    case "categorySchedule":
    case "categoryRemoved":
    case "categoryCreate":
      return change.categoryId;
    case "categoryOrder":
      return change.menuId;
    case "optionName":
    case "optionRules":
    case "optionRemoved":
    case "optionCreate":
    case "optionChoiceOrder":
      return change.optionId;
    case "optionChoicePrice":
    case "optionChoiceName":
    case "optionChoiceAvailability":
    case "optionChoiceActive":
    case "optionChoiceDefault":
    case "optionChoiceRemoved":
    case "optionChoiceChannelPrice":
    case "optionChoiceCreate":
      return change.optionChoiceId;
  }
}
var menuChangeKey = (change) => change.kind === "productChannelPrice" || change.kind === "optionChoiceChannelPrice" ? `${change.kind}:${menuChangeTarget(change)}:${change.channel}` : `${change.kind}:${menuChangeTarget(change)}`;
var MenuChangesetStaleSchema = z5.object({ stale: MenuChangesSchema });
var PriceListSchema = z5.object({
  id: CuidSchema,
  restaurantId: CuidSchema,
  franchiseId: CuidSchema,
  name: z5.string(),
  isDefault: z5.boolean(),
  appliesToChannels: z5.array(MenuChannelSchema),
  isActive: z5.boolean()
});
var PriceListEntrySchema = z5.object({
  id: CuidSchema,
  priceListId: CuidSchema,
  restaurantId: CuidSchema,
  franchiseId: CuidSchema,
  productId: CuidSchema.nullable(),
  optionChoiceId: CuidSchema.nullable(),
  price: StoredIntSchema
});
var CreatePriceListInputSchema = z5.object({
  name: z5.string().min(1).max(100),
  appliesToChannels: z5.array(MenuChannelSchema).default([])
});
var PriceListWithEntriesSchema = PriceListSchema.extend({
  entries: z5.array(PriceListEntrySchema)
});
var MenuCreateSchema = z5.object({ name: LocalizedStringSchema });
var MenuUpdateSchema = z5.object({ name: LocalizedStringSchema.optional() });
var CategoryCreateSchema = z5.object({
  name: LocalizedStringSchema,
  description: LocalizedStringOptionalSchema.optional(),
  imageUrl: LocalizedStringOptionalSchema.optional(),
  schedule: CategoryScheduleSchema.nullable().optional(),
  sortOrder: NonNegativeIntSchema.optional(),
  hiddenChannels: z5.array(MenuChannelSchema).optional()
});
var CategoryUpdateSchema = z5.object({
  name: LocalizedStringSchema.optional(),
  description: LocalizedStringOptionalSchema.nullable().optional(),
  imageUrl: LocalizedStringOptionalSchema.nullable().optional(),
  schedule: CategoryScheduleSchema.nullable().optional(),
  sortOrder: NonNegativeIntSchema.optional(),
  isActive: z5.boolean().optional(),
  hiddenChannels: z5.array(MenuChannelSchema).optional()
});
var ProductCreateSchema = z5.object({
  name: LocalizedStringSchema,
  code: ProductCodeSchema.nullish(),
  description: LocalizedStringOptionalSchema.optional(),
  imageUrl: LocalizedStringOptionalSchema.optional(),
  schedule: CategoryScheduleSchema.nullable().optional(),
  price: NonNegativeIntSchema,
  allergens: z5.array(AllergenSchema).optional(),
  allergenCodes: z5.array(AllergenShortKeySchema).optional(),
  labels: z5.array(ProductLabelSchema).optional(),
  sortOrder: NonNegativeIntSchema.optional()
});
var ProductUpdateSchema = z5.object({
  name: LocalizedStringSchema.optional(),
  code: ProductCodeSchema.nullish(),
  description: LocalizedStringOptionalSchema.nullable().optional(),
  imageUrl: LocalizedStringOptionalSchema.nullable().optional(),
  schedule: CategoryScheduleSchema.nullable().optional(),
  price: NonNegativeIntSchema.optional(),
  allergens: z5.array(AllergenSchema).optional(),
  allergenCodes: z5.array(AllergenShortKeySchema).optional(),
  labels: z5.array(ProductLabelSchema).optional(),
  sortOrder: NonNegativeIntSchema.optional(),
  isActive: z5.boolean().optional(),
  inStock: z5.boolean().optional(),
  categoryId: CuidSchema.optional()
});
var ProductOptionCreateSchema = z5.object({
  name: LocalizedStringSchema,
  isRequired: z5.boolean().optional(),
  isMultiple: z5.boolean().optional(),
  minSelect: NonNegativeIntSchema.optional(),
  maxSelect: PositiveIntSchema.nullable().optional(),
  sortOrder: NonNegativeIntSchema.optional()
});
var ProductOptionUpdateSchema = z5.object({
  name: LocalizedStringSchema.optional(),
  isRequired: z5.boolean().optional(),
  isMultiple: z5.boolean().optional(),
  minSelect: NonNegativeIntSchema.optional(),
  maxSelect: PositiveIntSchema.nullable().optional(),
  sortOrder: NonNegativeIntSchema.optional()
});
var OptionChoiceCreateSchema = z5.object({
  name: LocalizedStringSchema,
  price: NonNegativeIntSchema.optional(),
  allergens: z5.array(AllergenSchema).optional(),
  allergenCodes: z5.array(AllergenShortKeySchema).optional(),
  isDefault: z5.boolean().optional(),
  sortOrder: NonNegativeIntSchema.optional()
});
var ProductWithOptionsCreateSchema = ProductCreateSchema.extend({
  options: z5.array(
    ProductOptionCreateSchema.extend({
      choices: z5.array(OptionChoiceCreateSchema).default([])
    })
  ).default([])
});
var OptionChoiceUpdateSchema = z5.object({
  name: LocalizedStringSchema.optional(),
  price: NonNegativeIntSchema.optional(),
  allergens: z5.array(AllergenSchema).optional(),
  allergenCodes: z5.array(AllergenShortKeySchema).optional(),
  isDefault: z5.boolean().optional(),
  isActive: z5.boolean().optional(),
  inStock: z5.boolean().optional(),
  sortOrder: NonNegativeIntSchema.optional()
});
var PriceListEntryUpsertSchema = z5.object({
  productId: CuidSchema.nullable().optional(),
  optionChoiceId: CuidSchema.nullable().optional(),
  price: NonNegativeIntSchema
}).refine((v) => v.productId != null !== (v.optionChoiceId != null), {
  error: "Provide exactly one of productId or optionChoiceId"
});
var CustomerChoiceSchema = z5.object({
  id: CuidSchema,
  name: z5.string(),
  price: StoredIntSchema,
  allergens: z5.array(z5.string()),
  isDefault: z5.boolean(),
  available: z5.boolean()
});
var CustomerOptionSchema = z5.object({
  id: CuidSchema,
  name: z5.string(),
  isRequired: z5.boolean(),
  isMultiple: z5.boolean(),
  minSelect: StoredIntSchema,
  maxSelect: StoredIntSchema.nullable(),
  choices: z5.array(CustomerChoiceSchema)
});
var CustomerAvailabilitySchema = z5.object({
  state: z5.enum(["available", "scheduled", "unavailable"]),
  availableFrom: z5.string().nullable(),
  windows: z5.array(CategoryScheduleWindowSchema)
});
var CustomerProductSchema = z5.object({
  id: CuidSchema,
  code: z5.string().nullable(),
  name: z5.string(),
  description: z5.string().nullable(),
  imageUrl: z5.string().nullable(),
  price: StoredIntSchema,
  allergens: z5.array(z5.string()),
  allergenCodes: z5.array(z5.string()),
  labels: z5.array(z5.string()),
  available: z5.boolean(),
  availability: CustomerAvailabilitySchema,
  options: z5.array(CustomerOptionSchema)
});
var CustomerCategorySchema = z5.object({
  id: CuidSchema,
  name: z5.string(),
  description: z5.string().nullable(),
  imageUrl: z5.string().nullable(),
  availability: CustomerAvailabilitySchema,
  products: z5.array(CustomerProductSchema)
});
var CustomerMenuSchema = z5.object({
  menuId: CuidSchema,
  restaurantId: CuidSchema,
  language: MenuLanguageSchema,
  channel: MenuChannelSchema,
  // The restaurant's own allergen table (shortKey → description) and the origin
  // sheet, so the storefront can resolve per-dish codes and show the reference.
  allergenTable: AllergenTableViewSchema,
  allergenSheet: z5.object({ text: z5.string().nullable(), imageUrl: z5.string().nullable() }).nullable(),
  categories: z5.array(CustomerCategorySchema)
});
var AllergenAggregationRequestSchema = z5.object({
  choiceIds: z5.array(CuidSchema).default([])
});
var AllergenAggregationResponseSchema = z5.object({ allergens: z5.array(z5.string()) });

// src/schemas/label.ts
import { z as z6 } from "zod";
var HexColorSchema = z6.string().regex(/^#[0-9A-Fa-f]{6}$/, "Must be a hex color in #rrggbb form");
var LabelSchema = z6.object({
  id: CuidSchema,
  name: z6.string(),
  color: HexColorSchema,
  systemOwned: z6.boolean()
});
var CreateLabelInputSchema = z6.object({
  name: z6.string().min(1).max(30),
  color: HexColorSchema
});
var UpdateLabelInputSchema = z6.object({
  name: z6.string().min(1).max(30).optional(),
  color: HexColorSchema.optional()
});
var SetLabelsInputSchema = z6.object({
  labelIds: z6.array(CuidSchema).max(20)
});
var LabelRestaurantParamsSchema = z6.object({ restaurantId: CuidSchema });
var LabelParamsSchema = LabelRestaurantParamsSchema.extend({ labelId: CuidSchema });

// src/schemas/purge.ts
import { z as z7 } from "zod";
var PurgeTargetSchema = z7.enum(["ORDER", "RESERVATION"]);
var PurgeSourceSchema = z7.enum([
  "SCHEDULED",
  "MANUAL_RULE",
  "MANUAL_CRITERIA",
  "MANUAL_SELECTION"
]);
var PurgeRunStatusSchema = z7.enum(["RUNNING", "COMPLETED", "PARTIAL", "FAILED"]);
var AgeBasisSchema = z7.enum(["CREATED_AT", "SERVICE_TIME"]);
var PurgeLabelMatchSchema = z7.enum(["ANY", "ALL"]);
var PurgeScheduleKindSchema = z7.enum(["DAILY", "WEEKLY", "MONTHLY"]);
var PurgeSkipReasonSchema = z7.enum([
  "STILL_LIVE",
  "CARRIES_MONEY",
  "CONCURRENT_CHANGE",
  "CAP_REACHED"
]);
var PurgeCriteriaSchema = z7.object({
  target: PurgeTargetSchema,
  olderThanDays: z7.number().int().min(0).max(3650),
  ageBasis: AgeBasisSchema,
  labelIds: z7.array(CuidSchema).min(1).max(20),
  labelMatch: PurgeLabelMatchSchema.optional()
});
var PurgeSelectionSchema = z7.object({
  target: PurgeTargetSchema,
  ids: z7.array(CuidSchema).min(1).max(5e3)
});
var PurgeInputSchema = z7.union([
  z7.object({ kind: z7.literal("criteria"), criteria: PurgeCriteriaSchema }),
  z7.object({ kind: z7.literal("selection"), selection: PurgeSelectionSchema })
]);
var PurgeRecordSchema = z7.object({
  id: CuidSchema,
  target: PurgeTargetSchema,
  reference: z7.string(),
  name: z7.string(),
  status: z7.string(),
  at: DateTimeSchema
});
var PurgePreviewSchema = z7.object({
  count: z7.number().int().nonnegative(),
  records: z7.array(PurgeRecordSchema),
  nextCursor: z7.string().optional(),
  cappedAtRunLimit: z7.boolean(),
  skipped: z7.partialRecord(PurgeSkipReasonSchema, z7.number().int()).optional()
});
var PurgeCriteriaSnapshotSchema = z7.object({
  target: PurgeTargetSchema.optional(),
  olderThanDays: z7.number().int().optional(),
  ageBasis: AgeBasisSchema.optional(),
  ids: z7.array(z7.string()).optional(),
  ruleName: z7.string().optional(),
  labels: z7.array(z7.object({ id: z7.string(), name: z7.string() })).optional(),
  labelMatch: PurgeLabelMatchSchema.optional()
});
var PurgeRunBreakdownSchema = z7.object({
  skipped: z7.partialRecord(PurgeSkipReasonSchema, z7.number().int()).optional()
});
var PurgeRunSchema = z7.object({
  id: CuidSchema,
  target: PurgeTargetSchema,
  source: PurgeSourceSchema,
  status: PurgeRunStatusSchema,
  actorId: z7.string().nullable(),
  actorName: z7.string().nullable(),
  actorEmail: z7.string().nullable(),
  affectedCount: z7.number().int().nonnegative(),
  startedAt: DateTimeSchema,
  finishedAt: DateTimeSchema.nullable(),
  error: z7.string().nullable(),
  criteriaSnapshot: PurgeCriteriaSnapshotSchema,
  breakdown: PurgeRunBreakdownSchema.nullable()
});
var PurgeRestaurantParamsSchema = z7.object({ restaurantId: CuidSchema });
var PurgeRunParamsSchema = PurgeRestaurantParamsSchema.extend({ runId: CuidSchema });
var PurgeRuleParamsSchema = PurgeRestaurantParamsSchema.extend({ ruleId: CuidSchema });
var PurgePreviewInputSchema = z7.object({
  input: PurgeInputSchema,
  limit: z7.number().int().min(1).max(200).optional(),
  cursor: z7.string().optional()
});
var PurgeExecuteInputSchema = z7.object({
  input: PurgeInputSchema,
  ruleId: CuidSchema.optional(),
  expectedCount: z7.number().int().min(0)
});
var PurgeRunListQuerySchema = z7.object({
  limit: z7.coerce.number().int().min(1).max(101).optional()
});
var ruleFields = {
  name: z7.string().min(1).max(60),
  target: PurgeTargetSchema,
  olderThanDays: z7.number().int().min(0).max(3650),
  ageBasis: AgeBasisSchema,
  labelIds: z7.array(CuidSchema).min(1).max(20),
  labelMatch: PurgeLabelMatchSchema,
  scheduleKind: PurgeScheduleKindSchema,
  scheduleDay: z7.preprocess(
    (value) => value === null ? [] : typeof value === "number" ? [value] : value,
    z7.array(z7.number().int().min(0).max(31)).max(31).transform((days) => [...new Set(days)].sort((a, b) => a - b))
  ),
  scheduleHour: z7.number().int().min(0).max(23),
  enabled: z7.boolean()
};
var ruleShape = {
  ...ruleFields,
  labelMatch: ruleFields.labelMatch.default("ANY"),
  scheduleDay: ruleFields.scheduleDay.default([]),
  scheduleHour: ruleFields.scheduleHour.default(3),
  enabled: ruleFields.enabled.default(false)
};
function checkScheduleDay(value, ctx) {
  const { scheduleKind, scheduleDay } = value;
  if (scheduleKind === void 0) return;
  const fail = (message) => ctx.addIssue({ code: z7.ZodIssueCode.custom, path: ["scheduleDay"], message });
  if (scheduleKind === "DAILY") {
    if (scheduleDay !== void 0 && scheduleDay.length > 0) fail("A daily rule has no day.");
    return;
  }
  if (!scheduleDay || scheduleDay.length === 0) {
    fail(`A ${scheduleKind.toLowerCase()} rule needs a day.`);
    return;
  }
  if (scheduleKind === "WEEKLY") {
    if (scheduleDay.length !== 1) fail("A weekly rule needs exactly one day.");
    else if (scheduleDay[0] > 6) fail("Day of week is 0 (Sunday) to 6.");
  }
  if (scheduleKind === "MONTHLY") {
    if (scheduleDay.some((d) => d < 1 || d > 31)) fail("Day of month is 1 to 31.");
  }
}
var CreatePurgeRuleInputSchema = z7.object(ruleShape).superRefine(checkScheduleDay);
var UpdatePurgeRuleInputSchema = z7.object(ruleFields).partial().superRefine(checkScheduleDay);
var PurgeRuleSchema = z7.object({
  id: CuidSchema,
  name: z7.string(),
  target: PurgeTargetSchema,
  olderThanDays: z7.number().int(),
  ageBasis: AgeBasisSchema,
  labels: z7.array(z7.object({ id: CuidSchema, name: z7.string(), color: z7.string() })),
  labelMatch: PurgeLabelMatchSchema,
  scheduleKind: PurgeScheduleKindSchema,
  scheduleDay: z7.array(z7.number().int()),
  scheduleHour: z7.number().int(),
  enabled: z7.boolean(),
  lastRunAt: DateTimeSchema.nullable(),
  nextRunAt: DateTimeSchema.nullable()
});
var PurgeMetricsSchema = z7.object({
  restaurants: z7.object({
    active: z7.number().int(),
    withEnabledRule: z7.number().int()
  }),
  weeks: z7.array(
    z7.object({
      weekStart: DateSchema,
      runs: z7.object({
        SCHEDULED: z7.number().int(),
        MANUAL_RULE: z7.number().int(),
        MANUAL_CRITERIA: z7.number().int(),
        MANUAL_SELECTION: z7.number().int()
      }),
      recordsHidden: z7.number().int(),
      medianHiddenPerRun: z7.number().nullable()
    })
  ),
  listSize: z7.array(
    z7.object({
      target: PurgeTargetSchema,
      restaurants: z7.number().int(),
      medianBefore: z7.number().nullable(),
      medianAfter: z7.number().nullable()
    })
  )
});

// src/schemas/order.ts
import { z as z9 } from "zod";

// src/schemas/payment.ts
import { z as z8 } from "zod";
import { PAYMENT_METHOD_NOTE_MAX_LENGTH } from "@restaurant/types";
var PaymentStatusSchema = z8.enum([
  "PENDING_COLLECTION",
  "AWAITING_PROOF",
  "PROOF_SUBMITTED",
  "PROCESSING",
  // Online funds captured but held by the provider for review (e.g. PayPal
  // capture PENDING / eCheck). Order stays PENDING until it resolves.
  "ON_HOLD",
  "PAID",
  "REJECTED",
  "VOID",
  "REFUNDED",
  "PARTIALLY_REFUNDED"
]);
var PaymentMethodKindSchema = z8.enum([
  "ONLINE",
  "MANUAL",
  "CASH",
  "CARD_ON_HANDOFF",
  "GIFT_CARD"
]);
var CollectionModeSchema = z8.enum(["MANUAL", "AUTO"]);
var RefundReasonSchema = z8.enum([
  "ITEM_UNAVAILABLE",
  "WRONG_ITEM",
  "LATE_DELIVERY",
  "CANCELLED_BY_OWNER",
  "CUSTOMER_REQUEST",
  "OTHER"
]);
var RefundSettlementStatusSchema = z8.enum(["PENDING", "SETTLED", "FAILED"]);
var PaymentSchema = z8.object({
  id: CuidSchema,
  // Payment is polymorphic: exactly one of orderId / tableSessionId is set.
  orderId: CuidSchema.nullable(),
  tableSessionId: CuidSchema.nullable(),
  restaurantId: CuidSchema,
  status: PaymentStatusSchema,
  methodKind: PaymentMethodKindSchema,
  methodId: z8.string(),
  amountCents: NonNegativeIntSchema,
  currency: z8.string(),
  refundedCents: NonNegativeIntSchema,
  paymentReference: z8.string(),
  collectionMode: CollectionModeSchema.nullable(),
  collectedAt: DateTimeSchema.nullable(),
  acceptedAt: DateTimeSchema.nullable(),
  rejectedAt: DateTimeSchema.nullable(),
  rejectReason: z8.string().nullable(),
  // Whether this payment's provider supports an in-app refund. False for providers
  // with no refund endpoint (e.g. Paycat) — the dashboard hides the refund control
  // and notes refunds are handled out-of-band, so the UI never hardcodes provider names.
  refundable: z8.boolean(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema
});
var ProofUploadResultSchema = z8.object({
  id: CuidSchema,
  paymentId: CuidSchema,
  blobKey: z8.string(),
  mimeType: z8.string(),
  sizeBytes: z8.number().int().positive(),
  customerStatedAmountCents: z8.number().int().nonnegative().nullable(),
  createdAt: DateTimeSchema
});
var ProofViewSchema = z8.object({
  url: z8.url(),
  mimeType: z8.string(),
  customerStatedAmountCents: z8.number().int().nonnegative().nullable(),
  createdAt: DateTimeSchema
}).nullable();
var RefundEntrySchema = z8.object({
  id: CuidSchema,
  paymentId: CuidSchema,
  amountCents: z8.number().int().positive(),
  reason: RefundReasonSchema,
  actorType: z8.string(),
  actorUserId: z8.string().nullable(),
  externalRefundId: z8.string().nullable(),
  evidenceBlobKey: z8.string().nullable(),
  note: z8.string().nullable(),
  // Whether the money has actually moved. PENDING refunds show a "confirm
  // settled" action in the dashboard; SETTLED ones read as truly refunded; FAILED
  // ones show a "refund failed — retry / handle manually" state with the reason.
  settlementStatus: RefundSettlementStatusSchema,
  attemptedAt: DateTimeSchema.nullable(),
  settledAt: DateTimeSchema.nullable(),
  settledBy: z8.string().nullable(),
  failureReason: z8.string().nullable(),
  createdAt: DateTimeSchema
});
var CreateRefundInputSchema = z8.object({
  amountCents: z8.number().int().positive(),
  reason: RefundReasonSchema,
  evidenceBlobKey: z8.string().optional(),
  note: z8.string().max(500).optional()
});
var AccountFieldSchema = z8.object({
  key: z8.string().min(1).max(100),
  value: z8.string().min(1).max(500)
});
var ManualPaymentMethodSchema = z8.object({
  id: CuidSchema,
  restaurantId: CuidSchema,
  label: z8.string(),
  instructions: z8.string(),
  accountFields: z8.array(AccountFieldSchema),
  qrImageUrl: z8.url().nullable(),
  // signed URL, null if no QR
  displayOrder: z8.number().int(),
  isActive: z8.boolean(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema
});
var ManualPaymentMethodInputSchema = z8.object({
  label: z8.string().min(1).max(100),
  instructions: z8.string().min(1).max(2e3),
  accountFields: z8.array(AccountFieldSchema).max(10),
  displayOrder: z8.number().int().nonnegative().optional(),
  isActive: z8.boolean().optional()
});
var PaymentMethodNoteSchema = z8.object({
  methodId: z8.string().min(1).max(40),
  note: z8.string().min(1).max(PAYMENT_METHOD_NOTE_MAX_LENGTH)
});
var SetPaymentMethodNoteInputSchema = z8.object({
  note: z8.string().max(PAYMENT_METHOD_NOTE_MAX_LENGTH)
});
var PaymentMethodListSchema = z8.object({
  manualMethods: z8.array(ManualPaymentMethodSchema),
  // Every active online provider (by methodId), or empty when none is
  // connected/enabled. A restaurant may offer several at once and the guest
  // picks one; `id` is what the order submits as `onlineProviderId`. `label` is
  // the choosing wording, `shortName` the brand every other surface prints.
  // Data-driven — never a hardcoded provider identity.
  onlineProviders: z8.array(z8.object({ id: z8.string(), label: z8.string(), shortName: z8.string() })),
  // Whether card-at-handoff (owner's POS terminal) is enabled for the queried
  // order-type. The storefront renders the card-at-handoff button only when true.
  cardOnHandoff: z8.boolean(),
  // Owner-written badges ("Recommended"), at most one per rail. Sent as a list
  // keyed by methodId rather than a field on each rail, because the rails live in
  // three different shapes here (online provider, manual method, and cash /
  // card-at-handoff, which have no object at all) and all three resolve by the
  // same id. A rail with no entry simply renders no badge.
  methodNotes: z8.array(PaymentMethodNoteSchema)
});
var OnlinePaymentProviderSchema = z8.enum(["PAYCAT", "PAYPAL"]);
var PaymentProviderModeSchema = z8.enum(["SANDBOX", "LIVE"]);
var PaymentProviderConfigSchema = z8.object({
  id: CuidSchema,
  restaurantId: CuidSchema,
  provider: OnlinePaymentProviderSchema,
  mode: PaymentProviderModeSchema,
  isActive: z8.boolean(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema
});
var PaycatCredentialsInputSchema = z8.object({
  provider: z8.literal("PAYCAT"),
  baseUrl: z8.url(),
  tenantId: z8.string().min(1),
  tenantSecret: z8.string().min(1)
});
var PayPalCredentialsInputSchema = z8.object({
  provider: z8.literal("PAYPAL"),
  clientId: z8.string().min(1),
  clientSecret: z8.string().min(1),
  // Optional PayPal webhook id (from the app's webhook registration in the
  // PayPal dashboard). Enables inbound webhook signature verification so paid
  // orders confirm instantly; unset ⇒ reconciliation falls back to polling.
  webhookId: z8.string().min(1).optional()
});
var ConnectPaymentProviderInputSchema = z8.object({
  mode: PaymentProviderModeSchema,
  // Activate this provider on connect (deactivates any other online provider).
  activate: z8.boolean(),
  credentials: z8.discriminatedUnion("provider", [
    PaycatCredentialsInputSchema,
    PayPalCredentialsInputSchema
  ])
});
var SetProviderActivationInputSchema = z8.object({
  // Which credential set to (de)activate. A provider can hold both a SANDBOX and
  // a LIVE row; the toggle switches which one the storefront transacts in.
  mode: PaymentProviderModeSchema,
  isActive: z8.boolean()
});
var PaymentRestaurantParamsSchema = z8.object({ restaurantId: CuidSchema });
var PaymentOrderParamsSchema = z8.object({ restaurantId: CuidSchema, orderId: CuidSchema });
var ProofOrderParamsSchema = z8.object({ orderId: CuidSchema });
var RefundParamsSchema = z8.object({
  restaurantId: CuidSchema,
  orderId: CuidSchema,
  refundId: CuidSchema
});
var ManualPaymentMethodParamsSchema = z8.object({
  restaurantId: CuidSchema,
  methodId: CuidSchema
});
var PaymentMethodNoteParamsSchema = z8.object({
  restaurantId: CuidSchema,
  methodId: z8.string().min(1).max(40)
});
var PaymentProviderParamsSchema = z8.object({
  restaurantId: CuidSchema,
  provider: OnlinePaymentProviderSchema
});
var DisconnectPaymentProviderQuerySchema = z8.object({ mode: PaymentProviderModeSchema });
var UploadProofInputSchema = z8.object({
  blobKey: z8.string().min(1),
  blobHash: z8.string().min(1),
  mimeType: z8.string().min(1),
  sizeBytes: z8.number().int().positive(),
  customerStatedAmountCents: z8.coerce.number().int().nonnegative().optional()
});
var RejectProofInputSchema = z8.object({ reason: z8.string().min(1).max(500) });
var UpdateManualPaymentMethodInputSchema = ManualPaymentMethodInputSchema.partial();
var PaymentMethodNoteResultSchema = PaymentMethodNoteSchema.nullable();

// src/schemas/order.ts
var OrderStatusSchema = z9.enum(["PENDING", "CONFIRMED", "COMPLETED", "CANCELLED"]);
var OnlineOrderPrepStatusSchema = z9.enum(["QUEUED", "PREPARING", "READY", "SERVED"]);
var OrderStageSchema = z9.enum([
  "AWAITING_PAYMENT",
  "NEEDS_REVIEW",
  "NEEDS_ACCEPTANCE",
  "IN_KITCHEN",
  "READY",
  "COMPLETED",
  "CANCELLED"
]);
var OrderTypeSchema = z9.enum(["PICKUP", "DELIVERY"]);
var PaymentMethodsQuerySchema = z9.object({ orderType: OrderTypeSchema.optional() });
var CancelActorSchema = z9.enum(["OWNER", "STAFF", "CUSTOMER", "SYSTEM"]);
var PauseModeSchema = z9.enum(["TIMED", "UNTIL_CLOSE"]);
var DeliveryZoneModeSchema = z9.enum(["STRAIGHT_LINE", "POLYGON", "ROUTE_DISTANCE"]);
var DeliveryPriceModeSchema = z9.enum(["DISTANCE", "ZONE"]);
var DistanceCalculationSchema = z9.enum(["DIRECT", "ROUTE"]);
var StoredIntSchema2 = z9.number().int();
var OrderTimelineKindSchema = z9.enum([
  "CREATED",
  "CONFIRMED",
  "COMPLETED",
  "CANCELLED",
  "PROOF_UPLOADED",
  "PROOF_ACCEPTED",
  "PROOF_REJECTED",
  "PAYMENT_COLLECTED",
  "PAYMENT_VOIDED",
  "REFUND_ISSUED",
  "PREP_REMINDER_SENT",
  "PREP_ADVANCED",
  "PREP_RECALLED"
]);
var RouteGeometrySchema = z9.object({
  type: z9.literal("LineString"),
  coordinates: z9.array(z9.tuple([z9.number(), z9.number()]))
});
var DeliveryRouteSchema = z9.object({
  geometry: RouteGeometrySchema,
  distanceMeters: StoredIntSchema2,
  durationSeconds: StoredIntSchema2
});
var DeliveryAddressSchema = z9.object({
  id: CuidSchema,
  line1: z9.string(),
  line2: z9.string().nullable(),
  city: z9.string(),
  postalCode: z9.string().nullable(),
  country: z9.string(),
  instructions: z9.string().nullable(),
  latitude: z9.number(),
  longitude: z9.number(),
  route: DeliveryRouteSchema.nullable()
});
var DeliveryAddressInputSchema = z9.object({
  line1: requiredAddressPart(200),
  line2: addressPart(200).optional(),
  city: requiredAddressPart(100),
  postalCode: addressPart(20).optional(),
  country: z9.string().length(2),
  instructions: z9.string().max(500).optional()
  // NOTE: no coordinates. The client sends only the text address; the BACKEND
  // geocodes it authoritatively (ship-price + place-order) so a caller can't
  // forge coordinates to spoof a cheaper delivery zone/fee.
});
var OrderItemChoiceSchema = z9.object({
  id: CuidSchema,
  optionId: CuidSchema,
  optionChoiceId: CuidSchema,
  optionNameSnapshot: z9.string(),
  choiceNameSnapshot: z9.string(),
  unitPriceCents: z9.number().int(),
  allergenCodes: z9.array(z9.string())
});
var AllergenSnapshotEntrySchema = z9.object({
  shortKey: z9.string(),
  description: z9.string()
});
var AllergenSnapshotSchema = z9.array(AllergenSnapshotEntrySchema);
var OrderItemSchema = z9.object({
  id: CuidSchema,
  productId: CuidSchema,
  nameSnapshot: z9.string(),
  descriptionSnapshot: z9.string().nullable(),
  categoryNameSnapshot: z9.string().nullable(),
  quantity: StoredIntSchema2,
  unitPriceCents: StoredIntSchema2,
  lineTotalCents: StoredIntSchema2,
  allergenCodes: z9.array(z9.string()),
  allergenSnapshot: AllergenSnapshotSchema,
  customerNote: z9.string().nullable(),
  choices: z9.array(OrderItemChoiceSchema)
});
var OrderItemInputSchema = z9.object({
  productId: CuidSchema,
  // Capped so quantity * unitPriceCents can't overflow the 32-bit Int money math.
  quantity: z9.number().int().positive().max(999),
  customerNote: z9.string().max(500).optional(),
  choices: z9.array(
    z9.object({
      optionId: CuidSchema,
      optionChoiceId: CuidSchema
    })
  ).default([])
});
var OrderTimelineEntrySchema = z9.object({
  id: CuidSchema,
  kind: OrderTimelineKindSchema,
  fromStatus: OrderStatusSchema.nullable(),
  toStatus: OrderStatusSchema.nullable(),
  actorType: CancelActorSchema,
  actorUserId: z9.string().nullable(),
  note: z9.string().nullable(),
  createdAt: DateTimeSchema
});
var TaxBreakdownLineSchema = z9.object({
  rateBps: StoredIntSchema2,
  netCents: StoredIntSchema2,
  vatCents: StoredIntSchema2
});
var OnlineOrderSchema = z9.object({
  id: CuidSchema,
  restaurantId: CuidSchema,
  confirmationCode: z9.string(),
  paymentReference: z9.string(),
  type: OrderTypeSchema,
  status: OrderStatusSchema,
  // Kitchen prep axis (orthogonal to status; read-only to the owner UI).
  prepStatus: OnlineOrderPrepStatusSchema,
  prepUpdatedAt: DateTimeSchema.nullable(),
  // Payment status of the order's Payment row — so the list can show Paid/Unpaid
  // without a per-order payment fetch. Null on mutation results (payment not
  // loaded there); populated on query results (list / getOrder).
  paymentStatus: PaymentStatusSchema.nullable(),
  // Payment method of the order's Payment row — so the list row can show the rail
  // (Cash / Card at handoff / Bank transfer / Card) without a per-order fetch.
  // Populated on query results (list / getOrder); null on mutation results where
  // the payment relation isn't loaded (mirrors paymentStatus).
  paymentMethodKind: PaymentMethodKindSchema.nullable(),
  // Which rail instance took the money. For ONLINE this is the gateway
  // ('paycat' | 'paypal'), so the row can name it instead of the generic
  // "Online" — a restaurant may run several gateways at once. Same loaded-only
  // contract as paymentMethodKind.
  paymentMethodId: z9.string().nullable(),
  // Derived operator stage (whose turn / where is it). Populated on query results
  // (list / getOrder) and on mutation results once the order leaves PENDING;
  // null on a PENDING mutation result where the payment relation isn't loaded.
  stage: OrderStageSchema.nullable(),
  customerName: z9.string(),
  customerPhone: z9.string(),
  customerEmail: z9.string().nullable(),
  scheduledFor: DateTimeSchema.nullable(),
  estimatedReadyAt: DateTimeSchema.nullable(),
  prepStartAt: DateTimeSchema.nullable(),
  driverLeaveAt: DateTimeSchema.nullable(),
  // MANUAL / VietQR bank-transfer payment deadline (createdAt +
  // expireUnpaidAfterMinutes), stamped at placement. Drives the storefront
  // transfer countdown; the unpaid sweep cancels the order when it passes. Null
  // when the order has no payment deadline (cash) — or wasn't set.
  paymentExpiresAt: DateTimeSchema.nullable(),
  operatorVisibleAt: DateTimeSchema.nullable(),
  subtotalCents: StoredIntSchema2,
  deliveryFeeCents: StoredIntSchema2,
  discountCents: StoredIntSchema2,
  promoCode: z9.string().optional(),
  totalCents: StoredIntSchema2,
  // VAT portion of totalCents (inclusive — does not add to the total) + the
  // frozen per-rate breakdown for the receipt.
  taxCents: StoredIntSchema2,
  taxBreakdown: z9.array(TaxBreakdownLineSchema),
  currency: z9.string(),
  deliveryAddress: DeliveryAddressSchema.nullable(),
  // The delivery zone that matched at order time — its `mode` tells how the fee
  // was computed (STRAIGHT_LINE = straight-line distance, ROUTE_DISTANCE = MapBox
  // driving distance, POLYGON = map zone). Null for pickup or if the zone was
  // since removed. The fee itself (deliveryFeeCents) is a frozen snapshot.
  deliveryZone: z9.object({ label: z9.string(), mode: DeliveryZoneModeSchema }).nullable(),
  customerNote: z9.string().nullable(),
  // Which actions the requesting actor can take (computed server-side).
  actions: z9.array(z9.string()),
  items: z9.array(OrderItemSchema),
  timeline: z9.array(OrderTimelineEntrySchema).optional(),
  // Internal operator triage tags. OPTIONAL, like `timeline`, because three
  // payload shapes flow through orderBaseToWire and the CUSTOMER one must not
  // carry labels at all — emitting [] there would be indistinguishable from
  // "this order has no labels". Owner surfaces read `labels ?? []`.
  labels: z9.array(LabelSchema).optional(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema
});
var KitchenTicketStatusSchema = z9.enum([
  "QUEUED",
  "PREPARING",
  "READY",
  "SERVED",
  "FIRED"
]);
var KitchenTicketLineSchema = z9.object({
  name: z9.string(),
  quantity: StoredIntSchema2,
  categoryName: z9.string().nullable(),
  choices: z9.array(z9.object({ optionNameSnapshot: z9.string(), choiceNameSnapshot: z9.string() }))
});
var KitchenTicketSchema = z9.object({
  source: z9.enum(["ONLINE", "DINE_IN"]),
  // onlineOrderId (ONLINE) or orderRoundId (DINE_IN).
  refId: CuidSchema,
  // For DINE_IN tickets, the session the round belongs to (for the ready/recall calls).
  sessionId: CuidSchema.nullable(),
  status: KitchenTicketStatusSchema,
  label: z9.string(),
  // The age clock: order acceptedAt (online) / round firedAt (dine-in).
  firedAt: DateTimeSchema,
  // estimatedReadyAt (online) / firedAt + kdsTargetMinutes (dine-in); null if unknown.
  targetAt: DateTimeSchema.nullable(),
  lines: z9.array(KitchenTicketLineSchema)
});
var PlaceOrderInputSchema = z9.object({
  restaurantId: CuidSchema,
  type: OrderTypeSchema,
  customerName: z9.string().min(1).max(100),
  customerPhone: PhoneSchema,
  customerEmail: z9.email().optional(),
  // null / undefined = ASAP
  scheduledFor: DateTimeSchema.optional(),
  deliveryAddress: DeliveryAddressInputSchema.optional(),
  items: z9.array(OrderItemInputSchema).min(1),
  paymentMethodKind: z9.enum(["CASH", "CARD_ON_HANDOFF", "MANUAL", "ONLINE"]),
  // Required when paymentMethodKind = 'MANUAL'
  manualPaymentMethodId: CuidSchema.optional(),
  // Which online provider the guest chose ('paycat' | 'paypal'), from the
  // checkout method list. Required when paymentMethodKind = 'ONLINE' and the
  // restaurant has more than one provider running; with exactly one active
  // provider the server resolves it unambiguously and this may be omitted.
  onlineProviderId: z9.string().max(40).optional(),
  customerNote: z9.string().max(500).optional(),
  // Optional promo / coupon code entered at checkout.
  promoCode: z9.string().max(40).optional(),
  // Customer-chosen non-code promotions. [] = none; a list = apply those (the
  // server re-validates each is available + eligible, and — for >1 — stackable).
  selectedPromotionIds: z9.array(CuidSchema).nullable().optional(),
  // Optional loyalty points to redeem (requires a signed-in customer session).
  redeemPoints: z9.number().int().min(0).optional(),
  // Marketing consent (email/SMS) captured at checkout, if the form offers it.
  // Drives the customer's MARKETING consent when the order builds the customer.
  marketingOptIn: z9.boolean().optional(),
  // Base URL of the storefront's order-status page (origin + /{fr}/{rest}/order).
  // Online providers append the order id and redirect the payer back after paying.
  paymentReturnUrl: z9.url().optional()
});
var OrderWithPaymentSchema = OnlineOrderSchema.extend({
  payment: z9.object({
    id: CuidSchema,
    status: z9.string(),
    methodKind: z9.enum(["CASH", "CARD_ON_HANDOFF", "MANUAL", "ONLINE", "GIFT_CARD"]),
    // Which rail instance took the money — for ONLINE this is the gateway
    // ('paycat' | 'paypal'), so the status page can name it instead of saying
    // the generic "Online". A manual method's own cuid otherwise.
    methodId: z9.string(),
    amountCents: StoredIntSchema2,
    paymentReference: z9.string(),
    // For MANUAL: the chosen method's instructions
    manualInstructions: z9.string().nullable(),
    manualAccountFields: z9.array(z9.object({ key: z9.string(), value: z9.string() })).nullable(),
    qrImageUrl: z9.string().nullable(),
    // For CASH: display copy
    cashInstructions: z9.string().nullable(),
    // For ONLINE: hosted checkout URL to redirect the customer to (e.g. Paycat link).
    redirectUrl: z9.string().nullable()
  })
});
var RefreshPaymentResponseSchema = z9.object({
  status: PaymentStatusSchema,
  // Whether this poll moved the payment to a new (terminal) status.
  changed: z9.boolean(),
  // Present only while still awaiting payment — lets the storefront re-offer the link.
  redirectUrl: z9.string().nullable()
});
var ShipPriceInputSchema = z9.object({
  restaurantId: CuidSchema,
  type: OrderTypeSchema,
  // Required for DELIVERY (the point we resolve the zone from); omit for PICKUP.
  deliveryAddress: DeliveryAddressInputSchema.optional()
});
var ShipPriceResponseSchema = z9.object({
  // false = undeliverable; `reason` says why so the storefront shows the right
  // note: OUTSIDE_ZONE (outside every active zone), UNLOCATABLE (address couldn't
  // be geocoded), PROVIDER_ERROR (MapBox errored computing the route — transient).
  deliverable: z9.boolean(),
  reason: z9.enum(["OUTSIDE_ZONE", "UNLOCATABLE", "PROVIDER_ERROR"]).nullable(),
  deliveryFeeCents: StoredIntSchema2,
  // The matched zone (null for PICKUP or when undeliverable).
  zoneId: CuidSchema.nullable(),
  zoneLabel: z9.string().nullable(),
  // Zone-specific minimum order (0 = none); the storefront compares it to the
  // cart subtotal to warn before checkout.
  minOrderCents: StoredIntSchema2,
  // The address MapBox actually matched, so the storefront can confirm the
  // resolved location. Null for PICKUP or when the address couldn't be located.
  resolvedLabel: z9.string().nullable()
});
var AcceptOrderInputSchema = z9.object({
  // Owner may tune the ETA at accept time. Both feed the SAME timing math as
  // placement (OrderTimingService), so prepStartAt / driverLeaveAt /
  // estimatedReadyAt stay consistent — never a lone overridden handoff.
  //  - prepMinutesOverride: kitchen lead in minutes (pickup + delivery).
  //  - deliveryMinutesOverride: the travel leg in minutes (delivery only);
  //    when set, the live routing lookup is skipped in favour of this value.
  prepMinutesOverride: z9.number().int().min(0).max(600).optional(),
  deliveryMinutesOverride: z9.number().int().min(0).max(600).optional()
});
var CancelOrderInputSchema = z9.object({
  // Optional owner note kept on the timeline / cancelReason column; not shown to
  // the customer. The dashboard cancels with a plain confirm (no forced reason).
  reason: z9.string().min(1).max(500).optional(),
  // Whether cancelling a PAID online order also refunds it. Defaults to true (the
  // CancelOrderSheet's refund toggle is on by default); false = cancel only,
  // payment stays PAID (refund later via the cancelled-order refund action).
  issueRefund: z9.boolean().optional()
});
var AvailableSlotSchema = z9.object({
  time: DateTimeSchema,
  remaining: z9.number().int().nonnegative(),
  isFull: z9.boolean()
});
var GetAvailableSlotsQuerySchema = z9.object({
  orderType: OrderTypeSchema
});
var ListOrdersQuerySchema = z9.object({
  status: OrderStatusSchema.optional(),
  type: OrderTypeSchema.optional(),
  // Reuse the canonical payment-status enum so the filter never drifts from
  // PaymentStatusSchema / Prisma (previously omitted ON_HOLD, making held
  // orders unfilterable).
  paymentStatus: PaymentStatusSchema.optional(),
  collectionMode: z9.enum(["MANUAL", "AUTO"]).optional(),
  dateFrom: DateTimeSchema.optional(),
  dateTo: DateTimeSchema.optional(),
  // Scope to a single restaurant-local calendar day (YYYY-MM-DD) by the order's
  // SERVICE instant (scheduledFor → estimatedReadyAt → createdAt), the same time
  // the dashboard card shows - unlike dateFrom/dateTo which bound `createdAt`.
  // The server resolves the day's UTC bounds in the restaurant's timezone.
  day: z9.string().regex(/^\d{4}-\d{2}-\d{2}$/, "day must be YYYY-MM-DD").optional(),
  cursor: z9.string().optional(),
  limit: z9.coerce.number().int().min(1).max(100).default(20)
});
var OrderListResponseSchema = z9.object({
  items: z9.array(OnlineOrderSchema),
  nextCursor: z9.string().nullable()
});
var OrderSummarySchema = z9.object({
  pending: NonNegativeIntSchema,
  active: NonNegativeIntSchema,
  overdue: NonNegativeIntSchema
});
var OrderChannelStatusSchema = z9.object({
  orderType: z9.enum(["PICKUP", "DELIVERY"]),
  availability: z9.enum(["OPEN", "CLOSED", "PAUSED"]),
  nextChangeAt: DateTimeSchema.nullable(),
  nextChangeKind: z9.enum(["OPENS", "CLOSES"]).nullable()
});
var DeliveryZoneSchema = z9.object({
  id: CuidSchema,
  restaurantId: CuidSchema,
  label: z9.string(),
  mode: DeliveryZoneModeSchema,
  displayOrder: StoredIntSchema2,
  isActive: z9.boolean(),
  feeCents: StoredIntSchema2,
  minOrderCents: StoredIntSchema2,
  maxDistanceKm: z9.number().nullable(),
  // Promised travel time for this zone (delivery leg only — prep is added on
  // top). Set for STRAIGHT_LINE / POLYGON; null for ROUTE_DISTANCE, which uses
  // live MapBox driving time instead.
  deliveryMinutes: StoredIntSchema2.nullable(),
  polygon: z9.unknown().nullable(),
  // GeoJSON ring
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema
});
var DeliveryZoneInputSchema = z9.object({
  label: z9.string().min(1).max(100),
  // STRAIGHT_LINE = direct/haversine tier, ROUTE_DISTANCE = MapBox road-distance
  // tier, POLYGON = owner-drawn area. The active shape is gated by the
  // restaurant's deliveryPriceMode / distanceCalculation.
  mode: DeliveryZoneModeSchema,
  feeCents: NonNegativeIntSchema,
  minOrderCents: NonNegativeIntSchema.default(0),
  maxDistanceKm: z9.number().positive().optional(),
  // Required by the use-case for STRAIGHT_LINE / POLYGON (no routing to ask),
  // rejected for ROUTE_DISTANCE. Optional here because the shape rule depends on
  // `mode`, which a `.partial()` update may omit — validateZoneInput enforces it.
  deliveryMinutes: NonNegativeIntSchema.max(600).optional(),
  polygon: z9.unknown().optional(),
  // Owner-toggleable active flag. Optional on create (defaults active in the DB);
  // on the `.partial()` update contract it lets an owner deactivate a zone —
  // e.g. auto-retiring zones stranded by a delivery pricing-mode switch — with a
  // minimal `{ isActive: false }` payload.
  isActive: z9.boolean().optional()
});
var DeliveryReadinessCodeSchema = z9.enum([
  // Restaurant has no lat/lng. In DISTANCE mode resolveZone returns null, so
  // EVERY address is reported to the customer as out-of-delivery-area.
  "LOCATION_NOT_PINNED",
  // No active zone of the shape the current pricing mode consults — same
  // symptom: every address out-of-area.
  "NO_ACTIVE_ZONES",
  // A static-time zone (STRAIGHT_LINE / POLYGON) with no deliveryMinutes: we'd
  // have to promise a handoff time we can't compute.
  "ZONE_MISSING_DELIVERY_TIME",
  // Every payment method is off — the guest reaches checkout and cannot pay.
  "NO_PAYMENT_METHOD"
]);
var DeliveryReadinessProblemSchema = z9.object({
  code: DeliveryReadinessCodeSchema,
  // Zone label when the problem is about one specific zone, else null.
  zoneLabel: z9.string().nullable()
});
var DeliveryReadinessSchema = z9.object({
  // False when delivery is switched off entirely — nothing to warn about.
  deliveryEnabled: z9.boolean(),
  problems: z9.array(DeliveryReadinessProblemSchema)
});
var OrderTypeConfigSchema = z9.object({
  id: CuidSchema,
  restaurantId: CuidSchema,
  orderType: OrderTypeSchema,
  enabled: z9.boolean(),
  // Customer email master + per-payment-kind gates. Effective customer email =
  // customerEmailEnabled AND the matching kind flag.
  customerEmailEnabled: z9.boolean(),
  customerEmailCashEnabled: z9.boolean(),
  customerEmailManualEnabled: z9.boolean(),
  customerEmailOnlineEnabled: z9.boolean(),
  // Owner/staff notify (in-app + push) master + per-payment-kind gates for new
  // orders. Effective owner notify = ownerNotifyEnabled AND the matching flag.
  ownerNotifyEnabled: z9.boolean(),
  ownerNotifyCashEnabled: z9.boolean(),
  ownerNotifyManualEnabled: z9.boolean(),
  ownerNotifyOnlineEnabled: z9.boolean(),
  // Owner/staff email for new orders of this type (rides on the owner alert).
  ownerEmailEnabled: z9.boolean(),
  // "Start prep now" staff reminder when prepStartAt comes due. Opt-in (false).
  prepReminderEnabled: z9.boolean(),
  minOrderCents: StoredIntSchema2,
  cashEnabled: z9.boolean(),
  // Card-online payment per order-type. The dashboard only renders the toggle when
  // an online provider is configured; `onlineProviderConfigured` carries that flag.
  onlineEnabled: z9.boolean(),
  onlineProviderConfigured: z9.boolean(),
  // Card-at-handoff (owner's POS terminal on pickup/delivery) per order-type.
  // Independent of onlineEnabled — offline card, no provider.
  cardOnHandoffEnabled: z9.boolean(),
  // Auto-confirm incoming pay-at-handoff orders (cash / card-on-handoff) for this
  // order-type — they skip the manual Accept and go straight to CONFIRMED.
  autoConfirmHandoff: z9.boolean(),
  // Auto-close CONFIRMED orders once the auto-complete delay has passed. Off
  // means the owner completes every order by hand.
  autoCompleteEnabled: z9.boolean(),
  // Delivery price mode — only meaningful on the DELIVERY row.
  deliveryPriceMode: DeliveryPriceModeSchema,
  distanceCalculation: DistanceCalculationSchema,
  // Dispatch slack added to live MapBox travel time (ROUTE mode only).
  deliveryBufferMinutes: StoredIntSchema2,
  completeStaleAfterMinutesFromReadyAt: StoredIntSchema2,
  cancelPendingAfterHours: StoredIntSchema2,
  // Customer-payment window (minutes): how long a manual/online order stays
  // PENDING awaiting transfer proof before the stale-order sweep cancels it.
  // Stamped onto OnlineOrder.paymentExpiresAt at placement.
  expireUnpaidAfterMinutes: StoredIntSchema2,
  updatedAt: DateTimeSchema
});
var OrderTypeConfigInputSchema = z9.object({
  enabled: z9.boolean().optional(),
  customerEmailEnabled: z9.boolean().optional(),
  customerEmailCashEnabled: z9.boolean().optional(),
  customerEmailManualEnabled: z9.boolean().optional(),
  customerEmailOnlineEnabled: z9.boolean().optional(),
  ownerNotifyEnabled: z9.boolean().optional(),
  ownerNotifyCashEnabled: z9.boolean().optional(),
  ownerNotifyManualEnabled: z9.boolean().optional(),
  ownerNotifyOnlineEnabled: z9.boolean().optional(),
  ownerEmailEnabled: z9.boolean().optional(),
  prepReminderEnabled: z9.boolean().optional(),
  minOrderCents: NonNegativeIntSchema.optional(),
  cashEnabled: z9.boolean().optional(),
  onlineEnabled: z9.boolean().optional(),
  cardOnHandoffEnabled: z9.boolean().optional(),
  autoConfirmHandoff: z9.boolean().optional(),
  autoCompleteEnabled: z9.boolean().optional(),
  deliveryPriceMode: DeliveryPriceModeSchema.optional(),
  distanceCalculation: DistanceCalculationSchema.optional(),
  deliveryBufferMinutes: NonNegativeIntSchema.max(120).optional(),
  completeStaleAfterMinutesFromReadyAt: z9.number().int().positive().optional(),
  cancelPendingAfterHours: z9.number().int().positive().optional(),
  // Guardrailed 1 min .. 24 h — an owner-facing payment-proof window.
  expireUnpaidAfterMinutes: z9.number().int().min(1).max(1440).optional()
});
var ShiftModeSchema = z9.enum(["SIMPLE", "ADVANCED"]);
var TimeOfDay = z9.string().regex(/^\d{2}:\d{2}$/);
var DaysOfWeek = z9.array(z9.number().int().min(0).max(6)).min(1);
var OrderSimpleRulesSchema = z9.object({
  mode: z9.literal("SIMPLE"),
  // Pinned to 60: the owner thinks "max N orders per hour".
  slotMinutes: z9.literal(60).default(60),
  maxOrdersPerSlot: z9.number().int().positive().nullable()
});
var OrderAdvancedRulesSchema = z9.object({
  mode: z9.literal("ADVANCED"),
  // The booking GRID step — spacing of the pickable times the guest sees.
  slotMinutes: z9.number().int().positive(),
  maxOrdersPerSlot: z9.number().int().positive().nullable(),
  // The window `maxOrdersPerSlot` is counted over. Decouples "how finely can a
  // guest pick a time" (slotMinutes) from "over how long do we cap volume"
  // (e.g. book every 15 min but cap 200/hour → slotMinutes 15, window 60).
  // Absent = the legacy behaviour where the window equals the booking slot.
  // Must be a positive multiple of slotMinutes so buckets align to the grid.
  capacityWindowMinutes: z9.number().int().positive().optional()
});
var OrderShiftRulesSchema = z9.discriminatedUnion("mode", [
  OrderSimpleRulesSchema,
  OrderAdvancedRulesSchema
]);
var OPENING_BUFFER_MINUTES_MAX = 240;
var OpeningBufferMinutes = z9.number().int().nonnegative().max(OPENING_BUFFER_MINUTES_MAX);
var OrderShiftCommon = z9.object({
  id: CuidSchema,
  restaurantId: CuidSchema,
  orderType: OrderTypeSchema,
  label: z9.string(),
  prepTimeMinutes: z9.number().int().nonnegative().nullable(),
  schedulingLeadMinutes: z9.number().int().nonnegative().nullable(),
  openingBufferMinutes: z9.number().int().nonnegative().nullable(),
  mode: ShiftModeSchema,
  rules: OrderShiftRulesSchema,
  isActive: z9.boolean(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema
});
var BaselineOrderShiftSchema = OrderShiftCommon.extend({
  scope: z9.literal("BASELINE")
});
var WindowOrderShiftSchema = OrderShiftCommon.extend({
  scope: z9.literal("WINDOW"),
  startTime: TimeOfDay,
  endTime: TimeOfDay,
  dayOfWeek: DaysOfWeek
});
var OrderShiftSchema = z9.discriminatedUnion("scope", [
  BaselineOrderShiftSchema,
  WindowOrderShiftSchema
]);
var CreateOrderShiftCommon = z9.object({
  orderType: OrderTypeSchema,
  label: z9.string().min(1).max(100),
  prepTimeMinutes: z9.number().int().nonnegative().nullable().optional(),
  schedulingLeadMinutes: z9.number().int().nonnegative().nullable().optional(),
  openingBufferMinutes: OpeningBufferMinutes.nullable().optional(),
  rules: OrderShiftRulesSchema,
  isActive: z9.boolean().optional()
});
var CreateBaselineOrderShiftSchema = CreateOrderShiftCommon.extend({
  scope: z9.literal("BASELINE")
});
var CreateWindowOrderShiftSchema = CreateOrderShiftCommon.extend({
  scope: z9.literal("WINDOW"),
  startTime: TimeOfDay,
  endTime: TimeOfDay,
  dayOfWeek: DaysOfWeek
});
var CreateOrderShiftSchema = z9.discriminatedUnion("scope", [
  CreateBaselineOrderShiftSchema,
  CreateWindowOrderShiftSchema
]);
var UpdateOrderShiftSchema = z9.object({
  label: z9.string().min(1).max(100).optional(),
  prepTimeMinutes: z9.number().int().nonnegative().nullable().optional(),
  schedulingLeadMinutes: z9.number().int().nonnegative().nullable().optional(),
  openingBufferMinutes: OpeningBufferMinutes.nullable().optional(),
  rules: OrderShiftRulesSchema.optional(),
  // Only meaningful for WINDOW shifts; rejected on a BASELINE by the use-case.
  startTime: TimeOfDay.optional(),
  endTime: TimeOfDay.optional(),
  dayOfWeek: DaysOfWeek.optional(),
  isActive: z9.boolean().optional()
});
var OrderCapacityModeSchema = z9.object({
  orderType: OrderTypeSchema,
  shiftMode: ShiftModeSchema
});
var OrderShiftRestaurantParamsSchema = z9.object({ restaurantId: CuidSchema });
var OrderShiftParamsSchema = OrderShiftRestaurantParamsSchema.extend({
  shiftId: CuidSchema
});
var OrderCapacityModeQuerySchema = z9.object({ orderType: OrderTypeSchema });
var OrderShiftListQuerySchema = z9.object({
  orderType: OrderTypeSchema,
  activeOnly: z9.coerce.boolean().optional()
});
var DateStr = z9.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD");
var OrderHolidayHoursSchema = z9.object({
  id: CuidSchema,
  restaurantId: CuidSchema,
  orderType: OrderTypeSchema,
  name: z9.string(),
  startDate: DateStr,
  endDate: DateStr,
  openTime: TimeOfDay,
  closeTime: TimeOfDay,
  maxOrdersPerSlot: z9.number().int().positive().nullable(),
  slotMinutes: z9.number().int().positive(),
  prepTimeMinutes: z9.number().int().nonnegative(),
  schedulingLeadMinutes: z9.number().int().nonnegative(),
  enabled: z9.boolean(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema
});
var CreateOrderHolidayHoursSchema = z9.object({
  orderType: OrderTypeSchema,
  name: z9.string().min(1).max(100),
  startDate: DateStr,
  endDate: DateStr,
  openTime: TimeOfDay,
  closeTime: TimeOfDay,
  maxOrdersPerSlot: z9.number().int().positive().nullable(),
  slotMinutes: z9.number().int().positive(),
  prepTimeMinutes: z9.number().int().nonnegative(),
  schedulingLeadMinutes: z9.number().int().nonnegative(),
  enabled: z9.boolean().optional()
});
var UpdateOrderHolidayHoursSchema = CreateOrderHolidayHoursSchema.partial().omit({
  orderType: true
});
var OrderHolidayHoursRestaurantParamsSchema = z9.object({ restaurantId: CuidSchema });
var OrderHolidayHoursParamsSchema = OrderHolidayHoursRestaurantParamsSchema.extend({
  holidayId: CuidSchema
});
var OrderHolidayHoursListQuerySchema = z9.object({ orderType: OrderTypeSchema });
var PauseOrderInputSchema = z9.object({
  orderType: OrderTypeSchema,
  mode: PauseModeSchema,
  durationMinutes: z9.number().int().positive().optional()
});
var OrderPauseStateSchema = z9.object({
  orderType: OrderTypeSchema,
  paused: z9.boolean(),
  mode: PauseModeSchema.nullable(),
  pausedUntil: z9.iso.datetime().nullable()
});
var UnpauseOrderInputSchema = z9.object({ orderType: OrderTypeSchema });
var PauseOrderResultSchema = z9.object({ pausedUntil: DateTimeSchema.nullable() });
var OrderPrepTransitionInputSchema = z9.object({
  expectedPrepStatus: OnlineOrderPrepStatusSchema.optional()
});
var UpdateDeliveryZoneInputSchema = DeliveryZoneInputSchema.partial().extend({
  minOrderCents: NonNegativeIntSchema.optional()
});
var OrderIdParamsSchema = z9.object({ orderId: CuidSchema });
var OrderRestaurantParamsSchema = z9.object({ restaurantId: CuidSchema });
var OwnerOrderParamsSchema = OrderRestaurantParamsSchema.extend({ orderId: CuidSchema });
var OrderTypeConfigParamsSchema = OrderRestaurantParamsSchema.extend({
  orderType: OrderTypeSchema
});
var DeliveryZoneParamsSchema = OrderRestaurantParamsSchema.extend({ zoneId: CuidSchema });

// src/schemas/awaiting-payment-order.ts
import { z as z10 } from "zod";
var AwaitingPaymentOrdersParamsSchema = z10.object({ restaurantId: CuidSchema });
var AwaitingPaymentOrderSchema = z10.object({
  id: CuidSchema,
  confirmationCode: z10.string(),
  customerName: z10.string(),
  customerPhone: z10.string(),
  type: OrderTypeSchema,
  totalCents: z10.number().int(),
  currency: z10.string(),
  paymentMethodKind: PaymentMethodKindSchema,
  paymentStatus: PaymentStatusSchema,
  createdAt: DateTimeSchema,
  paymentExpiresAt: DateTimeSchema.nullable()
});
var AwaitingPaymentOrderListSchema = z10.object({
  orders: z10.array(AwaitingPaymentOrderSchema)
});

// src/schemas/customer.ts
import { z as z11 } from "zod";
var CustomerPhoneSchema = z11.object({
  id: CuidSchema,
  // Canonical E.164 (e.g. "+49901234567") — stored normalized, shown as-is.
  phone: z11.string(),
  label: z11.string().nullable(),
  isPrimary: z11.boolean(),
  isActive: z11.boolean()
});
var CustomerSchema = z11.object({
  id: CuidSchema,
  franchiseId: CuidSchema,
  firstName: z11.string(),
  lastName: z11.string(),
  email: z11.string().nullable(),
  /** YYYY-MM-DD */
  dateOfBirth: DateSchema.nullable(),
  notes: z11.string().nullable(),
  preferredLanguage: z11.string(),
  totalVisits: z11.number().int(),
  lastVisitAt: z11.iso.datetime().nullable(),
  isActive: z11.boolean(),
  createdAt: z11.iso.datetime(),
  updatedAt: z11.iso.datetime(),
  phoneNumbers: z11.array(CustomerPhoneSchema)
});
var CustomerListQuerySchema = z11.object({
  search: z11.string().optional(),
  page: z11.coerce.number().int().positive().optional(),
  limit: z11.coerce.number().int().positive().max(100).optional()
});
var CustomerListResponseSchema = z11.object({
  items: z11.array(CustomerSchema),
  total: z11.number().int(),
  page: z11.number().int(),
  limit: z11.number().int()
});
var CustomerCreateSchema = z11.object({
  firstName: z11.string().min(1).max(100),
  lastName: z11.string().min(1).max(100),
  email: EmailSchema.optional(),
  /** YYYY-MM-DD */
  dateOfBirth: DateSchema.optional(),
  notes: z11.string().max(2e3).optional()
});
var CustomerUpdateSchema = z11.object({
  firstName: z11.string().min(1).max(100).optional(),
  lastName: z11.string().min(1).max(100).optional(),
  // Nullable so an existing email can be cleared back to null (DB is nullable);
  // omit = leave unchanged, null = clear.
  email: EmailSchema.nullable().optional(),
  dateOfBirth: DateSchema.optional(),
  notes: z11.string().max(2e3).optional(),
  /** Active state. Toggled inline on the detail page (soft delete = false). */
  isActive: z11.boolean().optional()
});
var AddCustomerPhoneSchema = z11.object({
  phone: z11.string().min(1).max(30),
  label: z11.string().max(50).optional()
});
var UpdateCustomerPhoneSchema = z11.object({
  label: z11.string().max(50).optional(),
  isPrimary: z11.boolean().optional()
});
var CustomerFranchiseParamsSchema = z11.object({ franchiseId: CuidSchema });
var CustomerParamsSchema = z11.object({ customerId: CuidSchema });
var CustomerPhoneParamsSchema = z11.object({ phoneId: CuidSchema });

// src/schemas/reservation.ts
import { ArrivalStatus, ReservationSource, ReservationStatus } from "@restaurant/types";
import { z as z12 } from "zod";
var ReservationStatusSchema = z12.enum(ReservationStatus);
var ArrivalStatusSchema = z12.enum(ArrivalStatus);
var ReservationSourceSchema = z12.enum(ReservationSource);
var ReservationEventTypeSchema = z12.enum([
  "CREATED",
  "CONFIRMED",
  "REJECTED",
  "CANCELLED_BY_GUEST",
  "CANCELLED_BY_STAFF",
  "MODIFIED",
  "REACTIVATED",
  "AUTO_ARRIVED",
  "AUTO_COMPLETED",
  "NO_SHOW_ALERTED",
  "ARRIVAL_MARKED",
  "STATUS_CHANGED"
]);
var ReservationActorTypeSchema = z12.enum(["GUEST", "STAFF", "SYSTEM"]);
var ReservationEventSchema = z12.object({
  id: CuidSchema,
  type: ReservationEventTypeSchema,
  fromStatus: ReservationStatusSchema.nullable(),
  toStatus: ReservationStatusSchema.nullable(),
  fromArrivalStatus: ArrivalStatusSchema.nullable(),
  toArrivalStatus: ArrivalStatusSchema.nullable(),
  actorType: ReservationActorTypeSchema,
  actorUserId: z12.string().nullable(),
  actorCustomerId: z12.string().nullable(),
  reason: z12.string().nullable(),
  createdAt: DateTimeSchema
});
var GuestInfoSchema = z12.object({
  name: z12.string().min(1).max(100),
  // Cap the length here (EmailSchema itself has no max) so an over-long address
  // can't slip past the guest/staff forms into the booking.
  email: EmailSchema.max(120).nullable(),
  phone: PhoneSchema.nullable()
});
var OCCASION_VALUES = ["BIRTHDAY", "ANNIVERSARY", "BUSINESS", "DATE"];
var SEATING_VALUES = [
  "WINDOW",
  "QUIET",
  "OUTDOOR",
  "BOOTH",
  "BABY_CHAIR",
  "HIGH_CHAIR",
  "WHEELCHAIR_ACCESS",
  "GROUND_FLOOR"
];
var SpecialRequestSchema = z12.object({
  occasion: z12.array(z12.enum(OCCASION_VALUES)),
  seatingPreference: z12.array(z12.enum(SEATING_VALUES))
});
var ReservationSchema = z12.object({
  id: CuidSchema,
  restaurantId: CuidSchema,
  customerId: CuidSchema.nullable(),
  confirmationCode: z12.string(),
  // Single source of truth: UTC ISO instant. Frontend formats to local
  // using restaurant timezone (see docs/DATETIME_GUIDE.md).
  time: DateTimeSchema,
  partySize: PositiveIntSchema,
  duration: PositiveIntSchema,
  guest: GuestInfoSchema,
  source: ReservationSourceSchema,
  status: ReservationStatusSchema,
  arrivalStatus: ArrivalStatusSchema.nullable(),
  specialRequest: SpecialRequestSchema.nullable(),
  internalNote: z12.string().max(500).nullable(),
  language: z12.string().length(2),
  confirmedAt: DateTimeSchema.nullable(),
  arrivedAt: DateTimeSchema.nullable(),
  completedAt: DateTimeSchema.nullable(),
  cancelledAt: DateTimeSchema.nullable(),
  cancellationReason: z12.string().max(500).nullable(),
  noShowMarkedAt: DateTimeSchema.nullable(),
  // Tables this reservation is assigned to (ADVANCED mode). Empty for SIMPLE
  // mode bookings or anything not yet seated. `.default([])` keeps existing
  // callers compatible — older payloads silently fill in an empty array.
  tableIds: z12.array(CuidSchema).default([]),
  // Append-only state-change audit log, ordered createdAt asc. Only the detail
  // endpoint (`get`) loads these; list/mutation payloads default to an empty
  // array (mirrors the order timeline / tableIds pattern).
  events: z12.array(ReservationEventSchema).default([]),
  labels: z12.array(LabelSchema).default([]),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema
});
var ReservationCreateSchema = z12.object({
  date: DateSchema,
  time: TimeSchema,
  partySize: PositiveIntSchema,
  duration: PositiveIntSchema.optional(),
  // Will use default if not provided
  guest: GuestInfoSchema,
  specialRequest: z12.object({
    occasion: z12.array(z12.enum(OCCASION_VALUES)).optional(),
    seatingPreference: z12.array(z12.enum(SEATING_VALUES)).optional()
  }).optional(),
  language: z12.string().length(2).optional()
});
var ReservationUpdateSchema = z12.object({
  date: DateSchema.optional(),
  time: TimeSchema.optional(),
  partySize: PositiveIntSchema.optional(),
  duration: PositiveIntSchema.optional(),
  guest: GuestInfoSchema.partial().optional(),
  specialRequest: z12.object({
    // Send the full desired array (an empty array clears the field).
    occasion: z12.array(z12.enum(OCCASION_VALUES)).optional(),
    seatingPreference: z12.array(z12.enum(SEATING_VALUES)).optional()
  }).optional(),
  internalNote: z12.string().max(500).nullable().optional()
});
var StaffReservationCreateSchema = ReservationCreateSchema.omit({ language: true }).extend(
  {
    source: z12.enum([ReservationSource.PHONE, ReservationSource.WALK_IN]),
    internalNote: z12.string().max(500).optional()
  }
);
var RejectReservationSchema = z12.object({
  reason: z12.string().max(500)
});
var CancelReservationSchema = z12.object({
  reason: z12.string().max(500).optional()
});
var MarkArrivalStatusSchema = z12.object({
  arrivalStatus: z12.enum([
    ArrivalStatus.ARRIVED,
    ArrivalStatus.SEATED,
    ArrivalStatus.IN_PROGRESS,
    ArrivalStatus.COMPLETED,
    ArrivalStatus.NO_SHOW
  ])
});
var SetArrivalStatusSchema = z12.object({
  arrivalStatus: z12.enum([
    "WAITING",
    "ARRIVED",
    "SEATED",
    "IN_PROGRESS",
    "COMPLETED",
    "NO_SHOW",
    "RESCHEDULED"
  ]),
  note: z12.string().max(500).optional()
});
var PartySizeQuerySchema = z12.coerce.number().int().min(1).default(2);
var AvailabilityQuerySchema = z12.object({
  date: DateSchema,
  partySize: PartySizeQuerySchema
});
var AvailabilityResponseSchema = z12.object({
  data: z12.array(z12.object({ time: z12.string(), available: z12.boolean() })),
  date: DateSchema,
  partySize: PositiveIntSchema,
  // Set only when the day is fully closed by a holiday/special-hours exception
  // (so the storefront can say "Closed for …" rather than show an empty list).
  // `reason` may be null when the operator left it blank.
  closure: z12.object({ reason: z12.string().nullable() }).nullable().optional(),
  // Set when the day is OPEN but on reduced/special hours (a MODIFIED exception),
  // so the storefront can flag the unusual hours above the slot list.
  specialHours: z12.object({ reason: z12.string().nullable() }).nullable().optional(),
  // Last-minute cutoff in minutes. Echoed so the storefront can re-apply it to a
  // slot list that goes stale while the guest fills the form.
  cutoffTime: z12.number().int().nonnegative()
});
var SlotCapacityCheckResponseSchema = z12.object({
  available: z12.boolean(),
  bookedCovers: z12.number().int().nonnegative()
});
var SlotCapacityQuerySchema = z12.object({
  date: DateSchema,
  time: TimeSchema,
  partySize: PartySizeQuerySchema
});
var AvailabilityRangeQuerySchema = z12.object({
  startDate: DateSchema,
  endDate: DateSchema,
  partySize: PartySizeQuerySchema
});
var AvailabilityRangeResponseSchema = z12.object({
  // `open`: the day has operating hours (storefront shows it on the calendar).
  // `available`: it currently has bookable slots. An open-but-unavailable day
  // (paused / fully booked / all slots past) stays on the calendar but empty;
  // a not-open day is dropped from the calendar entirely.
  // `reason`: a holiday/special-hours note when an exception applies that day
  // (e.g. "Public holiday" for a closed day, or special-hours text for a
  // reduced-hours day); null on a normal day.
  data: z12.array(
    z12.object({
      date: DateSchema,
      available: z12.boolean(),
      open: z12.boolean(),
      reason: z12.string().nullable()
    })
  )
});
var ReservationListQuerySchema = z12.object({
  date: DateSchema.optional(),
  page: z12.coerce.number().int().min(1).default(1),
  limit: z12.coerce.number().int().min(1).max(1e3).default(50)
});
var ReservationListSummarySchema = z12.object({
  totalReservations: z12.number().int().nonnegative(),
  totalGuests: z12.number().int().nonnegative()
});
var ReservationListResponseSchema = z12.object({
  date: DateSchema,
  data: z12.array(ReservationSchema),
  summary: ReservationListSummarySchema,
  page: z12.number().int(),
  limit: z12.number().int(),
  total: z12.number().int()
});
var ReservationRestaurantParamsSchema = z12.object({ restaurantId: CuidSchema });
var ReservationParamsSchema = z12.object({ reservationId: CuidSchema });
var GuestReservationParamsSchema = z12.object({ confirmationCode: z12.string() });
var PendingReservationsQuerySchema = z12.object({
  page: z12.coerce.number().int().min(1).default(1),
  limit: z12.coerce.number().int().min(1).max(1e3).default(50)
});

// src/schemas/reservation-setup.ts
import { z as z13 } from "zod";
var ShiftModeSchema2 = z13.enum(["SIMPLE", "ADVANCED"]);
var TableModeSchema = z13.enum(["SIMPLE", "ADVANCED"]);
var TableAttributeSchema = z13.enum([
  "WINDOW",
  "BOOTH",
  "OUTDOOR",
  "BAR",
  "HIGH_TOP",
  "PRIVATE_ROOM",
  "QUIET",
  "WHEELCHAIR",
  "NEAR_FIREPLACE",
  "KIDS_FRIENDLY"
]);
var ZoneSchema = z13.object({
  id: CuidSchema,
  restaurantId: CuidSchema,
  franchiseId: CuidSchema,
  label: z13.string(),
  sortOrder: z13.number().int(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema
});
var CreateZoneSchema = z13.object({
  label: z13.string().min(1).max(60),
  sortOrder: z13.number().int().min(0).optional().default(0)
});
var UpdateZoneSchema = z13.object({
  label: z13.string().min(1).max(60).optional(),
  sortOrder: z13.number().int().min(0).optional()
});
var RestaurantTableSchema = z13.object({
  id: CuidSchema,
  restaurantId: CuidSchema,
  franchiseId: CuidSchema,
  zoneId: CuidSchema.nullable(),
  label: z13.string(),
  // always resolved — null in DB is computed as "{zone} - {seats}-table - #{ordinal}"
  seats: z13.number().int().positive(),
  ordinal: z13.number().int(),
  attributes: z13.array(TableAttributeSchema),
  customTags: z13.array(z13.string()),
  qrToken: z13.string().nullable(),
  countsForCapacity: z13.boolean(),
  isBookableOnline: z13.boolean(),
  isMergeable: z13.boolean(),
  isActive: z13.boolean(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema
});
var TableCustomTagSchema = z13.string().min(1).max(40);
var UpdateRestaurantTableSchema = z13.object({
  label: z13.string().min(1).max(40).optional(),
  seats: z13.number().int().positive().optional(),
  zoneId: CuidSchema.nullable().optional(),
  attributes: z13.array(TableAttributeSchema).optional(),
  customTags: z13.array(TableCustomTagSchema).optional(),
  countsForCapacity: z13.boolean().optional(),
  isBookableOnline: z13.boolean().optional(),
  isMergeable: z13.boolean().optional(),
  isActive: z13.boolean().optional()
});
var BulkCreateTablesSchema = z13.object({
  zoneId: CuidSchema.nullable().optional(),
  labelPrefix: z13.string().min(1).max(8).optional().default("T"),
  rows: z13.array(
    z13.object({
      seats: z13.number().int().positive(),
      quantity: z13.number().int().positive().max(200)
    })
  ).min(1)
});
var BulkCreateTablesResponseSchema = z13.object({
  created: z13.number().int().nonnegative()
});
var TimeOfDay2 = TimeSchema;
var DaysOfWeek2 = z13.array(z13.number().int().min(0).max(6)).min(1);
var SimpleShiftRulesSchema = z13.object({
  mode: z13.literal("SIMPLE"),
  // The window the caps below are counted over, NOT the booking-grid step. The
  // grid (how finely a guest picks a time) is the restaurant-wide "Slot spacing"
  // (ReservationConfig.slotInterval); this is "over how long do we cap volume".
  // Default 60 → the owner's "N bookings per hour" while the grid can still be
  // every 15 min. Aligned to local midnight (see SlotCounter.countSlot).
  slotMinutes: z13.number().int().positive().default(60),
  maxReservationsPerSlot: z13.number().int().positive().nullable(),
  maxCoversPerSlot: z13.number().int().positive().nullable()
});
var PartySizePacingRuleSchema = z13.object({
  minPartySize: z13.number().int().min(1),
  maxPartiesPerBucket: z13.number().int().min(0)
});
var AdvancedShiftRulesSchema = z13.object({
  mode: z13.literal("ADVANCED"),
  // Pacing = rate of arrivals. Caps new bookings *starting* inside a rolling
  // 15-min window. Protects kitchen / greet throughput at the moment guests
  // walk in.
  pacingMaxCovers: z13.number().int().positive().nullable(),
  pacingMaxParties: z13.number().int().positive().nullable(),
  // Concurrent = load. Caps bookings *currently active* at any moment. A
  // booking is active across `[start, start + duration)`. Useful as a soft
  // ceiling below physical table count — e.g. 12 tables but only 8 servers'
  // worth of bandwidth. Independent of pacing: two parties 20 min apart
  // can fit both pacing caps yet still overlap.
  // .default(null) so older rows (persisted before these fields existed) still
  // parse — Zod fills the missing keys with null instead of rejecting.
  maxConcurrentParties: z13.number().int().positive().nullable().default(null),
  maxConcurrentCovers: z13.number().int().positive().nullable().default(null),
  partySizePacing: z13.array(PartySizePacingRuleSchema).default([])
});
var ShiftRulesSchema = z13.discriminatedUnion("mode", [
  SimpleShiftRulesSchema,
  AdvancedShiftRulesSchema
]);
var ShiftCommon = z13.object({
  id: CuidSchema,
  restaurantId: CuidSchema,
  franchiseId: CuidSchema,
  label: z13.string(),
  defaultDurationMinutes: z13.number().int().positive(),
  mode: ShiftModeSchema2,
  rules: ShiftRulesSchema,
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema
});
var BaselineShiftSchema = ShiftCommon.extend({
  scope: z13.literal("BASELINE")
});
var WindowShiftSchema = ShiftCommon.extend({
  scope: z13.literal("WINDOW"),
  startTime: TimeOfDay2,
  endTime: TimeOfDay2,
  daysOfWeek: DaysOfWeek2
});
var ReservationShiftSchema = z13.discriminatedUnion("scope", [
  BaselineShiftSchema,
  WindowShiftSchema
]);
var CreateShiftCommon = z13.object({
  label: z13.string().min(1).max(60),
  defaultDurationMinutes: z13.number().int().positive().optional().default(60),
  rules: ShiftRulesSchema
});
var CreateBaselineShiftSchema = CreateShiftCommon.extend({
  scope: z13.literal("BASELINE")
});
var CreateWindowShiftSchema = CreateShiftCommon.extend({
  scope: z13.literal("WINDOW"),
  startTime: TimeOfDay2,
  endTime: TimeOfDay2,
  daysOfWeek: DaysOfWeek2
});
var CreateShiftSchema = z13.discriminatedUnion("scope", [
  CreateBaselineShiftSchema,
  CreateWindowShiftSchema
]);
var UpdateShiftSchema = z13.object({
  label: z13.string().min(1).max(60).optional(),
  defaultDurationMinutes: z13.number().int().positive().optional(),
  rules: ShiftRulesSchema.optional(),
  // Only meaningful for WINDOW shifts. The use-case rejects these on a BASELINE.
  startTime: TimeOfDay2.optional(),
  endTime: TimeOfDay2.optional(),
  daysOfWeek: DaysOfWeek2.optional()
});
var CapacityModeSchema = z13.object({
  shiftMode: ShiftModeSchema2
});
var CapacityModeViewSchema = CapacityModeSchema.extend({
  defaultBaselineRules: ShiftRulesSchema
});
var TableModeViewSchema = z13.object({
  tableMode: TableModeSchema
});
var SimpleTableCategorySchema = z13.object({
  seats: z13.number().int().positive().max(50),
  count: z13.number().int().min(1).max(200)
});
var SetZoneTableLayoutSchema = z13.object({ categories: z13.array(SimpleTableCategorySchema).max(20) }).refine((d) => new Set(d.categories.map((c) => c.seats)).size === d.categories.length, {
  error: "Each seat-category may appear at most once"
});
var ZoneTableLayoutResultSchema = z13.object({
  created: z13.number().int().nonnegative(),
  reactivated: z13.number().int().nonnegative(),
  deactivated: z13.number().int().nonnegative()
});
var Coord = z13.number().finite();
var Size = z13.number().finite().positive();
var FloorPlanTableElementSchema = z13.object({
  kind: z13.literal("table"),
  tableId: z13.string(),
  x: Coord,
  y: Coord,
  width: Size,
  height: Size,
  rotation: z13.number().finite().default(0),
  shape: z13.enum(["rect", "circle"]).default("rect")
});
var FloorPlanWallElementSchema = z13.object({
  kind: z13.literal("wall"),
  x1: Coord,
  y1: Coord,
  x2: Coord,
  y2: Coord,
  thickness: Size.default(4)
});
var FloorPlanLabelElementSchema = z13.object({
  kind: z13.literal("label"),
  x: Coord,
  y: Coord,
  text: z13.string().max(120)
});
var FloorPlanElementSchema = z13.discriminatedUnion("kind", [
  FloorPlanTableElementSchema,
  FloorPlanWallElementSchema,
  FloorPlanLabelElementSchema
]);
var FloorPlanLayoutSchema = z13.object({
  canvas: z13.object({
    width: Size.default(1200),
    height: Size.default(800),
    backgroundImageKey: z13.string().nullable().default(null)
  }),
  elements: z13.array(FloorPlanElementSchema).default([])
});
var FloorPlanSchema = z13.object({
  id: z13.string().nullable(),
  // null when no plan has been saved yet
  restaurantId: CuidSchema,
  franchiseId: CuidSchema,
  version: z13.number().int().nonnegative(),
  layout: FloorPlanLayoutSchema
});
var UpdateFloorPlanSchema = z13.object({
  version: z13.number().int().nonnegative(),
  layout: FloorPlanLayoutSchema
});
var ReservationSetupParamsSchema = z13.object({ restaurantId: CuidSchema });
var ZoneParamsSchema = ReservationSetupParamsSchema.extend({ zoneId: CuidSchema });
var RestaurantTableParamsSchema = ReservationSetupParamsSchema.extend({
  tableId: CuidSchema
});
var ShiftParamsSchema = ReservationSetupParamsSchema.extend({ shiftId: CuidSchema });
var ActiveOnlyQuerySchema = z13.object({
  activeOnly: z13.coerce.boolean().optional()
});
var DEFAULT_SHIFT_RULES = {
  SIMPLE: {
    mode: "SIMPLE",
    slotMinutes: 60,
    maxReservationsPerSlot: null,
    maxCoversPerSlot: 500
  },
  ADVANCED: {
    mode: "ADVANCED",
    pacingMaxCovers: 500,
    pacingMaxParties: 12,
    maxConcurrentParties: null,
    maxConcurrentCovers: null,
    partySizePacing: []
  }
};

// src/schemas/reservation-config.ts
import { z as z14 } from "zod";
var AutoConfirmModeSchema = z14.enum(["AUTO", "MANUAL"]);
var ReservationConfigEditableSchema = z14.object({
  enabled: z14.boolean(),
  // When off, customer-facing reservation emails are suppressed (owner/staff
  // notifications still fire).
  customerEmailEnabled: z14.boolean(),
  // When off, owner/staff new-booking push + inbox alerts are suppressed.
  ownerNotifyEnabled: z14.boolean(),
  // When on, owner/staff also receive an email for each new booking (rides on
  // the staff new-booking alert; requires ownerNotifyEnabled to fire).
  ownerEmailEnabled: z14.boolean(),
  // When off, owner/staff push + inbox alerts for guest cancellations are
  // suppressed (independent of the new-booking alert).
  ownerNotifyCancellationEnabled: z14.boolean(),
  // When off, the no-show owner/staff push + inbox alert is suppressed.
  ownerNotifyNoShowEnabled: z14.boolean(),
  // Master switch for guest reminder emails (independent of customerEmailEnabled;
  // both must be on for a reminder to send).
  reminderEnabled: z14.boolean(),
  // Booking-form field gates — when off, the storefront hides that picker.
  occasionEnabled: z14.boolean(),
  seatingPreferenceEnabled: z14.boolean(),
  duration: z14.number().int().min(15),
  bufferTime: z14.number().int().min(0),
  autoConfirmMode: AutoConfirmModeSchema,
  maxPartySize: z14.number().int().min(1),
  largePartyMsg: z14.string().max(280).nullable(),
  advanceWindow: z14.number().int().min(1),
  // Minutes before the slot to stop taking bookings (last-minute cutoff). 0 =
  // bookings allowed right up to the slot start.
  cutoffTime: z14.number().int().min(0),
  slotInterval: z14.number().int().min(5),
  // Stop offering online booking slots this many minutes before each service
  // closes — the owner's "last booking before close" policy. A 22:00 close with
  // 60 takes its last booking at 21:00. 0 = up to close. Independent of dining
  // duration: staff can work overtime, so this is purely how late the owner will
  // still accept a NEW booking, not whether the meal finishes by close.
  closingBufferMinutes: z14.number().int().min(0),
  gracePeriod: z14.number().int().min(0),
  // Temporary "pause new bookings" switch (the "pause today" action sets this to
  // the end of the local day). Slots before this instant are refused/hidden.
  // Null = not paused. ISO-8601 instant.
  pausedUntil: z14.iso.datetime().nullable()
});
var ReservationConfigSchema = ReservationConfigEditableSchema.extend({
  // Read-only: the public storefront booking URL for this restaurant, ready to
  // copy/share. Derived from the franchise + restaurant slugs and the configured
  // storefront base URL — never accepted on update.
  bookingUrl: z14.string()
});
var ReservationConfigUpdateSchema = ReservationConfigEditableSchema.partial();
var ReservationConfigParamsSchema = z14.object({ restaurantId: CuidSchema });

// src/schemas/ops-feed.ts
import { z as z15 } from "zod";
var OpsEventTypeSchema = z15.enum(["RESERVATION_CREATED", "ONLINE_ORDER_PLACED"]);
var OpsEventSummarySchema = z15.object({
  id: z15.string(),
  type: OpsEventTypeSchema,
  franchiseId: z15.string(),
  restaurantId: z15.string(),
  refId: z15.string(),
  refCode: z15.string(),
  headline: z15.string(),
  occurredAt: z15.string(),
  traceId: z15.string().nullable()
});
var OpsEventDetailSchema = OpsEventSummarySchema.extend({
  payload: z15.unknown()
});
var OpsEventListQuerySchema = z15.object({
  type: OpsEventTypeSchema.optional(),
  restaurantId: z15.string().optional(),
  /** ISO date-times bounding `occurredAt` — `from` inclusive, `to` exclusive. */
  from: z15.string().optional(),
  to: z15.string().optional(),
  /** Keyset cursor: the `nextCursor` of the previous page. */
  cursor: z15.string().optional(),
  limit: z15.coerce.number().int().min(1).max(200).optional()
});
var OpsEventPageSchema = z15.object({
  items: z15.array(OpsEventSummarySchema),
  nextCursor: z15.string().nullable()
});
var OpsEventParamsSchema = z15.object({ id: z15.string() });

// src/schemas/partner.ts
import { z as z16 } from "zod";
var PartnerGroupRoleSchema = z16.enum(["GROUP_ADMIN", "GROUP_MEMBER"]);
var PartnerMemberStatusSchema = z16.enum(["ACTIVE", "INACTIVE", "PENDING"]);
var PartnerAccessModeSchema = z16.enum(["ALL", "SPECIFIC"]);
var PartnerFranchiseRoleSchema = z16.enum(["FRANCHISE_ADMIN", "FRANCHISE_MEMBER"]);
var PartnerGroupParamsSchema = z16.object({ groupId: CuidSchema });
var PartnerMemberParamsSchema = z16.object({ memberId: CuidSchema });
var PartnerGroupSchema = z16.object({
  id: CuidSchema,
  name: z16.string(),
  slug: z16.string(),
  description: z16.string().nullable(),
  logoUrl: z16.string().nullable(),
  isActive: z16.boolean(),
  defaultCountry: z16.string().nullable(),
  defaultTimezone: z16.string().nullable(),
  defaultCurrency: z16.string().nullable(),
  defaultLanguage: LanguageSchema.nullable(),
  createdAt: z16.iso.datetime(),
  updatedAt: z16.iso.datetime()
});
var PartnerGroupFranchiseSchema = z16.object({
  id: CuidSchema,
  groupId: CuidSchema,
  franchiseId: CuidSchema,
  createdAt: z16.iso.datetime()
});
var PartnerGroupSelectionSchema = z16.object({
  success: z16.boolean(),
  selectedGroupId: CuidSchema
});
var PartnerActionResultSchema = z16.object({
  success: z16.boolean(),
  message: z16.string()
});
var PartnerMemberInviteSchema = z16.object({
  email: EmailSchema,
  role: PartnerGroupRoleSchema,
  accessMode: PartnerAccessModeSchema,
  franchiseAssignments: z16.array(z16.object({ franchiseId: CuidSchema, role: PartnerFranchiseRoleSchema })).optional()
});
var PartnerMemberRoleChangeSchema = z16.object({
  role: PartnerGroupRoleSchema
});
var PartnerMemberSchema = z16.object({
  id: CuidSchema,
  groupId: CuidSchema,
  userId: CuidSchema,
  role: PartnerGroupRoleSchema,
  status: PartnerMemberStatusSchema,
  accessMode: PartnerAccessModeSchema,
  invitedBy: CuidSchema.nullable(),
  invitedAt: z16.iso.datetime().nullable(),
  joinedAt: z16.iso.datetime().nullable(),
  createdAt: z16.iso.datetime(),
  updatedAt: z16.iso.datetime()
});
var PartnerMemberDetailSchema = PartnerMemberSchema.extend({
  user: z16.object({
    id: CuidSchema,
    email: z16.string().nullable(),
    name: z16.string().nullable()
  }),
  franchiseAssignments: z16.array(
    z16.object({
      id: CuidSchema,
      franchiseId: CuidSchema,
      role: PartnerFranchiseRoleSchema,
      franchise: z16.object({ id: CuidSchema, name: z16.string(), slug: z16.string() })
    })
  )
});

// src/schemas/device-token.ts
import { z as z17 } from "zod";
var DevicePlatformSchema = z17.enum(["web", "ios", "android"]);
var DeviceHealthStateSchema = z17.enum([
  "ok",
  "permission_denied",
  "channel_missing",
  "channel_blocked",
  "channel_silent",
  "background_restricted",
  "delivered_quietly",
  "scheduled_summary",
  "sound_off",
  "banner_off",
  "lock_screen_off",
  "unknown"
]);
var DeviceTokenSchema = z17.object({
  id: CuidSchema,
  userId: CuidSchema,
  token: z17.string().min(1),
  platform: DevicePlatformSchema,
  isActive: z17.boolean(),
  healthState: DeviceHealthStateSchema.nullable(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema
});
var DeviceHealthSchema = z17.object({
  // OS notification permission. `denied` means nothing will ever be shown.
  permission: z17.enum(["granted", "denied", "prompt"]).optional(),
  // Android only. From 8.0 the alert SOUND is a property of the notification
  // channel, not of our payload, so these two fields are what distinguish a
  // device that will ring from one that will not. Omitted on ios/web.
  channelImportance: z17.number().int().min(0).max(5).optional(),
  channelSound: z17.boolean().optional(),
  iosAuthorization: z17.enum(["authorized", "provisional", "ephemeral", "denied", "notDetermined"]).optional(),
  iosAlertStyle: z17.enum(["none", "banner", "alert"]).optional(),
  iosSound: z17.boolean().optional(),
  iosLockScreen: z17.boolean().optional(),
  iosNotificationCenter: z17.boolean().optional(),
  iosTimeSensitive: z17.boolean().optional(),
  iosScheduledSummary: z17.boolean().optional(),
  channelVisibility: z17.number().int().min(-1e3).max(1).optional(),
  androidNotificationsEnabled: z17.boolean().optional(),
  androidInterruptionFilter: z17.enum(["all", "priority", "alarms", "none"]).optional(),
  androidAlertsBypassDnd: z17.boolean().optional(),
  androidRingerMode: z17.enum(["normal", "vibrate", "silent"]).optional(),
  androidBackgroundRestricted: z17.boolean().optional(),
  androidIgnoringBatteryOptimizations: z17.boolean().optional(),
  androidStandbyBucket: z17.enum(["active", "working_set", "frequent", "rare", "restricted"]).optional(),
  // Pins a fault to a release. `appVersion` is the OTA bundle actually running,
  // which on Capgo can differ from the installed native build.
  appVersion: z17.string().max(50).optional(),
  osVersion: z17.string().max(50).optional()
});
var RegisterDeviceTokenSchema = z17.object({
  token: z17.string().min(1),
  platform: DevicePlatformSchema,
  health: DeviceHealthSchema.optional()
});
var RegisterDeviceTokenResponseSchema = z17.object({
  data: DeviceTokenSchema,
  message: z17.string()
});
var UnregisterDeviceTokenResponseSchema = z17.object({
  message: z17.string()
});
var DeviceTokenParamsSchema = z17.object({ token: z17.string().min(1) });

// src/schemas/table-ordering.ts
import { z as z18 } from "zod";
var TableSessionStatusSchema = z18.enum(["OPEN", "BILL_REQUESTED", "CLOSED"]);
var OrderRoundStatusSchema = z18.enum(["PLACED", "FIRED", "READY", "SERVED", "CANCELLED"]);
var TableSessionActorSchema = z18.enum(["GUEST_QR", "STAFF", "SYSTEM"]);
var TableOrderingModeSchema = z18.enum(["GUEST_SELF", "STAFF_ONLY", "HYBRID"]);
var RoundFiringSchema = z18.enum(["AUTO_FIRE", "STAFF_CONFIRM"]);
var TableServiceRequestKindSchema = z18.enum(["CALL_WAITER", "REQUEST_BILL"]);
var TableServiceRequestStatusSchema = z18.enum(["PENDING", "RESOLVED"]);
var SplitModeSchema = z18.enum(["EVEN", "BY_AMOUNT", "BY_SEAT"]);
var TableSessionTimelineKindSchema = z18.enum([
  "OPENED",
  "ROUND_PLACED",
  "ROUND_FIRED",
  "ROUND_READY",
  "ROUND_SERVED",
  "ROUND_CANCELLED",
  "ITEM_VOIDED",
  "ITEM_RESTORED",
  "RESERVATION_ATTACHED",
  "SERVICE_REQUESTED",
  "SERVICE_RESOLVED",
  "BILL_REQUESTED",
  "PAYMENT_TAKEN",
  "FORCE_CLOSED",
  "MOVED",
  "MERGED_INTO",
  "CLOSED",
  "SEATED_FROM_RESERVATION"
]);
var TableDerivedStateSchema = z18.enum(["AVAILABLE", "RESERVED", "OCCUPIED"]);
var StoredIntSchema3 = z18.number().int();
var OrderRoundItemChoiceSchema = z18.object({
  id: CuidSchema,
  optionId: CuidSchema,
  optionChoiceId: CuidSchema,
  optionNameSnapshot: z18.string(),
  choiceNameSnapshot: z18.string(),
  unitPriceCents: z18.number().int(),
  allergenCodes: z18.array(z18.string())
});
var OrderRoundItemSchema = z18.object({
  id: CuidSchema,
  productId: CuidSchema,
  // Which seat this line is for; null = the Shared bucket.
  seatId: CuidSchema.nullable(),
  nameSnapshot: z18.string(),
  descriptionSnapshot: z18.string().nullable(),
  categoryNameSnapshot: z18.string().nullable(),
  quantity: StoredIntSchema3,
  unitPriceCents: StoredIntSchema3,
  lineTotalCents: StoredIntSchema3,
  allergenCodes: z18.array(z18.string()),
  allergenSnapshot: AllergenSnapshotSchema,
  guestNote: z18.string().nullable(),
  voidedAt: DateTimeSchema.nullable(),
  voidReason: z18.string().nullable(),
  choices: z18.array(OrderRoundItemChoiceSchema)
});
var OrderRoundSchema = z18.object({
  id: CuidSchema,
  sequence: StoredIntSchema3,
  status: OrderRoundStatusSchema,
  placedByActor: TableSessionActorSchema,
  firedAt: DateTimeSchema.nullable(),
  ticketPrintedAt: DateTimeSchema.nullable(),
  servedAt: DateTimeSchema.nullable(),
  cancelledAt: DateTimeSchema.nullable(),
  cancelReason: z18.string().nullable(),
  guestNote: z18.string().nullable(),
  items: z18.array(OrderRoundItemSchema),
  createdAt: DateTimeSchema
});
var SessionSeatSchema = z18.object({
  id: CuidSchema,
  ordinal: StoredIntSchema3,
  label: z18.string().nullable()
});
var TableServiceRequestSchema = z18.object({
  id: CuidSchema,
  kind: TableServiceRequestKindSchema,
  status: TableServiceRequestStatusSchema,
  note: z18.string().nullable(),
  resolvedAt: DateTimeSchema.nullable(),
  createdAt: DateTimeSchema
});
var TableSessionTimelineEntrySchema = z18.object({
  id: CuidSchema,
  kind: TableSessionTimelineKindSchema,
  fromStatus: TableSessionStatusSchema.nullable(),
  toStatus: TableSessionStatusSchema.nullable(),
  actorType: TableSessionActorSchema,
  actorUserId: z18.string().nullable(),
  note: z18.string().nullable(),
  createdAt: DateTimeSchema
});
var SessionPaymentSchema = z18.object({
  id: CuidSchema,
  status: z18.string(),
  methodKind: PaymentMethodKindSchema,
  amountCents: StoredIntSchema3,
  tipCents: StoredIntSchema3,
  paymentReference: z18.string(),
  createdAt: DateTimeSchema
});
var ProposedSplitSchema = z18.object({
  mode: SplitModeSchema,
  // EVEN: number of ways. BY_AMOUNT: one entry per party (amountCents).
  ways: StoredIntSchema3.optional(),
  parties: z18.array(
    z18.object({
      label: z18.string().optional(),
      amountCents: StoredIntSchema3.optional()
    })
  ).optional()
});
var ProposedSplitInputSchema = z18.object({
  mode: SplitModeSchema,
  ways: z18.number().int().min(2).max(20).optional(),
  parties: z18.array(
    z18.object({
      label: z18.string().max(60).optional(),
      amountCents: NonNegativeIntSchema.optional()
    })
  ).optional()
});
var TableSessionSchema = z18.object({
  id: CuidSchema,
  restaurantId: CuidSchema,
  tableId: CuidSchema,
  tableLabel: z18.string(),
  reservationId: CuidSchema.nullable(),
  status: TableSessionStatusSchema,
  openedByActor: TableSessionActorSchema,
  guestName: z18.string().nullable(),
  guestPhone: z18.string().nullable(),
  // The linked CRM customer (resolved), or null for an anonymous walk-in. When
  // present it is the source of truth for the guest's name/phone above (those
  // are resolved from it), and the FE links through to the customer profile.
  customer: z18.object({
    id: CuidSchema,
    name: z18.string(),
    phone: z18.string().nullable(),
    totalVisits: StoredIntSchema3
  }).nullable(),
  subtotalCents: StoredIntSchema3,
  serviceChargeCents: StoredIntSchema3,
  // Σ of completed payment tips (derived from payments — not stored on session).
  tipCents: StoredIntSchema3,
  totalCents: StoredIntSchema3,
  // Σ of completed payment amounts; balance = totalCents − paidCents.
  paidCents: StoredIntSchema3,
  currency: z18.string(),
  billRequestedAt: DateTimeSchema.nullable(),
  proposedSplit: ProposedSplitSchema.nullable(),
  closedAt: DateTimeSchema.nullable(),
  closeReason: z18.string().nullable(),
  rounds: z18.array(OrderRoundSchema),
  seats: z18.array(SessionSeatSchema),
  payments: z18.array(SessionPaymentSchema),
  serviceRequests: z18.array(TableServiceRequestSchema),
  timeline: z18.array(TableSessionTimelineEntrySchema).optional(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema
});
var TableSessionSummarySchema = z18.object({
  id: CuidSchema,
  tableLabel: z18.string(),
  zoneLabel: z18.string().nullable(),
  guestName: z18.string().nullable(),
  openedAt: DateTimeSchema,
  closedAt: DateTimeSchema,
  roundCount: StoredIntSchema3,
  totalCents: StoredIntSchema3,
  paidCents: StoredIntSchema3,
  currency: z18.string(),
  closeReason: z18.string().nullable(),
  // Billing type, derived for the history row's tags.
  // splitMode null = paid as a single bill (not split).
  splitMode: SplitModeSchema.nullable(),
  // The settled payment method across PAID payments: one kind, or 'MIXED'
  // when a split used more than one. Null = nothing was collected.
  paymentMethod: z18.enum(["ONLINE", "MANUAL", "CASH", "CARD_ON_HANDOFF", "GIFT_CARD", "MIXED"]).nullable()
});
var TableStateSchema = z18.object({
  tableId: CuidSchema,
  label: z18.string(),
  zoneId: CuidSchema.nullable(),
  zoneLabel: z18.string().nullable(),
  seats: StoredIntSchema3,
  qrToken: z18.string().nullable(),
  state: TableDerivedStateSchema,
  sessionId: CuidSchema.nullable(),
  sessionStatus: TableSessionStatusSchema.nullable(),
  openedAt: DateTimeSchema.nullable(),
  roundCount: StoredIntSchema3,
  totalCents: StoredIntSchema3,
  pendingServiceRequests: StoredIntSchema3,
  // Soonest upcoming reservation start (for RESERVED), if any.
  nextReservationAt: DateTimeSchema.nullable()
});
var QrResolveResponseSchema = z18.object({
  restaurantId: CuidSchema,
  restaurantName: z18.string(),
  restaurantSlug: z18.string(),
  franchiseSlug: z18.string(),
  tableId: CuidSchema,
  tableLabel: z18.string(),
  enabled: z18.boolean(),
  orderingMode: TableOrderingModeSchema,
  roundFiring: RoundFiringSchema,
  serviceChargeBps: StoredIntSchema3,
  tipPresetsBps: z18.array(StoredIntSchema3),
  currency: z18.string(),
  // The current OPEN session for this table, if one exists (else null).
  session: TableSessionSchema.nullable()
});
var TableOrderingConfigSchema = z18.object({
  id: CuidSchema,
  restaurantId: CuidSchema,
  enabled: z18.boolean(),
  orderingMode: TableOrderingModeSchema,
  roundFiring: RoundFiringSchema,
  serviceChargeBps: StoredIntSchema3,
  tipPresetsBps: z18.array(StoredIntSchema3),
  kitchenAutoPrint: z18.boolean(),
  updatedAt: DateTimeSchema
});
var TableOrderingConfigInputSchema = z18.object({
  enabled: z18.boolean().optional(),
  orderingMode: TableOrderingModeSchema.optional(),
  roundFiring: RoundFiringSchema.optional(),
  serviceChargeBps: z18.number().int().min(0).max(1e5).optional(),
  tipPresetsBps: z18.array(z18.number().int().min(0).max(1e5)).max(6).optional(),
  kitchenAutoPrint: z18.boolean().optional()
});
var RoundItemInputSchema = z18.object({
  productId: CuidSchema,
  // Capped so quantity * unitPriceCents can't overflow the 32-bit Int money math.
  quantity: z18.number().int().positive().max(999),
  guestNote: z18.string().max(500).optional(),
  // Which seat this line is for; omit/null = Shared. Staff tag per line; a
  // guest device tags every line to its own claimed seat (see claimSeat).
  seatId: CuidSchema.nullable().optional(),
  choices: z18.array(z18.object({ optionId: CuidSchema, optionChoiceId: CuidSchema })).default([])
});
var SubmitRoundInputSchema = z18.object({
  items: z18.array(RoundItemInputSchema).min(1),
  guestNote: z18.string().max(500).optional()
});
var OpenSessionInputSchema = z18.object({
  guestName: z18.string().min(1).max(100).optional(),
  guestPhone: PhoneSchema.optional()
});
var StaffOpenSessionInputSchema = z18.object({
  tableId: CuidSchema,
  guestName: z18.string().min(1).max(100).optional(),
  guestPhone: PhoneSchema.optional()
});
var RequestServiceInputSchema = z18.object({
  kind: TableServiceRequestKindSchema,
  note: z18.string().max(500).optional()
});
var RequestBillInputSchema = z18.object({
  // Optional advisory split the guest proposed on their phone.
  proposedSplit: ProposedSplitInputSchema.optional()
});
var CancelRoundInputSchema = z18.object({
  reason: z18.string().min(1).max(500)
});
var VoidItemInputSchema = z18.object({
  // Optional — staff can void a fired item without a note (audit still records
  // the action + who did it; the reason is just extra colour when given).
  reason: z18.string().max(500).optional()
});
var AttachReservationInputSchema = z18.object({
  reservationId: CuidSchema
});
var CollectMethodKindSchema = z18.enum(["ONLINE", "MANUAL", "CASH"]);
var TakePaymentInputSchema = z18.object({
  methodKind: CollectMethodKindSchema,
  amountCents: z18.number().int().positive(),
  tipCents: NonNegativeIntSchema.default(0),
  manualPaymentMethodId: CuidSchema.optional(),
  // Settle this specific pending split share (omit = settle the earliest, or
  // create a fresh PAID payment when the tab is unsplit). amountCents is
  // ignored when settling a share — the share keeps its planned amount.
  paymentId: CuidSchema.optional()
});
var SplitBillInputSchema = z18.object({
  mode: SplitModeSchema,
  ways: z18.number().int().min(2).max(20).optional(),
  parties: z18.array(
    z18.object({
      label: z18.string().max(60).optional(),
      amountCents: z18.number().int().positive().optional()
    })
  ).optional()
});
var AddSeatInputSchema = z18.object({
  label: z18.string().min(1).max(60).optional()
});
var AssignItemSeatInputSchema = z18.object({
  seatId: CuidSchema.nullable()
});
var MoveSessionInputSchema = z18.object({
  toTableId: CuidSchema
});
var MergeSessionInputSchema = z18.object({
  // The session to absorb INTO this one (this = survivor).
  fromSessionId: CuidSchema
});
var ForceCloseInputSchema = z18.object({
  reason: z18.string().min(1).max(500)
});
var LinkSessionCustomerInputSchema = z18.object({
  customerId: CuidSchema.optional(),
  name: z18.string().min(1).max(120).optional(),
  phone: PhoneSchema.optional()
});
var SeatReservationInputSchema = z18.object({
  tableId: CuidSchema,
  reservationId: CuidSchema
});
var IdentifySessionInputSchema = z18.object({
  name: z18.string().min(1).max(120).optional(),
  phone: PhoneSchema
});
var TableOrderingQrParamsSchema = z18.object({ qrToken: z18.string().min(1) });
var TableOrderingRestaurantParamsSchema = z18.object({ restaurantId: CuidSchema });
var TableSessionParamsSchema = z18.object({
  restaurantId: CuidSchema,
  sessionId: CuidSchema
});
var TableRoundParamsSchema = TableSessionParamsSchema.extend({ roundId: CuidSchema });
var TableItemParamsSchema = TableSessionParamsSchema.extend({ itemId: CuidSchema });
var TableServiceRequestParamsSchema = TableSessionParamsSchema.extend({
  requestId: CuidSchema
});
var SessionHistoryQuerySchema = z18.object({ date: z18.string().optional() });

// src/schemas/print.ts
import { z as z19 } from "zod";
var PrinterTypeSchema = z19.enum(["EPSON_SERVER_DIRECT"]);
var PrintJobTypeSchema = z19.enum(["KITCHEN_TICKET", "RECEIPT", "RESERVATION", "TEST"]);
var PrintJobStatusSchema = z19.enum(["QUEUED", "PRINTING", "PRINTED", "FAILED"]);
var PrintJobSourceKindSchema = z19.enum([
  "KITCHEN_ROUND",
  "ONLINE_ORDER",
  "SESSION_RECEIPT",
  "RESERVATION",
  "TEST_PRINT"
]);
var PrinterStatusSchema = z19.enum(["ONLINE", "OFFLINE", "ERROR"]);
var PrinterConnectionSchema = z19.object({
  deviceId: z19.string().min(1)
});
var ReceiptPaymentSnapshotSchema = z19.object({
  method: z19.string(),
  amountCents: NonNegativeIntSchema
});
var ReceiptSnapshotSchema = z19.object({
  currency: z19.string(),
  subtotalCents: NonNegativeIntSchema,
  serviceChargeCents: NonNegativeIntSchema,
  tipCents: NonNegativeIntSchema,
  totalCents: NonNegativeIntSchema,
  payments: z19.array(ReceiptPaymentSnapshotSchema)
});
var PrintPayloadSchema = z19.object({
  body: z19.string(),
  title: z19.string().optional(),
  receipt: ReceiptSnapshotSchema.optional()
});
var PrinterSchema = z19.object({
  id: CuidSchema,
  name: z19.string(),
  type: PrinterTypeSchema,
  connection: PrinterConnectionSchema,
  isActive: z19.boolean(),
  isDefault: z19.boolean(),
  // Derived, read-only.
  status: PrinterStatusSchema,
  queuedCount: NonNegativeIntSchema,
  lastHeartbeatAt: DateTimeSchema.nullable(),
  lastErrorAt: DateTimeSchema.nullable(),
  lastErrorText: z19.string().nullable(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema
});
var PrinterStatusEventSchema = z19.object({
  status: PrinterStatusSchema,
  errorText: z19.string().nullable(),
  at: DateTimeSchema
});
var PrintPageSchema = z19.object({
  id: CuidSchema,
  name: z19.string(),
  categoryIds: z19.array(CuidSchema),
  productIds: z19.array(CuidSchema),
  printerId: CuidSchema
});
var PrintRoutingSchema = z19.object({
  pages: z19.array(PrintPageSchema),
  defaultPrinterId: CuidSchema.nullable()
});
var PrintJobSchema = z19.object({
  id: CuidSchema,
  printerId: CuidSchema,
  printerName: z19.string(),
  type: PrintJobTypeSchema,
  status: PrintJobStatusSchema,
  payload: PrintPayloadSchema,
  sourceKind: PrintJobSourceKindSchema,
  sourceId: z19.string().nullable(),
  attempts: z19.number().int().nonnegative(),
  lastErrorText: z19.string().nullable(),
  queuedAt: DateTimeSchema,
  claimedAt: DateTimeSchema.nullable(),
  printedAt: DateTimeSchema.nullable(),
  failedAt: DateTimeSchema.nullable()
});
var AdoptedDeviceIdSchema = z19.string().trim().regex(/^[A-Za-z0-9]{8,16}$/);
var RegisterPrinterInputSchema = z19.object({
  name: z19.string().min(1),
  type: PrinterTypeSchema.default("EPSON_SERVER_DIRECT"),
  deviceId: AdoptedDeviceIdSchema.optional(),
  isActive: z19.boolean().default(true),
  isDefault: z19.boolean().default(false)
});
var UpdatePrinterInputSchema = z19.object({
  name: z19.string().min(1).optional(),
  isActive: z19.boolean().optional(),
  isDefault: z19.boolean().optional()
});
var PrintPageInputSchema = z19.object({
  name: z19.string().min(1),
  categoryIds: z19.array(CuidSchema),
  productIds: z19.array(CuidSchema).default([]),
  printerId: CuidSchema
});
var UpsertRoutingInputSchema = z19.object({
  pages: z19.array(PrintPageInputSchema),
  defaultPrinterId: CuidSchema.nullable()
});
var PrintReceiptInputSchema = z19.object({
  sessionId: CuidSchema
});
var PrintOrderTicketInputSchema = z19.object({
  orderId: CuidSchema
});
var PrintTextSizeSchema = z19.enum(["MEDIUM", "LARGE"]);
var PrintItemLabelModeSchema = z19.enum(["NAME", "CODE_AND_NAME", "CODE_ONLY"]);
var PrintDateTimeFormatSchema = z19.enum(["DMY_SLASH", "DMY_DOT", "YMD_DASH", "MDY_SLASH"]);
var PrintSettingsSchema = z19.object({
  printOnlineOrders: z19.boolean(),
  printReservations: z19.boolean(),
  // Kitchen-ticket splitting: split a fired round across station printers by
  // page; `printExpoTicket` adds a full "all items" copy on the default printer
  // (only in effect while splitting is on).
  splitKitchenTickets: z19.boolean(),
  printExpoTicket: z19.boolean(),
  textSize: PrintTextSizeSchema,
  printItemCategory: z19.boolean(),
  itemLabelMode: PrintItemLabelModeSchema,
  dateTimeFormat: PrintDateTimeFormatSchema
});
var UpdatePrintSettingsInputSchema = z19.object({
  printOnlineOrders: z19.boolean().optional(),
  printReservations: z19.boolean().optional(),
  splitKitchenTickets: z19.boolean().optional(),
  printExpoTicket: z19.boolean().optional(),
  textSize: PrintTextSizeSchema.optional(),
  printItemCategory: z19.boolean().optional(),
  itemLabelMode: PrintItemLabelModeSchema.optional(),
  dateTimeFormat: PrintDateTimeFormatSchema.optional()
}).strict().refine((v) => Object.values(v).some((x) => x !== void 0), {
  error: "At least one setting must be provided."
});
var ListJobsQuerySchema = z19.object({
  printerId: CuidSchema.optional(),
  status: PrintJobStatusSchema.optional(),
  limit: z19.coerce.number().int().min(1).max(200).default(50)
});
var PrintRestaurantParamsSchema = z19.object({ restaurantId: CuidSchema });
var PrinterParamsSchema = PrintRestaurantParamsSchema.extend({ printerId: CuidSchema });
var PrintJobParamsSchema = PrintRestaurantParamsSchema.extend({ jobId: CuidSchema });
var PrinterStatusEventsQuerySchema = z19.object({
  limit: z19.coerce.number().int().min(1).max(200).default(100)
});
var PrintPreviewKindSchema = z19.enum(["ONLINE_ORDER", "RECEIPT"]);
var PrintPreviewQuerySchema = z19.object({ kind: PrintPreviewKindSchema });
var PrintPreviewSchema = z19.object({
  kind: PrintPreviewKindSchema,
  // Sanitised Epson ePOS-Print `<epos-print>` body, same as a real job payload.
  eposXml: z19.string(),
  // Columns per line at the effective text size (48 MEDIUM / 32 LARGE on 80mm).
  columns: z19.number().int()
});

// src/schemas/analytics.ts
import { z as z20 } from "zod";
var DayPartSchema = z20.enum(["BREAKFAST", "LUNCH", "AFTERNOON", "DINNER", "LATE"]);
var TopItemsRankBySchema = z20.enum(["QUANTITY", "REVENUE"]);
var ComparisonBasisSchema = z20.enum(["PREVIOUS_PERIOD", "SAME_PERIOD_LAST_YEAR"]);
var SalesTotalsSchema = z20.object({
  grossSalesCents: z20.number().int(),
  discountsCents: z20.number().int(),
  serviceChargeCents: z20.number().int(),
  deliveryFeesCents: z20.number().int(),
  refundsCents: z20.number().int(),
  netSalesCents: z20.number().int(),
  tipsCents: z20.number().int(),
  vatCents: z20.number().int(),
  orderCount: z20.number().int(),
  sessionCount: z20.number().int()
});
var DailyPointSchema = z20.object({
  date: DateSchema,
  netSalesCents: z20.number().int(),
  grossSalesCents: z20.number().int(),
  orderCount: z20.number().int(),
  // Per-day series behind each KPI tile's trend line. Tips are dated to the
  // payment, everything else to the order/session.
  sessionCount: z20.number().int(),
  tipsCents: z20.number().int(),
  discountsCents: z20.number().int(),
  refundsCents: z20.number().int()
});
var DailySalesSummarySchema = z20.object({
  from: DateSchema,
  to: DateSchema,
  currency: z20.string(),
  combined: SalesTotalsSchema,
  online: SalesTotalsSchema,
  dineIn: SalesTotalsSchema,
  // Dine-in covers and off-premise order count are reported separately and
  // never summed into one "covers" number (design §4.4).
  dineInCovers: z20.number().int(),
  daily: z20.array(DailyPointSchema)
});
var PaymentMethodRowSchema = z20.object({
  methodKind: PaymentMethodKindSchema,
  // Present only on ONLINE rows, which are split one per gateway ("PayPal",
  // "Menucat") — each settles to its own account, so a lumped online figure
  // reconciles against neither payout statement.
  providerName: z20.string().optional(),
  grossCents: z20.number().int(),
  refundsCents: z20.number().int(),
  netCents: z20.number().int(),
  tipsCents: z20.number().int(),
  count: z20.number().int()
});
var PaymentBreakdownSchema = z20.object({
  from: DateSchema,
  to: DateSchema,
  currency: z20.string(),
  byMethod: z20.array(PaymentMethodRowSchema)
});
var DayEndReportSchema = z20.object({
  date: DateSchema,
  currency: z20.string(),
  takingsCents: z20.number().int(),
  refundsCents: z20.number().int(),
  netReceivedCents: z20.number().int(),
  tipsCents: z20.number().int(),
  serviceChargeCents: z20.number().int(),
  grossSalesCents: z20.number().int(),
  discountsCents: z20.number().int(),
  netSalesCents: z20.number().int(),
  deliveryFeesCents: z20.number().int(),
  vatCents: z20.number().int(),
  dineInCovers: z20.number().int(),
  orderCount: z20.number().int(),
  sessionCount: z20.number().int(),
  byMethod: z20.array(PaymentMethodRowSchema),
  expectedCashCents: z20.number().int(),
  openSessionsAtClose: z20.number().int()
});
var TableTurnSchema = z20.object({
  tableLabel: z20.string(),
  turns: z20.number().int()
});
var CoversTurnoverSchema = z20.object({
  from: DateSchema,
  to: DateSchema,
  currency: z20.string(),
  dineInCovers: z20.number().int(),
  onlineOrders: z20.number().int(),
  sessionCount: z20.number().int(),
  distinctTables: z20.number().int(),
  sessionsPerTable: z20.number(),
  // ratio
  avgTabCents: z20.number().int(),
  avgDwellMinutes: z20.number(),
  turnsPerTable: z20.array(TableTurnSchema)
});
var TopItemRowSchema = z20.object({
  productId: CuidSchema,
  name: z20.string(),
  quantity: z20.number().int(),
  revenueCents: z20.number().int()
});
var TopItemsResponseSchema = z20.object({
  from: DateSchema,
  to: DateSchema,
  currency: z20.string(),
  rankBy: TopItemsRankBySchema,
  items: z20.array(TopItemRowSchema)
});
var PeakHourSlotSchema = z20.object({
  hour: z20.number().int().min(0).max(23),
  orderCount: z20.number().int(),
  netSalesCents: z20.number().int()
});
var DayPartSlotSchema = z20.object({
  dayPart: DayPartSchema,
  orderCount: z20.number().int(),
  netSalesCents: z20.number().int()
});
var PeakHoursResponseSchema = z20.object({
  from: DateSchema,
  to: DateSchema,
  currency: z20.string(),
  hours: z20.array(PeakHourSlotSchema),
  dayParts: z20.array(DayPartSlotSchema)
});
var PeriodComparisonSchema = z20.object({
  currency: z20.string(),
  // Echoed back so the dashboard labels the deltas with the basis it actually
  // got, not the one it thinks it asked for.
  basis: ComparisonBasisSchema,
  currentRange: z20.object({ from: DateSchema, to: DateSchema }),
  previousRange: z20.object({ from: DateSchema, to: DateSchema }),
  current: SalesTotalsSchema,
  previous: SalesTotalsSchema
});
var MAX_RANGE_DAYS = 366;
var inRange = (data) => {
  const days = (Date.parse(data.to) - Date.parse(data.from)) / 864e5;
  return days >= 0 && days <= MAX_RANGE_DAYS;
};
var rangeMsg = {
  error: `Date range must span between 0 and ${MAX_RANGE_DAYS} days (from \u2264 to)`
};
var RangeBoundsSchema = z20.object({ from: DateSchema, to: DateSchema });
var AnalyticsParamsSchema = z20.object({ restaurantId: CuidSchema });
var AnalyticsRangeQuerySchema = RangeBoundsSchema.refine(inRange, rangeMsg);
var AnalyticsDayQuerySchema = z20.object({ date: DateSchema });
var TopItemsQuerySchema = RangeBoundsSchema.extend({
  rankBy: TopItemsRankBySchema.optional(),
  limit: z20.coerce.number().int().min(1).max(100).optional()
}).refine(inRange, rangeMsg);
var PeriodComparisonQuerySchema = RangeBoundsSchema.extend({
  compareTo: ComparisonBasisSchema.optional()
}).refine(inRange, rangeMsg);

// src/schemas/notification.ts
import { z as z21 } from "zod";
var NotificationSchema = z21.object({
  id: CuidSchema,
  type: z21.string(),
  title: z21.string(),
  body: z21.string(),
  data: z21.record(z21.string(), z21.unknown()),
  readAt: DateTimeSchema.nullable(),
  createdAt: DateTimeSchema
});
var NotificationListResponseSchema = z21.object({
  items: z21.array(NotificationSchema),
  nextSkip: z21.number().int().nullable()
});
var UnreadCountResponseSchema = z21.object({
  count: z21.number().int().nonnegative()
});
var InboxListQuerySchema = z21.object({
  unreadOnly: z21.coerce.boolean().optional(),
  take: z21.coerce.number().int().min(1).max(100).optional(),
  skip: z21.coerce.number().int().min(0).optional()
});
var NotificationParamsSchema = z21.object({ id: CuidSchema });
var MarkAllReadResponseSchema = z21.object({
  count: z21.number().int()
});
var SendTestPushBodySchema = z21.object({
  target: z21.string().min(1),
  title: z21.string().min(1),
  body: z21.string().min(1)
});
var SendTestPushResultSchema = z21.object({
  userId: z21.string(),
  email: z21.string().nullable(),
  deviceCount: z21.number().int().nonnegative(),
  invalidTokens: z21.number().int().nonnegative(),
  sent: z21.boolean()
});
var SendSelfTestPushBodySchema = z21.object({
  delaySeconds: z21.number().int().min(1).max(60),
  title: z21.string().min(1).max(100).optional(),
  body: z21.string().min(1).max(200).optional()
});
var SendSelfTestPushResultSchema = z21.object({
  scheduled: z21.boolean(),
  deviceCount: z21.number().int().nonnegative(),
  delaySeconds: z21.number().int().nonnegative()
});
var DeviceTokenListQuerySchema = z21.object({
  target: z21.string().optional()
});
var DeviceTokenRowSchema = z21.object({
  id: z21.string(),
  userId: z21.string(),
  userEmail: z21.string().nullable(),
  userName: z21.string().nullable(),
  platform: z21.string(),
  isActive: z21.boolean(),
  tokenPreview: z21.string(),
  createdAt: z21.string(),
  updatedAt: z21.string()
});
var DeviceTokenListSchema = z21.object({
  items: z21.array(DeviceTokenRowSchema),
  total: z21.number().int().nonnegative()
});

// src/schemas/ai-assistant.ts
import { z as z23 } from "zod";

// src/schemas/promotion.ts
import { z as z22 } from "zod";
var DiscountTypeSchema = z22.enum(["PERCENTAGE", "FIXED_AMOUNT", "FREE_DELIVERY"]);
var PromotionTargetTypeSchema = z22.enum(["ENTIRE_ORDER", "SELECTED_ITEMS"]);
var PromotionTriggerSchema = z22.enum(["AUTOMATIC", "CODE_REQUIRED"]);
var CouponKindSchema = z22.enum(["PUBLIC", "PRIVATE"]);
var PromotionOrderTypeSchema = z22.enum(["DINE_IN", "PICKUP", "DELIVERY"]);
var PromotionPaymentMethodKindSchema = z22.enum([
  "CASH",
  "CARD_ON_HANDOFF",
  "ONLINE",
  "MANUAL"
]);
var ScheduleWindowSchema = z22.object({
  dayOfWeek: z22.number().int().min(0).max(6),
  start: z22.string().regex(/^\d{2}:\d{2}$/),
  end: z22.string().regex(/^\d{2}:\d{2}$/)
});
var PromotionTargetSchema = z22.object({
  id: CuidSchema,
  categoryId: CuidSchema.nullable(),
  productId: CuidSchema.nullable()
});
var PromoCodeSchema = z22.object({
  code: z22.string(),
  maxRedemptions: z22.number().int().nullable(),
  oncePerCustomer: z22.boolean(),
  redeemedCount: z22.number().int()
});
var PromotionSchema = z22.object({
  id: CuidSchema,
  name: LocalizedTextSchema,
  description: LocalizedTextSchema.nullable(),
  discountType: DiscountTypeSchema,
  discountValue: z22.number().int(),
  maxDiscountCents: z22.number().int().nullable(),
  targetType: PromotionTargetTypeSchema,
  targets: z22.array(PromotionTargetSchema),
  minSubtotalCents: z22.number().int(),
  orderTypes: z22.array(PromotionOrderTypeSchema),
  paymentMethodKinds: z22.array(PromotionPaymentMethodKindSchema),
  startsAt: DateTimeSchema.nullable(),
  endsAt: DateTimeSchema.nullable(),
  weeklySchedule: z22.array(ScheduleWindowSchema).nullable(),
  stackable: z22.boolean(),
  priority: z22.number().int(),
  trigger: PromotionTriggerSchema,
  couponKind: CouponKindSchema.nullable(),
  customerVisible: z22.boolean(),
  isActive: z22.boolean(),
  code: PromoCodeSchema.nullable()
});
var CustomerPromotionSchema = z22.object({
  id: CuidSchema,
  name: z22.string(),
  description: z22.string().nullable(),
  discountType: DiscountTypeSchema,
  discountValue: z22.number().int(),
  maxDiscountCents: z22.number().int().nullable(),
  targetType: PromotionTargetTypeSchema,
  targetCategoryIds: z22.array(CuidSchema),
  targetProductIds: z22.array(CuidSchema),
  minSubtotalCents: z22.number().int(),
  // Order types this promo applies to — lets the storefront mark a promo
  // selectable only when it matches the cart's fulfillment (frontend gate).
  orderTypes: z22.array(PromotionOrderTypeSchema),
  paymentMethodKinds: z22.array(PromotionPaymentMethodKindSchema),
  payableWithSelectedMethod: z22.boolean(),
  // Whether this discount may be combined with other stackable ones. The
  // checkout lets a guest pick several offers only when all are stackable.
  stackable: z22.boolean(),
  // Validity window the storefront surfaces on each offer card. startsAt/endsAt
  // are UTC instants; weeklySchedule is the restaurant-local days & hours the
  // promo applies (null = no weekly restriction within the date window).
  startsAt: DateTimeSchema.nullable(),
  endsAt: DateTimeSchema.nullable(),
  weeklySchedule: z22.array(ScheduleWindowSchema).nullable(),
  // Whether the promo can be APPLIED right now — inside its weekly/time window
  // AND its date window. Display is bounded only by the date window, so an
  // out-of-weekly-window promo is still returned (for display) with this false;
  // the storefront shows it but must not let a guest apply it.
  applicableNow: z22.boolean()
});
var PersonalCouponSchema = z22.object({
  id: CuidSchema,
  code: z22.string(),
  customerId: CuidSchema,
  isSharable: z22.boolean(),
  isRedeemed: z22.boolean()
});
var PersonalCouponListItemSchema = z22.object({
  id: CuidSchema,
  code: z22.string(),
  isSharable: z22.boolean(),
  isRedeemed: z22.boolean(),
  redeemedAt: DateTimeSchema.nullable(),
  // How many times it was actually applied to an order (0 or 1 for a single-use
  // coupon; kept as a count so a future multi-use coupon needs no shape change).
  redemptionCount: z22.number().int(),
  issuedAt: DateTimeSchema,
  customerId: CuidSchema,
  customerName: z22.string(),
  promotionId: CuidSchema,
  promotionName: z22.string()
});
var ValidateCodeResultSchema = z22.discriminatedUnion("valid", [
  z22.object({ valid: z22.literal(true), promotionId: CuidSchema }),
  z22.object({ valid: z22.literal(false), reason: z22.string() })
]);
var PromotionTargetInputSchema = z22.object({ categoryId: CuidSchema.optional(), productId: CuidSchema.optional() }).refine((t) => t.categoryId ? !t.productId : !!t.productId, {
  error: "A target must reference exactly one of categoryId or productId"
});
var PromoCodeInputSchema = z22.object({
  code: z22.string().min(1).max(40),
  maxRedemptions: z22.number().int().positive().nullable().optional(),
  oncePerCustomer: z22.boolean().optional()
});
var PromotionInputShape = z22.object({
  name: LocalizedTextSchema,
  description: LocalizedTextSchema.nullable().optional(),
  discountType: DiscountTypeSchema,
  discountValue: z22.number().int().min(0),
  maxDiscountCents: z22.number().int().positive().nullable().optional(),
  targetType: PromotionTargetTypeSchema,
  targets: z22.array(PromotionTargetInputSchema).optional(),
  minSubtotalCents: z22.number().int().min(0).optional(),
  orderTypes: z22.array(PromotionOrderTypeSchema).min(1),
  paymentMethodKinds: z22.array(PromotionPaymentMethodKindSchema).optional(),
  startsAt: DateTimeSchema.nullable().optional(),
  endsAt: DateTimeSchema.nullable().optional(),
  weeklySchedule: z22.array(ScheduleWindowSchema).nullable().optional(),
  stackable: z22.boolean().optional(),
  priority: z22.number().int().optional(),
  trigger: PromotionTriggerSchema,
  couponKind: CouponKindSchema.nullable().optional(),
  customerVisible: z22.boolean().optional(),
  code: PromoCodeInputSchema.nullable().optional()
});
var targetsMatchType = (p) => p.targetType === void 0 || p.targets === void 0 || p.targetType === "ENTIRE_ORDER" === (p.targets.length === 0);
var targetsMessage = {
  error: "SELECTED_ITEMS needs at least one target; ENTIRE_ORDER takes none"
};
var percentageWithinBounds = (p) => p.discountType !== "PERCENTAGE" || p.discountValue === void 0 || p.discountValue <= 1e4;
var percentageMessage = { error: "PERCENTAGE discount cannot exceed 100% (10000 bps)" };
var CreatePromotionInputSchema = PromotionInputShape.refine(
  (p) => p.trigger !== "CODE_REQUIRED" || !!p.couponKind,
  { error: "CODE_REQUIRED promotions need a couponKind (PUBLIC or PRIVATE)" }
).refine((p) => p.couponKind !== "PUBLIC" || !!p.code, {
  error: "A PUBLIC coupon needs its shared code"
}).refine(percentageWithinBounds, percentageMessage).refine(
  (p) => targetsMatchType({ targetType: p.targetType, targets: p.targets ?? [] }),
  targetsMessage
);
var UpdatePromotionInputSchema = PromotionInputShape.partial().refine(percentageWithinBounds, percentageMessage).refine(targetsMatchType, targetsMessage);
var PROMOTION_CHANGESET_MAX = 20;
var PromotionValueShape = z22.object({
  name: LocalizedTextSchema,
  description: LocalizedTextSchema.nullable(),
  discountType: DiscountTypeSchema,
  discountValue: z22.number().int().min(0),
  maxDiscountCents: z22.number().int().positive().nullable(),
  targetType: PromotionTargetTypeSchema,
  categoryIds: z22.array(CuidSchema),
  productIds: z22.array(CuidSchema),
  minSubtotalCents: z22.number().int().min(0),
  orderTypes: z22.array(PromotionOrderTypeSchema).min(1),
  paymentMethodKinds: z22.array(PromotionPaymentMethodKindSchema),
  startsAt: DateTimeSchema.nullable(),
  endsAt: DateTimeSchema.nullable(),
  weeklySchedule: z22.array(ScheduleWindowSchema).nullable(),
  stackable: z22.boolean(),
  priority: z22.number().int(),
  trigger: PromotionTriggerSchema,
  couponKind: CouponKindSchema.nullable(),
  customerVisible: z22.boolean(),
  code: PromoCodeInputSchema.required().nullable()
});
var followsFormRules = (p) => percentageWithinBounds(p) && p.targetType === "ENTIRE_ORDER" === (p.categoryIds.length + p.productIds.length === 0) && (p.trigger !== "CODE_REQUIRED" || p.couponKind !== null) && (p.couponKind !== "PUBLIC" || p.code !== null);
var formRulesMessage = {
  error: "A promotion must pass the Discounts form rules: at most 100%, selected items need at least one category or dish, a code promotion needs a coupon kind, a shared coupon needs its code"
};
var PromotionChangeSchema = z22.discriminatedUnion("kind", [
  PromotionValueShape.extend({
    kind: z22.literal("promotionCreated"),
    promotionId: CuidSchema
  }).refine(followsFormRules, formRulesMessage),
  z22.object({
    kind: z22.literal("promotionUpdated"),
    promotionId: CuidSchema,
    before: PromotionValueShape,
    after: PromotionValueShape.refine(followsFormRules, formRulesMessage)
  }),
  z22.object({
    kind: z22.literal("promotionArchived"),
    promotionId: CuidSchema,
    before: z22.boolean(),
    after: z22.boolean()
  })
]);
var PromotionChangesSchema = z22.array(PromotionChangeSchema).max(PROMOTION_CHANGESET_MAX);
var PromotionChangesetStaleSchema = z22.object({ stale: PromotionChangesSchema });
var PromotionConflictSchema = z22.object({
  promotionId: CuidSchema,
  otherPromotionId: CuidSchema,
  effect: z22.enum(["replaces", "stacks", "loses"]),
  orderType: PromotionOrderTypeSchema,
  subtotalCents: z22.number().int(),
  at: DateTimeSchema
});
var IssuePersonalCouponInputSchema = z22.object({
  customerId: CuidSchema,
  code: z22.string().min(1).max(40),
  isSharable: z22.boolean().optional()
});
var ValidateCodeInputSchema = z22.object({
  code: z22.string().min(1).max(40),
  customerId: CuidSchema.optional()
});
var PreviewDiscountLineSchema = z22.object({
  productId: CuidSchema,
  quantity: z22.number().int().min(1),
  lineCents: z22.number().int().min(0)
});
var PreviewDiscountInputSchema = z22.object({
  orderType: PromotionOrderTypeSchema,
  lines: z22.array(PreviewDiscountLineSchema).min(1),
  deliveryFeeCents: z22.number().int().min(0).default(0),
  code: z22.string().max(40).optional(),
  customerId: CuidSchema.optional(),
  /**
   * Customer-chosen non-code promotions. Tri-state: omit = legacy auto-apply of
   * all eligible automatic promos; [] = none; a list = apply exactly those (when
   * eligible, and — for >1 — all stackable). The checkout always sends it.
   */
  selectedPromotionIds: z22.array(CuidSchema).nullable().optional(),
  /**
   * The slot the guest picked for a "for later" order (ISO instant). Time-windowed
   * promotions are judged on WHEN THE FOOD IS HANDED OVER, not when the guest
   * clicks — otherwise a lunch offer bought at 11:30 for a 22:00 pickup still
   * discounts. Omit for ASAP: the server falls back to now, which for ASAP is
   * the handover time.
   */
  scheduledFor: DateTimeSchema.optional(),
  paymentMethodKind: PromotionPaymentMethodKindSchema.optional(),
  autoSelectBest: z22.boolean().optional()
});
var PreviewDiscountResultSchema = z22.object({
  subtotalCents: z22.number().int(),
  discountCents: z22.number().int(),
  freeDelivery: z22.boolean(),
  /** Set when a code was entered but is invalid / unavailable (advisory). */
  codeError: z22.string().nullable(),
  appliedPromotionIds: z22.array(CuidSchema)
});
var PromotionRestaurantParamsSchema = z22.object({ restaurantId: CuidSchema });
var PromotionParamsSchema = PromotionRestaurantParamsSchema.extend({
  promotionId: CuidSchema
});
var PromotionCustomerParamsSchema = z22.object({ customerId: CuidSchema });
var PromotionListQuerySchema = z22.object({ status: z22.enum(["active", "all"]).optional() });
var PromotionLangQuerySchema = z22.object({ lang: MenuLanguageSchema.default("en") });
var ActivePromotionsQuerySchema = z22.object({
  lang: MenuLanguageSchema.default("en"),
  scheduledFor: DateTimeSchema.optional(),
  paymentMethodKind: PromotionPaymentMethodKindSchema.optional()
});

// src/schemas/ai-assistant.ts
var ThreadStatusSchema = z23.enum(["active", "archived", "deleted"]);
var AiThreadIdSchema = z23.string().regex(/^[A-Za-z0-9_-]{8,64}$/, "Invalid thread id");
var AiConversationThreadSchema = z23.object({
  id: z23.string(),
  franchiseId: CuidSchema,
  restaurantId: CuidSchema,
  userId: CuidSchema,
  title: z23.string(),
  status: ThreadStatusSchema,
  lastMessageAt: DateTimeSchema,
  lastMessagePreview: z23.string().nullable(),
  messageCount: z23.number().int().nonnegative(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema
});
var AiThreadConnectionSchema = z23.object({
  threads: z23.array(AiConversationThreadSchema),
  total: z23.number().int().nonnegative(),
  hasMore: z23.boolean()
});
var AiStoredMessageSchema = z23.object({
  id: z23.string(),
  role: z23.enum(["system", "user", "assistant"]),
  parts: z23.array(z23.record(z23.string(), z23.unknown()))
});
var AiRestaurantParamsSchema = z23.object({ restaurantId: CuidSchema });
var AiThreadParamsSchema = AiRestaurantParamsSchema.extend({ threadId: AiThreadIdSchema });
var AiAttachmentParamsSchema = AiRestaurantParamsSchema.extend({
  attachmentId: CuidSchema
});
var AiMenuParamsSchema = AiRestaurantParamsSchema.extend({ menuId: CuidSchema });
var AiProductParamsSchema = AiMenuParamsSchema.extend({ productId: CuidSchema });
var AiMenuTurnParamsSchema = AiMenuParamsSchema.extend({ turnId: AiThreadIdSchema });
var AiProductTurnParamsSchema = AiProductParamsSchema.extend({ turnId: AiThreadIdSchema });
var AiPromotionsTurnParamsSchema = AiRestaurantParamsSchema.extend({
  turnId: AiThreadIdSchema
});
var AiExceptionsTurnParamsSchema = AiRestaurantParamsSchema.extend({
  turnId: AiThreadIdSchema
});
var AiThreadListQuerySchema = z23.object({
  status: ThreadStatusSchema.optional(),
  search: z23.string().optional(),
  limit: z23.coerce.number().int().min(1).max(100).optional(),
  offset: z23.coerce.number().int().min(0).optional()
});
var UpdateAiThreadSchema = z23.object({
  title: z23.string().min(1).max(200).optional(),
  status: ThreadStatusSchema.optional()
});
var AiSuccessSchema = z23.object({ success: z23.boolean() });
var AiAssetUrlPrefix = "asset:";
var AiLocaleSchema = z23.enum(["en", "de", "vi", "pl"]);
var AiApprovalResponseSchema = z23.object({
  approvalId: z23.string().min(1).max(200),
  approved: z23.boolean(),
  reason: z23.string().max(500).optional()
}).strict();
var AiChatUserMessageSchema = z23.object({
  type: z23.literal("user"),
  text: z23.string().max(8e3),
  assetIds: z23.array(CuidSchema).max(5).default([])
}).strict();
var AiChatApprovalsMessageSchema = z23.object({
  type: z23.literal("approvals"),
  responses: z23.array(AiApprovalResponseSchema).min(1).max(50)
}).strict();
var AiChatRequestSchema = z23.object({
  threadId: AiThreadIdSchema,
  locale: AiLocaleSchema,
  viewContext: z23.string().max(4e3).optional(),
  message: z23.discriminatedUnion("type", [AiChatUserMessageSchema, AiChatApprovalsMessageSchema])
}).strict();
var AI_CHANGESET_VIEW_SEARCH_MAX = 80;
var AiChangesetViewSchema = z23.object({
  sectionId: z23.string().max(64).nullable(),
  search: z23.string().trim().max(200).transform((value) => value.slice(0, AI_CHANGESET_VIEW_SEARCH_MAX)).nullable(),
  mode: z23.enum(["changes", "full"]).nullable(),
  rowIds: z23.array(z23.string().max(64)).max(8)
}).strict();
var AI_CHANGESET_TURN_ATTACHMENTS_MAX = 10;
var AiChangesetTurnSchema = z23.object({
  locale: AiLocaleSchema,
  text: z23.string().trim().max(1e3),
  view: AiChangesetViewSchema.optional(),
  attachmentIds: z23.array(CuidSchema).max(AI_CHANGESET_TURN_ATTACHMENTS_MAX).default([])
}).strict().refine((turn) => turn.text.length > 0 || turn.attachmentIds.length > 0, {
  error: "The message is empty.",
  path: ["text"]
});
var AiTurnOutcomeSchema = z23.enum([
  "pending",
  "applying",
  "applied",
  "discarded",
  "created",
  "undone"
]);
var AiTurnAttachmentSchema = z23.object({
  id: CuidSchema,
  kind: z23.enum(["image", "pdf", "text"]),
  fileName: z23.string(),
  url: z23.string()
});
var AiTurnAuthorSchema = z23.object({
  userId: CuidSchema,
  name: z23.string().nullable()
});
var AiMenuImportProgressSchema = z23.object({
  status: z23.enum(["reading", "ready", "failed"]),
  pagesDone: z23.number().int().nonnegative(),
  pagesTotal: z23.number().int().nonnegative(),
  dishesSoFar: z23.number().int().nonnegative(),
  failureReason: z23.string().nullable(),
  sources: z23.array(
    z23.object({
      page: z23.number().int().positive(),
      attachmentId: CuidSchema,
      kind: z23.enum(["image", "pdf", "text"]),
      label: z23.string(),
      sections: z23.array(z23.string()),
      failed: z23.boolean()
    })
  ),
  legend: z23.array(
    z23.object({
      shortKey: z23.string(),
      description: z23.string(),
      kind: z23.enum(["ALLERGEN", "ADDITIVE", "DIETARY"]),
      inTable: z23.boolean()
    })
  )
});
var AI_CHANGESET_HISTORY_PAGE_MAX = 50;
var AiChangesetHistoryQuerySchema = z23.object({
  limit: z23.coerce.number().int().min(1).max(AI_CHANGESET_HISTORY_PAGE_MAX).optional(),
  before: z23.string().min(1).max(64).optional()
});
var aiChangesetSessionSchema = (changes) => z23.object({
  session: z23.object({
    id: z23.string(),
    turns: z23.array(
      z23.object({
        id: z23.string(),
        author: AiTurnAuthorSchema.nullable(),
        createdAt: DateTimeSchema,
        text: z23.string(),
        attachments: z23.array(AiTurnAttachmentSchema),
        reply: z23.string().nullable(),
        changes,
        outcome: AiTurnOutcomeSchema.nullable(),
        canUndo: z23.boolean(),
        verification: z23.object({
          appliedCount: z23.number().int().nonnegative(),
          missingCount: z23.number().int().nonnegative()
        }).nullable()
      })
    ),
    hasMore: z23.boolean(),
    draft: z23.object({ turnId: z23.string(), changes }).nullable(),
    busy: z23.boolean(),
    menuImport: AiMenuImportProgressSchema.nullable()
  }).nullable()
});
var MenuQuickEditSessionSchema = aiChangesetSessionSchema(MenuChangesSchema);
var ExceptionsQuickEditSessionSchema = aiChangesetSessionSchema(ExceptionChangesSchema);
var PromotionsQuickEditSessionSchema = aiChangesetSessionSchema(
  PromotionChangesSchema
).extend({ conflicts: z23.array(PromotionConflictSchema) });
var aiChangesetAppliedSchema = (changes) => z23.object({
  applied: z23.literal(true),
  appliedCount: z23.number().int().nonnegative(),
  missing: changes
});
var AiChangesetAppliedSchema = aiChangesetAppliedSchema(MenuChangesSchema);
var ExceptionsChangesetAppliedSchema = aiChangesetAppliedSchema(ExceptionChangesSchema);
var PromotionsChangesetAppliedSchema = aiChangesetAppliedSchema(PromotionChangesSchema);
var AI_ATTACHMENT_MAX_BYTES = {
  "image/png": 10 * 1024 * 1024,
  "image/jpeg": 10 * 1024 * 1024,
  "image/webp": 10 * 1024 * 1024,
  "application/pdf": 20 * 1024 * 1024
};
var isAiAttachmentMimeType = (value) => value in AI_ATTACHMENT_MAX_BYTES;
var AiAttachmentPresignSchema = z23.object({
  fileName: z23.string().trim().min(1).max(255),
  mimeType: z23.string().refine(isAiAttachmentMimeType, {
    error: "Unsupported file type. Upload a PNG, JPEG or WebP image, or a PDF."
  }),
  size: z23.number().int().positive()
}).strict().refine(
  (body) => !isAiAttachmentMimeType(body.mimeType) || body.size <= AI_ATTACHMENT_MAX_BYTES[body.mimeType],
  { error: "File too large (max 10 MB for images, 20 MB for PDFs).", path: ["size"] }
);
var AiAttachmentUploadTicketSchema = z23.object({
  attachmentId: CuidSchema,
  uploadUrl: z23.url(),
  headers: z23.record(z23.string(), z23.string())
});
var AiAttachmentUploadSchema = z23.object({
  id: CuidSchema,
  fileName: z23.string(),
  fileSize: z23.number().int().nonnegative(),
  mimeType: z23.string()
});
var AI_VOICE_MAX_SECONDS = 60;
var AI_VOICE_MAX_UPLOAD_BYTES = 3 * 1024 * 1024;
var AI_VOICE_SAMPLE_RATE = 24e3;
var AI_VOICE_AUDIO_TYPES = {
  "audio/webm": "webm",
  "audio/mp4": "mp4",
  "audio/x-m4a": "m4a",
  "audio/m4a": "m4a",
  "audio/ogg": "ogg"
};
var isAiVoiceAudioType = (value) => Object.keys(AI_VOICE_AUDIO_TYPES).includes(value);
var AiVoiceSessionRequestSchema = z23.object({
  locale: AiLocaleSchema.optional()
}).strict();
var AiVoiceSessionSchema = z23.object({
  clientSecret: z23.string().min(1),
  expiresAt: DateTimeSchema,
  realtimeUrl: z23.url(),
  sampleRate: z23.literal(AI_VOICE_SAMPLE_RATE),
  maxSeconds: z23.number().int().positive()
});
var AiVoiceTranscribeFieldsSchema = z23.object({
  durationMs: z23.coerce.number().int().positive().max((AI_VOICE_MAX_SECONDS + 5) * 1e3),
  locale: AiLocaleSchema.optional()
}).strict();
var AiVoiceTranscriptionSchema = z23.object({
  text: z23.string()
});

// src/schemas/ai-tools.ts
import { z as z24 } from "zod";
var MinorUnits = z24.number().int().nonnegative().describe("Amount in integer minor currency units (e.g. cents)");
var LocalDate = z24.string().regex(/^\d{4}-\d{2}-\d{2}$/).describe("Local date YYYY-MM-DD");
var LocalTime = z24.string().regex(/^\d{1,2}:\d{2}$/).describe("Local time HH:mm (24h)");
var Id = z24.string().min(1);
var Label = z24.string().min(1).max(200);
var OpeningHoursService = z24.enum(["BASE", "PICKUP", "DELIVERY", "RESERVATION"]);
var AiSetPricesInputSchema = z24.object({
  items: z24.array(
    z24.object({
      productId: Id,
      productName: Label.describe("Product name exactly as returned by get_menu"),
      price: MinorUnits.describe("New price in integer minor currency units")
    })
  ).min(1).max(100)
});
var AiSetItemAvailabilityInputSchema = z24.object({
  items: z24.array(
    z24.object({
      productId: Id,
      productName: Label.describe("Product name exactly as returned by get_menu"),
      inStock: z24.boolean().describe("true = available, false = sold out (86)")
    })
  ).min(1).max(100)
});
var AiManageProductsInputSchema = z24.object({
  items: z24.array(
    z24.discriminatedUnion("action", [
      z24.object({
        action: z24.literal("create"),
        categoryId: Id,
        categoryName: Label,
        name: Label,
        price: MinorUnits,
        description: z24.string().max(1e3).optional(),
        code: ProductCodeSchema.optional().describe("Short item code / SKU")
      }),
      z24.object({
        action: z24.literal("update"),
        productId: Id,
        currentName: Label,
        name: Label.optional(),
        description: z24.string().max(1e3).optional(),
        isActive: z24.boolean().optional(),
        code: ProductCodeSchema.nullable().optional().describe("Item code / SKU; null to clear")
      }),
      z24.object({
        action: z24.literal("delete"),
        productId: Id,
        currentName: Label
      })
    ])
  ).min(1).max(100)
});
var AiManageCategoriesInputSchema = z24.object({
  items: z24.array(
    z24.discriminatedUnion("action", [
      z24.object({
        action: z24.literal("create"),
        menuId: Id,
        name: Label,
        description: z24.string().max(1e3).optional()
      }),
      z24.object({
        action: z24.literal("update"),
        categoryId: Id,
        currentName: Label,
        name: Label.optional(),
        description: z24.string().max(1e3).optional(),
        isActive: z24.boolean().optional()
      }),
      z24.object({
        action: z24.literal("delete"),
        categoryId: Id,
        currentName: Label
      })
    ])
  ).min(1).max(50)
});
var AiCreateReservationInputSchema = z24.object({
  date: LocalDate,
  time: LocalTime,
  partySize: z24.number().int().min(1).max(500),
  guestName: Label,
  guestPhone: z24.string().max(40).optional(),
  guestEmail: z24.email().optional(),
  duration: z24.number().int().positive().max(1440).optional().describe("Minutes"),
  internalNote: z24.string().max(1e3).optional()
});
var AiCancelReservationInputSchema = z24.object({
  reservationId: Id,
  guestName: Label,
  reason: z24.string().max(500).optional()
});
var AiAssignTablesInputSchema = z24.object({
  reservationId: Id,
  guestName: Label,
  tableIds: z24.array(Id).min(1).max(20),
  tableLabels: z24.array(Label).min(1).max(20)
});
var AiCreateCustomerInputSchema = z24.object({
  firstName: Label,
  lastName: Label,
  email: z24.email().optional(),
  notes: z24.string().max(1e3).optional()
});
var AiUpdateOpeningHoursInputSchema = z24.object({
  service: OpeningHoursService.optional().describe("Defaults to BASE (general hours)"),
  schedule: z24.array(
    z24.object({
      day: z24.string().min(1).describe('Weekday in English or German, e.g. "Monday", "Montag"'),
      closed: z24.boolean().optional(),
      windows: z24.array(z24.object({ open: LocalTime, close: LocalTime })).optional().describe("One or more time windows; two for a lunch + dinner split")
    })
  ).min(1).max(7)
});
var AiUpdateProfileInputSchema = z24.object({
  name: Label.optional(),
  address: z24.string().max(500).optional(),
  phone: z24.string().max(40).optional(),
  email: z24.email().optional(),
  description: z24.string().max(2e3).optional()
});
var AiToggleOnlineOrderingInputSchema = z24.object({
  orderType: z24.enum(["PICKUP", "DELIVERY"]),
  enabled: z24.boolean()
});
var AiToggleTableOrderingInputSchema = z24.object({
  enabled: z24.boolean()
});
var AiFireRoundInputSchema = z24.object({
  sessionId: Id,
  roundId: Id,
  tableLabel: Label
});
var AiVoidItemInputSchema = z24.object({
  sessionId: Id,
  itemId: Id,
  itemName: Label,
  reason: z24.string().max(500).optional()
});
var AiTakePaymentInputSchema = z24.object({
  sessionId: Id,
  tableLabel: Label,
  methodKind: z24.enum(["CASH", "MANUAL"]).describe("CASH or MANUAL bank transfer; never card"),
  amountCents: MinorUnits,
  tipCents: MinorUnits.optional()
});
var AiCloseTabInputSchema = z24.object({
  sessionId: Id,
  tableLabel: Label
});
var AiMoveSessionInputSchema = z24.object({
  sessionId: Id,
  toTableId: Id,
  fromTableLabel: Label,
  toTableLabel: Label
});
var AiReservationListOutputSchema = z24.object({
  date: z24.string(),
  totalReservations: z24.number().int(),
  totalGuests: z24.number().int(),
  reservations: z24.array(
    z24.object({
      id: z24.string(),
      time: z24.string(),
      partySize: z24.number().int(),
      guestName: z24.string(),
      status: z24.string(),
      tableCount: z24.number().int()
    })
  )
});
var AiRevenueOutputSchema = z24.object({
  from: z24.string(),
  to: z24.string(),
  currency: z24.string(),
  netSalesCents: z24.number().int(),
  grossSalesCents: z24.number().int(),
  tipsCents: z24.number().int(),
  orderCount: z24.number().int(),
  onlineNetSalesCents: z24.number().int(),
  dineInNetSalesCents: z24.number().int(),
  dineInCovers: z24.number().int()
});
var AiMenuItemsOutputSchema = z24.object({
  items: z24.array(
    z24.object({
      productId: z24.string(),
      name: z24.string(),
      code: z24.string().nullable(),
      category: z24.string(),
      price: z24.number().int(),
      inStock: z24.boolean(),
      isActive: z24.boolean()
    })
  )
});
var AI_WRITE_TOOL_INPUT_SCHEMAS = {
  set_prices: AiSetPricesInputSchema,
  set_item_availability: AiSetItemAvailabilityInputSchema,
  manage_products: AiManageProductsInputSchema,
  manage_categories: AiManageCategoriesInputSchema,
  create_reservation: AiCreateReservationInputSchema,
  cancel_reservation: AiCancelReservationInputSchema,
  assign_tables: AiAssignTablesInputSchema,
  create_customer: AiCreateCustomerInputSchema,
  update_opening_hours: AiUpdateOpeningHoursInputSchema,
  update_profile: AiUpdateProfileInputSchema,
  toggle_online_ordering: AiToggleOnlineOrderingInputSchema,
  toggle_table_ordering: AiToggleTableOrderingInputSchema,
  fire_round: AiFireRoundInputSchema,
  void_item: AiVoidItemInputSchema,
  take_payment: AiTakePaymentInputSchema,
  close_tab: AiCloseTabInputSchema,
  move_session: AiMoveSessionInputSchema
};
var AI_WRITE_TOOL_NAMES = Object.keys(AI_WRITE_TOOL_INPUT_SCHEMAS).filter(
  (name) => name in AI_WRITE_TOOL_INPUT_SCHEMAS
);

// src/schemas/ai-menu-create.ts
import { z as z25 } from "zod";
var AI_MENU_SOURCES_MAX = 20;
var AI_MENU_SOURCE_TEXT_MAX = 5e4;
var AiMenuSourceSchema = z25.discriminatedUnion("kind", [
  z25.object({ kind: z25.literal("photo"), attachmentId: CuidSchema }).strict(),
  z25.object({ kind: z25.literal("pdf"), attachmentId: CuidSchema }).strict(),
  z25.object({ kind: z25.literal("url"), url: z25.url().max(2e3) }).strict(),
  z25.object({
    kind: z25.literal("text"),
    text: z25.string().trim().min(1).max(AI_MENU_SOURCE_TEXT_MAX)
  }).strict()
]);
var CreateMenuFromSourcesSchema = z25.object({
  name: z25.string().trim().min(1).max(200).optional(),
  hint: z25.string().trim().max(1e3).optional(),
  keepItemNumbers: z25.boolean(),
  sources: z25.array(AiMenuSourceSchema).min(1).max(AI_MENU_SOURCES_MAX)
}).strict();
var MenuFromSourcesAcceptedSchema = z25.object({ menuId: CuidSchema });
var MenuChecklistSchema = z25.object({
  unpriced: z25.array(z25.object({ productId: CuidSchema, categoryId: CuidSchema, name: z25.string() })),
  unknownAllergenCodes: z25.array(
    z25.object({
      productId: CuidSchema,
      categoryId: CuidSchema,
      name: z25.string(),
      codes: z25.array(z25.string())
    })
  )
});
var MenuImportFlagSchema = z25.discriminatedUnion("kind", [
  z25.object({ kind: z25.literal("noPrice") }),
  z25.object({
    kind: z25.literal("priceOutlier"),
    price: z25.number().int().nonnegative(),
    median: z25.number().int().nonnegative()
  }),
  z25.object({ kind: z25.literal("unknownAllergenCodes"), codes: z25.array(z25.string()) }),
  z25.object({ kind: z25.literal("itemNumberDropped"), code: z25.string() }),
  z25.object({
    kind: z25.literal("pickLimit"),
    options: z25.array(z25.object({ name: z25.string(), count: z25.number().int().positive() }))
  })
]);
var MenuImportReviewSchema = z25.object({
  available: z25.boolean(),
  pages: z25.array(
    z25.object({
      page: z25.number().int().positive(),
      kind: z25.enum(["image", "pdf", "text"]),
      label: z25.string(),
      url: z25.string().nullable(),
      text: z25.string().nullable(),
      sections: z25.array(z25.string())
    })
  ),
  dishes: z25.array(
    z25.object({
      productId: CuidSchema,
      categoryId: CuidSchema,
      name: z25.string(),
      price: z25.number().int().nonnegative(),
      page: z25.number().int().nonnegative(),
      flags: z25.array(MenuImportFlagSchema)
    })
  )
});

// src/schemas/customer-account.ts
import { z as z26 } from "zod";
var RequestOtpInputSchema = z26.object({
  franchiseId: CuidSchema,
  phone: PhoneSchema
});
var RequestOtpResponseSchema = z26.object({
  expiresAt: DateTimeSchema,
  resendAvailableInSeconds: z26.number().int(),
  /** Dev/staging only: the code, surfaced through the honest notification seam. */
  devCode: z26.string().nullable()
});
var VerifyOtpInputSchema = z26.object({
  franchiseId: CuidSchema,
  phone: PhoneSchema,
  code: z26.string().min(4).max(8)
});
var CustomerMeSchema = z26.object({
  id: CuidSchema,
  franchiseId: CuidSchema,
  firstName: z26.string(),
  lastName: z26.string(),
  email: z26.string().nullable(),
  phone: z26.string().nullable()
});
var CustomerAddressSchema = z26.object({
  id: CuidSchema,
  label: z26.string().nullable(),
  line1: z26.string(),
  line2: z26.string().nullable(),
  city: z26.string(),
  postalCode: z26.string().nullable(),
  country: z26.string(),
  latitude: z26.number(),
  longitude: z26.number(),
  instructions: z26.string().nullable(),
  isDefault: z26.boolean()
});
var CustomerAddressInputSchema = z26.object({
  label: z26.string().max(60).optional(),
  line1: requiredAddressPart(200),
  line2: addressPart(200).optional(),
  city: requiredAddressPart(100),
  postalCode: addressPart(20).optional(),
  country: z26.string().length(2),
  latitude: z26.number(),
  longitude: z26.number(),
  instructions: z26.string().max(500).optional(),
  isDefault: z26.boolean().optional()
});
var FavoriteTargetTypeSchema = z26.enum(["PRODUCT", "RESTAURANT"]);
var CustomerFavoriteSchema = z26.object({
  id: CuidSchema,
  targetType: FavoriteTargetTypeSchema,
  productId: CuidSchema.nullable(),
  restaurantId: CuidSchema.nullable(),
  /** Resolved display label (product or restaurant name) for the account list. */
  label: z26.string()
});
var AddFavoriteInputSchema = z26.object({
  targetType: FavoriteTargetTypeSchema,
  productId: CuidSchema.optional(),
  restaurantId: CuidSchema.optional()
}).refine((f) => f.targetType === "PRODUCT" ? !!f.productId : !!f.restaurantId, {
  error: "A PRODUCT favorite needs productId; a RESTAURANT favorite needs restaurantId"
});
var HistoryEntrySchema = z26.discriminatedUnion("channel", [
  z26.object({
    channel: z26.literal("ONLINE_ORDER"),
    id: CuidSchema,
    occurredAt: DateTimeSchema,
    confirmationCode: z26.string(),
    status: z26.string(),
    orderType: z26.enum(["PICKUP", "DELIVERY"]),
    totalCents: z26.number().int(),
    currency: z26.string(),
    itemCount: z26.number().int(),
    restaurantId: CuidSchema,
    restaurantSlug: z26.string(),
    franchiseSlug: z26.string()
  }),
  z26.object({
    channel: z26.literal("RESERVATION"),
    id: CuidSchema,
    occurredAt: DateTimeSchema,
    status: z26.string(),
    partySize: z26.number().int()
  }),
  z26.object({
    channel: z26.literal("DINE_IN"),
    id: CuidSchema,
    occurredAt: DateTimeSchema,
    status: z26.string(),
    totalCents: z26.number().int(),
    currency: z26.string()
  })
]);
var OrderHistoryResponseSchema = z26.object({
  entries: z26.array(HistoryEntrySchema),
  nextCursor: z26.string().nullable()
});
var ReorderLineSchema = z26.object({
  productId: CuidSchema,
  quantity: z26.number().int(),
  choices: z26.array(z26.object({ optionId: CuidSchema, optionChoiceId: CuidSchema }))
});
var ReorderItemReportSchema = z26.object({
  name: z26.string(),
  status: z26.enum(["OK", "PRICE_CHANGED", "UNAVAILABLE"]),
  oldPriceCents: z26.number().int().nullable(),
  newPriceCents: z26.number().int().nullable()
});
var ReorderResponseSchema = z26.object({
  /** Validated cart lines (only the still-available items) to seed checkout. */
  lines: z26.array(ReorderLineSchema),
  report: z26.array(ReorderItemReportSchema)
});
var CustomerAddressParamsSchema = z26.object({ addressId: CuidSchema });
var CustomerFavoriteParamsSchema = z26.object({ favoriteId: CuidSchema });
var ReorderParamsSchema = z26.object({ orderId: CuidSchema });
var OrderHistoryQuerySchema = z26.object({ cursor: z26.string().optional() });

// src/schemas/loyalty.ts
import { z as z27 } from "zod";
var LoyaltyEntryTypeSchema = z27.enum(["EARN", "REDEEM", "ADJUST"]);
var LoyaltyBalanceSchema = z27.object({
  enabled: z27.boolean(),
  balancePoints: z27.number().int(),
  /** Money one point is worth on redemption (so the UI can show "N points = $X"). */
  redeemValueCents: z27.number().int()
});
var LoyaltyLedgerEntrySchema = z27.object({
  id: CuidSchema,
  entryType: LoyaltyEntryTypeSchema,
  points: z27.number().int(),
  // signed: + earn/credit, − redeem/debit
  balanceAfter: z27.number().int(),
  reason: z27.string().nullable(),
  createdAt: DateTimeSchema
});
var PreviewRedemptionInputSchema = z27.object({
  restaurantId: CuidSchema,
  subtotalCents: z27.number().int().min(0),
  points: z27.number().int().min(0)
});
var RedemptionResultSchema = z27.object({
  /** Points actually applied (clamped to balance + what the subtotal can absorb). */
  points: z27.number().int(),
  discountCents: z27.number().int()
});
var LoyaltyConfigSchema = z27.object({
  enabled: z27.boolean(),
  earnRateBps: z27.number().int().min(0),
  redeemValueCents: z27.number().int().min(0)
});
var UpsertLoyaltyConfigInputSchema = LoyaltyConfigSchema;
var AdjustPointsInputSchema = z27.object({
  points: z27.number().int(),
  // signed manual adjustment
  reason: z27.string().min(1).max(200)
});
var CustomerLoyaltySchema = z27.object({
  balancePoints: z27.number().int(),
  entries: z27.array(LoyaltyLedgerEntrySchema)
});
var LoyaltyRestaurantParamsSchema = z27.object({ restaurantId: CuidSchema });
var LoyaltyCustomerParamsSchema = LoyaltyRestaurantParamsSchema.extend({
  customerId: CuidSchema
});

// src/schemas/gift-card.ts
import { z as z28 } from "zod";
var GiftCardStatusSchema = z28.enum(["ACTIVE", "DEPLETED", "VOID", "EXPIRED"]);
var GiftCardTransactionTypeSchema = z28.enum(["ISSUE", "REDEEM", "ADJUST", "VOID"]);
var GiftCardSchema = z28.object({
  id: CuidSchema,
  code: z28.string(),
  initialBalanceCents: z28.number().int(),
  balanceCents: z28.number().int(),
  currency: z28.string(),
  status: GiftCardStatusSchema,
  issuedAt: DateTimeSchema,
  expiresAt: DateTimeSchema.nullable(),
  recipientName: z28.string().nullable(),
  recipientPhone: z28.string().nullable()
});
var GiftCardTransactionSchema = z28.object({
  id: CuidSchema,
  type: GiftCardTransactionTypeSchema,
  amountCents: z28.number().int(),
  balanceAfter: z28.number().int(),
  reason: z28.string().nullable(),
  createdAt: DateTimeSchema
});
var GiftCardDetailSchema = GiftCardSchema.extend({
  transactions: z28.array(GiftCardTransactionSchema)
});
var IssueGiftCardInputSchema = z28.object({
  // Cap at 1,000,000 currency units so balances stay within safe Int money math.
  initialBalanceCents: z28.number().int().positive().max(1e8),
  /** Optional explicit code; generated when omitted. */
  code: z28.string().min(4).max(40).optional(),
  recipientName: z28.string().max(120).optional(),
  recipientPhone: z28.string().max(30).optional(),
  expiresAt: DateTimeSchema.optional()
});
var AdjustGiftCardInputSchema = z28.object({
  // Signed manual adjustment, bounded both ways to keep Int money math safe.
  amountCents: z28.number().int().min(-1e8).max(1e8),
  reason: z28.string().min(1).max(200)
});
var CheckBalanceInputSchema = z28.object({
  franchiseId: CuidSchema,
  code: z28.string().min(1).max(40)
});
var GiftCardBalanceSchema = z28.object({
  balanceCents: z28.number().int(),
  currency: z28.string(),
  status: GiftCardStatusSchema
});
var VoidGiftCardInputSchema = z28.object({ reason: z28.string().max(200).optional() });
var GiftCardRestaurantParamsSchema = z28.object({ restaurantId: CuidSchema });
var GiftCardParamsSchema = GiftCardRestaurantParamsSchema.extend({
  giftCardId: CuidSchema
});

// src/schemas/worker.ts
import { z as z29 } from "zod";
var WorkerRoleSchema = z29.enum(["MANAGER", "STAFF", "KITCHEN", "CASHIER"]);
var WorkerFranchiseRoleSchema = z29.enum(["OWNER", "MANAGER", "STAFF"]);
var StaffMemberSchema = z29.object({
  workerId: CuidSchema,
  name: z29.string(),
  email: z29.string().nullable(),
  role: WorkerRoleSchema,
  franchiseRole: WorkerFranchiseRoleSchema,
  isActive: z29.boolean()
});
var InviteWorkerInputSchema = z29.object({
  email: z29.email(),
  name: z29.string().min(1).max(120),
  role: WorkerRoleSchema,
  franchiseRole: WorkerFranchiseRoleSchema.optional()
});
var ChangeWorkerRoleInputSchema = z29.object({ role: WorkerRoleSchema });
var ChangeFranchiseRoleInputSchema = z29.object({
  franchiseRole: WorkerFranchiseRoleSchema
});
var SetWorkerActiveInputSchema = z29.object({ isActive: z29.boolean() });
var StaffRestaurantParamsSchema = z29.object({ restaurantId: CuidSchema });
var StaffWorkerParamsSchema = StaffRestaurantParamsSchema.extend({ workerId: CuidSchema });

// src/schemas/onboarding.ts
import { z as z30 } from "zod";
var FinalizeNewUserSchema = z30.object({
  email: z30.email(),
  password: z30.string().min(1),
  firstName: z30.string().min(1),
  lastName: z30.string().min(1)
});
var MenuInputRefSchema = z30.object({
  type: z30.enum(["photo", "pdf", "url", "text"]),
  storageKey: z30.string().nullish(),
  mimeType: z30.string().nullish(),
  urlOrText: z30.string().nullish(),
  label: z30.string().nullish()
});
var FinalizeOnboardingBodySchema = z30.object({
  newUser: FinalizeNewUserSchema,
  restaurantName: z30.string().min(1),
  address: z30.string().min(1),
  addressLine2: z30.string().nullish(),
  city: z30.string().nullish(),
  postalCode: z30.string().nullish(),
  state: z30.string().nullish(),
  country: z30.string().nullish(),
  phone: z30.string().nullish(),
  timezone: z30.string().nullish(),
  currency: z30.string().min(1),
  defaultLanguage: z30.string().nullish(),
  hoursText: z30.string().nullish(),
  menuInput: MenuInputRefSchema.nullish()
});
var FinalizeOnboardingResponseSchema = z30.object({
  queueEntryId: z30.string(),
  queuePosition: z30.number(),
  userId: z30.string(),
  paused: z30.boolean()
});
var ONBOARDING_MENU_MAX_BYTES = 10 * 1024 * 1024;
var MenuUploadUrlBodySchema = z30.object({
  type: z30.enum(["photo", "pdf"]),
  mimeType: z30.string().min(1),
  sizeBytes: z30.number().int().positive().max(ONBOARDING_MENU_MAX_BYTES)
});
var MenuUploadUrlResponseSchema = z30.object({
  uploadUrl: z30.string(),
  storageKey: z30.string(),
  mimeType: z30.string(),
  maxBytes: z30.number()
});
var QueueEntryStatusBodySchema = z30.object({
  queueEntryId: z30.string().min(1)
});
var QueueEntryStatusResponseSchema = z30.object({
  status: z30.enum(["PENDING", "PROCESSING", "COMPLETED", "FAILED"]),
  queuePosition: z30.number(),
  paused: z30.boolean(),
  errorMessage: z30.string().nullable()
});
var OnboardingErrorSchema = z30.object({
  message: z30.string(),
  code: z30.string().optional()
});

// src/schemas/onboarding-admin.ts
import { z as z31 } from "zod";
var OnboardingQueueStatusSchema = z31.enum(["PENDING", "PROCESSING", "COMPLETED", "FAILED"]);
var OnboardingQueueEntrySummarySchema = z31.object({
  id: z31.string(),
  email: z31.string(),
  name: z31.string(),
  status: OnboardingQueueStatusSchema,
  queuePosition: z31.number(),
  restaurantName: z31.string().nullable(),
  city: z31.string().nullable(),
  restaurantId: z31.string().nullable(),
  franchiseId: z31.string().nullable(),
  attempts: z31.number(),
  errorMessage: z31.string().nullable(),
  warnings: z31.array(z31.string()),
  createdAt: z31.string(),
  startedAt: z31.string().nullable(),
  processedAt: z31.string().nullable(),
  expiresAt: z31.string()
});
var OnboardingQueueEntryDetailSchema = OnboardingQueueEntrySummarySchema.extend({
  address: z31.string().nullable(),
  addressLine2: z31.string().nullable(),
  postalCode: z31.string().nullable(),
  state: z31.string().nullable(),
  country: z31.string().nullable(),
  phone: z31.string().nullable(),
  timezone: z31.string().nullable(),
  currency: z31.string().nullable(),
  defaultLanguage: z31.string().nullable(),
  hoursText: z31.string().nullable(),
  menuInputType: z31.enum(["photo", "pdf", "url", "text"]).nullable(),
  menuInputLabel: z31.string().nullable()
});
var OnboardingConfigSchema = z31.object({
  isActive: z31.boolean(),
  dailyThreshold: z31.number(),
  maxConcurrentJobs: z31.number()
});
var OnboardingQueueListSchema = z31.object({
  entries: z31.array(OnboardingQueueEntrySummarySchema),
  total: z31.number(),
  config: OnboardingConfigSchema
});
var OnboardingQueueListQuerySchema = z31.object({
  status: OnboardingQueueStatusSchema.optional(),
  limit: z31.coerce.number().int().min(1).max(100).optional(),
  offset: z31.coerce.number().int().min(0).optional()
});
var OnboardingQueueEntryParamsSchema = z31.object({ id: z31.string() });
var UpdateOnboardingConfigBodySchema = z31.object({
  isActive: z31.boolean().optional(),
  dailyThreshold: z31.coerce.number().int().min(0).optional(),
  maxConcurrentJobs: z31.coerce.number().int().min(1).max(50).optional()
}).refine((v) => Object.keys(v).length > 0, { error: "No fields to update" });

// src/schemas/user-admin.ts
import { z as z32 } from "zod";
var UserAdminNamedRefSchema = z32.object({ id: z32.string(), name: z32.string() });
var UserGroupMembershipSchema = z32.object({
  memberId: z32.string(),
  groupId: z32.string(),
  groupName: z32.string(),
  role: PartnerGroupRoleSchema,
  status: PartnerMemberStatusSchema,
  accessMode: PartnerAccessModeSchema,
  franchises: z32.array(UserAdminNamedRefSchema)
});
var UserWorkerMembershipSchema = z32.object({
  workerId: z32.string(),
  franchiseId: z32.string(),
  franchiseName: z32.string(),
  franchiseRole: WorkerFranchiseRoleSchema,
  isActive: z32.boolean(),
  restaurants: z32.array(UserAdminNamedRefSchema.extend({ role: WorkerRoleSchema }))
});
var UserSummarySchema = z32.object({
  id: z32.string(),
  email: z32.string().nullable(),
  name: z32.string().nullable(),
  isSystemAdmin: z32.boolean(),
  password: z32.string().nullable(),
  memberships: z32.array(UserGroupMembershipSchema),
  workers: z32.array(UserWorkerMembershipSchema),
  createdAt: z32.string()
});
var UserListSchema = z32.object({
  users: z32.array(UserSummarySchema),
  total: z32.number()
});
var UserListQuerySchema = z32.object({
  search: z32.string().trim().min(1).optional(),
  limit: z32.coerce.number().int().min(1).max(200).optional(),
  offset: z32.coerce.number().int().min(0).optional()
});
var UserIdParamsSchema = z32.object({ id: z32.string() });
var ResetUserPasswordBodySchema = z32.object({
  password: z32.string().min(4).max(128)
});
var SetSystemRoleBodySchema = z32.object({
  isAdmin: z32.boolean()
});
var CreateUserBodySchema = z32.object({
  email: z32.string().trim().email(),
  name: z32.string().trim().min(1).max(120),
  password: z32.string().min(4).max(128).optional(),
  isSystemAdmin: z32.boolean().optional(),
  partner: z32.object({
    groupId: z32.string(),
    role: PartnerGroupRoleSchema,
    accessMode: PartnerAccessModeSchema,
    franchiseIds: z32.array(z32.string()).optional()
  }).optional(),
  worker: z32.object({
    franchiseId: z32.string(),
    franchiseRole: WorkerFranchiseRoleSchema,
    restaurants: z32.array(z32.object({ restaurantId: z32.string(), role: WorkerRoleSchema }))
  }).optional()
});

// src/schemas/online-payment-admin.ts
import { z as z33 } from "zod";
var OnlinePaymentSummarySchema = z33.object({
  id: z33.string(),
  franchiseId: z33.string(),
  franchiseName: z33.string(),
  restaurantId: z33.string(),
  restaurantName: z33.string(),
  methodId: z33.string(),
  providerMode: PaymentProviderModeSchema.nullable(),
  providerPaymentId: z33.string().nullable(),
  paymentReference: z33.string(),
  status: PaymentStatusSchema,
  amountCents: z33.number().int(),
  refundedCents: z33.number().int(),
  currency: z33.string(),
  orderId: z33.string().nullable(),
  orderConfirmationCode: z33.string().nullable(),
  orderStatus: z33.string().nullable(),
  refundCount: z33.number().int(),
  hasUnsettledRefund: z33.boolean(),
  createdAt: z33.string(),
  updatedAt: z33.string()
});
var OnlinePaymentPageSchema = z33.object({
  items: z33.array(OnlinePaymentSummarySchema),
  nextCursor: z33.string().nullable()
});
var OnlinePaymentListQuerySchema = z33.object({
  methodId: z33.string().optional(),
  status: PaymentStatusSchema.optional(),
  restaurantId: z33.string().optional(),
  search: z33.string().optional(),
  cursor: z33.string().optional(),
  limit: z33.coerce.number().int().min(1).max(200).optional()
});
var OnlinePaymentParamsSchema = z33.object({ paymentId: z33.string() });
var OnlinePaymentDetailSchema = OnlinePaymentSummarySchema.extend({
  refunds: z33.array(RefundEntrySchema),
  providerData: z33.unknown()
});
var VerificationVerdictSchema = z33.enum(["MATCH", "MISMATCH", "UNKNOWN"]);
var ProviderRefundSchema = z33.object({
  externalRefundId: z33.string().nullable(),
  amountCents: z33.number().int().nullable(),
  rawStatus: z33.string(),
  settled: z33.boolean()
});
var VerificationCheckSchema = z33.object({
  key: z33.enum(["status", "captured", "refunded"]),
  verdict: VerificationVerdictSchema
});
var PaymentVerificationSchema = z33.object({
  paymentId: z33.string(),
  methodId: z33.string(),
  providerPaymentId: z33.string(),
  checkedAt: z33.string(),
  ours: z33.object({
    status: PaymentStatusSchema,
    amountCents: z33.number().int(),
    currency: z33.string(),
    capturedCents: z33.number().int(),
    refundedCents: z33.number().int(),
    recordedRefundCents: z33.number().int()
  }),
  theirs: z33.object({
    rawStatus: z33.string(),
    mappedStatus: PaymentStatusSchema,
    capturedCents: z33.number().int().nullable(),
    refundedCents: z33.number().int().nullable(),
    refunds: z33.array(ProviderRefundSchema).nullable(),
    raw: z33.unknown()
  }),
  checks: z33.array(VerificationCheckSchema),
  verdict: VerificationVerdictSchema
});

// src/schemas/waitlist.ts
import { z as z34 } from "zod";
var WaitlistStatusSchema = z34.enum([
  "WAITING",
  "NOTIFIED",
  "SEATED",
  "CANCELLED",
  "NO_SHOW"
]);
var WaitlistEntrySchema = z34.object({
  id: CuidSchema,
  partySize: z34.number().int().positive(),
  guestName: z34.string(),
  guestPhone: z34.string().nullable(),
  quotedWaitMinutes: z34.number().int().nonnegative(),
  status: WaitlistStatusSchema,
  /** Minutes since the party was added — drives the aging colour. */
  waitingMinutes: z34.number().int().nonnegative(),
  createdAt: z34.string(),
  notifiedAt: z34.string().nullable()
});
var AddToWaitlistInputSchema = z34.object({
  partySize: z34.number().int().positive().max(99),
  guestName: z34.string().min(1).max(120),
  guestPhone: z34.string().max(40).optional(),
  /** Host override of the auto quote; omit to use the heuristic. */
  quotedWaitMinutes: z34.number().int().nonnegative().max(600).optional()
});
var SeatWaitlistInputSchema = z34.object({ tableId: CuidSchema });
var SeatWaitlistResultSchema = z34.object({
  entryId: CuidSchema,
  tableSessionId: CuidSchema
});
var WaitlistRestaurantParamsSchema = z34.object({ restaurantId: CuidSchema });
var WaitlistEntryParamsSchema = WaitlistRestaurantParamsSchema.extend({
  entryId: CuidSchema
});

// src/schemas/ai-credit.ts
import { z as z35 } from "zod";
var AiCreditStatusSchema = z35.object({
  restaurantId: z35.string(),
  aiChatEnabled: z35.boolean(),
  currentBalanceCents: z35.number().int(),
  totalSpentCents: z35.number().int(),
  lastResetAt: z35.string().nullable(),
  // ISO timestamp, or null if never activated
  monthlyDefaultCents: z35.number().int(),
  usage: z35.object({
    totalRequests: z35.number().int().nonnegative(),
    totalTokens: z35.number().int().nonnegative(),
    totalCostCents: z35.number().int().nonnegative()
  })
});
var SetAiEnabledBodySchema = z35.object({ enabled: z35.boolean() });
var TopUpAiCreditBodySchema = z35.object({
  // Amount to add, in cents. Positive only; max one top-up of $10,000 per call.
  amountCents: z35.number().int().positive().max(1e6)
});
var PartnerGroupAiCreditStatusSchema = z35.object({
  groupId: z35.string(),
  currentBalanceCents: z35.number().int(),
  totalSpentCents: z35.number().int(),
  byUser: z35.array(
    z35.object({
      userId: z35.string(),
      email: z35.string().nullable(),
      calls: z35.number().int().nonnegative(),
      totalTokens: z35.number().int().nonnegative(),
      chargedCents: z35.number().int().nonnegative()
    })
  )
});
var AiMenuImportQualityRowSchema = z35.object({
  threadId: z35.string(),
  restaurantId: z35.string(),
  restaurantName: z35.string(),
  status: z35.enum(["READING", "READY", "FAILED"]),
  pagesTotal: z35.number().int(),
  pagesFailed: z35.number().int(),
  sections: z35.number().int(),
  dishes: z35.number().int(),
  dishesWithoutPriceRatio: z35.number(),
  currencyMismatch: z35.boolean(),
  droppedCodes: z35.number().int(),
  clampedPickLimits: z35.number().int(),
  optionGroups: z35.number().int(),
  choices: z35.number().int(),
  modelId: z35.string(),
  promptVersion: z35.string(),
  createdAt: z35.string()
});
var AiMenuImportQualityListSchema = z35.object({
  items: z35.array(AiMenuImportQualityRowSchema)
});

// src/schemas/ai-admin.ts
import { z as z36 } from "zod";
var UsdAmountSchema = z36.string().regex(/^-?\d+(\.\d+)?$/);
var AiModelOptionSchema = z36.object({
  id: z36.string(),
  label: z36.string(),
  summary: z36.string(),
  pricing: z36.object({
    inputUsdPerMillionTokens: UsdAmountSchema,
    outputUsdPerMillionTokens: UsdAmountSchema
  })
});
var AiFeatureModelSchema = z36.object({
  id: z36.string(),
  label: z36.string(),
  summary: z36.string(),
  modelId: z36.string()
});
var AiModelSettingsSchema = z36.object({
  models: z36.array(AiModelOptionSchema),
  features: z36.array(AiFeatureModelSchema)
});
var SetAiFeatureModelBodySchema = z36.object({ modelId: z36.string().min(1) });
var AiUsageQuerySchema = z36.object({
  days: z36.coerce.number().int().min(1).max(365)
});
var AiUsageTotalsSchema = z36.object({
  calls: z36.number().int().nonnegative(),
  failedCalls: z36.number().int().nonnegative(),
  inputTokens: z36.number().int().nonnegative(),
  outputTokens: z36.number().int().nonnegative(),
  costUsd: UsdAmountSchema
});
var AiUsageSummarySchema = z36.object({
  from: z36.string(),
  to: z36.string(),
  total: AiUsageTotalsSchema,
  byFeature: z36.array(AiUsageTotalsSchema.extend({ featureId: z36.string(), label: z36.string() })),
  byModel: z36.array(AiUsageTotalsSchema.extend({ modelId: z36.string(), label: z36.string() }))
});

// src/schemas/setting-audit.ts
import { z as z37 } from "zod";
var PageQuerySchema = z37.object({
  page: z37.coerce.number().int().min(1).default(1),
  pageSize: z37.coerce.number().int().min(1).max(100).default(10)
});
var SettingAreaSchema = z37.enum([
  "BUSINESS_PROFILE",
  "OPENING_HOURS",
  "HOLIDAYS",
  "TABLE_PLAN",
  "BRANDING",
  "STOREFRONT_LINK",
  "RESERVATION_SETUP",
  "RESERVATION_CAPACITY",
  "RESERVATION_HOURS",
  "ORDER_SETUP",
  "ORDER_CAPACITY",
  "ORDER_HOURS",
  "DELIVERY_ZONES",
  "PAYMENT",
  "ORDER_LABELS",
  "PRINTERS",
  "PUSH_NOTIFICATIONS",
  "EMAIL"
]);
var SettingChangeTypeSchema = z37.enum(["CREATED", "UPDATED", "DELETED"]);
var SettingChangeSchema = z37.object({
  id: z37.string(),
  area: SettingAreaSchema,
  changeType: SettingChangeTypeSchema,
  /** The sub-entity edited — which printer, which weekday, which provider. */
  targetId: z37.string().nullable(),
  targetLabel: z37.string().nullable(),
  /** Null on CREATED/DELETED, where the row describes the whole sub-entity. */
  field: z37.string().nullable(),
  /** Rendered to text at write time. Null means "was not set". */
  oldValue: z37.string().nullable(),
  newValue: z37.string().nullable(),
  /**
   * The value is withheld on purpose (payment credentials). Render "changed"
   * rather than a diff — `oldValue`/`newValue` are always null here.
   */
  isSensitive: z37.boolean(),
  actorUserId: z37.string(),
  /** Snapshotted at write time, so a deleted account still reads as a person. */
  actorName: z37.string(),
  actorEmail: z37.string().nullable(),
  /** Groups the rows written by one Save. */
  changeSetId: z37.string(),
  changedAt: z37.string(),
  /** Ties the change to its trace in Tempo. */
  traceId: z37.string().nullable()
});
var SettingChangeActorSchema = z37.object({
  actorUserId: z37.string(),
  actorName: z37.string()
});
var SettingChangesParamsSchema = z37.object({ restaurantId: z37.string() });
var SettingChangesQuerySchema = PageQuerySchema.extend({
  area: SettingAreaSchema.optional(),
  actorUserId: z37.string().optional(),
  /** ISO date-times bounding `changedAt` — `from` inclusive, `to` exclusive. */
  from: z37.string().optional(),
  to: z37.string().optional()
});
var SettingChangePageSchema = z37.object({
  items: z37.array(SettingChangeSchema),
  /** Total matching the filters, so the UI can render the page numbers. */
  total: z37.number().int().nonnegative(),
  page: z37.number().int().min(1),
  pageSize: z37.number().int().min(1),
  pageCount: z37.number().int().nonnegative()
});
export {
  AI_ATTACHMENT_MAX_BYTES,
  AI_CHANGESET_HISTORY_PAGE_MAX,
  AI_CHANGESET_TURN_ATTACHMENTS_MAX,
  AI_CHANGESET_VIEW_SEARCH_MAX,
  AI_MENU_SOURCES_MAX,
  AI_MENU_SOURCE_TEXT_MAX,
  AI_VOICE_AUDIO_TYPES,
  AI_VOICE_MAX_SECONDS,
  AI_VOICE_MAX_UPLOAD_BYTES,
  AI_VOICE_SAMPLE_RATE,
  AI_WRITE_TOOL_INPUT_SCHEMAS,
  AI_WRITE_TOOL_NAMES,
  ALLERGEN_SHORTKEY_MAX,
  AcceptOrderInputSchema,
  AccountFieldSchema,
  ActiveOnlyQuerySchema,
  ActivePromotionsQuerySchema,
  AddCustomerPhoneSchema,
  AddFavoriteInputSchema,
  AddSeatInputSchema,
  AddToWaitlistInputSchema,
  AdjustGiftCardInputSchema,
  AdjustPointsInputSchema,
  AdoptedDeviceIdSchema,
  AdvancedShiftRulesSchema,
  AgeBasisSchema,
  AiApprovalResponseSchema,
  AiAssetUrlPrefix,
  AiAssignTablesInputSchema,
  AiAttachmentParamsSchema,
  AiAttachmentPresignSchema,
  AiAttachmentUploadSchema,
  AiAttachmentUploadTicketSchema,
  AiCancelReservationInputSchema,
  AiChangesetAppliedSchema,
  AiChangesetHistoryQuerySchema,
  AiChangesetTurnSchema,
  AiChangesetViewSchema,
  AiChatApprovalsMessageSchema,
  AiChatRequestSchema,
  AiChatUserMessageSchema,
  AiCloseTabInputSchema,
  AiConversationThreadSchema,
  AiCreateCustomerInputSchema,
  AiCreateReservationInputSchema,
  AiCreditStatusSchema,
  AiExceptionsTurnParamsSchema,
  AiFeatureModelSchema,
  AiFireRoundInputSchema,
  AiLocaleSchema,
  AiManageCategoriesInputSchema,
  AiManageProductsInputSchema,
  AiMenuImportProgressSchema,
  AiMenuImportQualityListSchema,
  AiMenuImportQualityRowSchema,
  AiMenuItemsOutputSchema,
  AiMenuParamsSchema,
  AiMenuSourceSchema,
  AiMenuTurnParamsSchema,
  AiModelOptionSchema,
  AiModelSettingsSchema,
  AiMoveSessionInputSchema,
  AiProductParamsSchema,
  AiProductTurnParamsSchema,
  AiPromotionsTurnParamsSchema,
  AiReservationListOutputSchema,
  AiRestaurantParamsSchema,
  AiRevenueOutputSchema,
  AiSetItemAvailabilityInputSchema,
  AiSetPricesInputSchema,
  AiStoredMessageSchema,
  AiSuccessSchema,
  AiTakePaymentInputSchema,
  AiThreadConnectionSchema,
  AiThreadIdSchema,
  AiThreadListQuerySchema,
  AiThreadParamsSchema,
  AiToggleOnlineOrderingInputSchema,
  AiToggleTableOrderingInputSchema,
  AiTurnAttachmentSchema,
  AiTurnAuthorSchema,
  AiTurnOutcomeSchema,
  AiUpdateOpeningHoursInputSchema,
  AiUpdateProfileInputSchema,
  AiUsageQuerySchema,
  AiUsageSummarySchema,
  AiUsageTotalsSchema,
  AiVoiceSessionRequestSchema,
  AiVoiceSessionSchema,
  AiVoiceTranscribeFieldsSchema,
  AiVoiceTranscriptionSchema,
  AiVoidItemInputSchema,
  AllergenAggregationRequestSchema,
  AllergenAggregationResponseSchema,
  AllergenCodeKindSchema,
  AllergenSchema,
  AllergenSheetConfigSchema,
  AllergenSheetParseResultSchema,
  AllergenShortKeySchema,
  AllergenSnapshotEntrySchema,
  AllergenSnapshotSchema,
  AllergenTableEntrySchema,
  AllergenTableEntryViewSchema,
  AllergenTableSchema,
  AllergenTableViewSchema,
  AnalyticsDayQuerySchema,
  AnalyticsParamsSchema,
  AnalyticsRangeQuerySchema,
  ArchivedProductSchema,
  ArrivalStatusSchema,
  AssignItemSeatInputSchema,
  AttachReservationInputSchema,
  AutoConfirmModeSchema,
  AvailabilityQuerySchema,
  AvailabilityRangeQuerySchema,
  AvailabilityRangeResponseSchema,
  AvailabilityResponseSchema,
  AvailableSlotSchema,
  AwaitingPaymentOrderListSchema,
  AwaitingPaymentOrderSchema,
  AwaitingPaymentOrdersParamsSchema,
  BaselineOrderShiftSchema,
  BaselineShiftSchema,
  BulkCreateTablesResponseSchema,
  BulkCreateTablesSchema,
  BulkOpeningHoursSchema,
  CancelActorSchema,
  CancelOrderInputSchema,
  CancelReservationSchema,
  CancelRoundInputSchema,
  CapacityModeSchema,
  CapacityModeViewSchema,
  CategoryCreateSchema,
  CategoryParamsSchema,
  CategoryScheduleSchema,
  CategoryScheduleWindowSchema,
  CategorySchema,
  CategoryUpdateSchema,
  ChangeFranchiseRoleInputSchema,
  ChangeWorkerRoleInputSchema,
  CheckBalanceInputSchema,
  ChoiceParamsSchema,
  CollectMethodKindSchema,
  CollectionModeSchema,
  ComparisonBasisSchema,
  ConnectPaymentProviderInputSchema,
  CouponKindSchema,
  CoversTurnoverSchema,
  CreateBaselineOrderShiftSchema,
  CreateBaselineShiftSchema,
  CreateLabelInputSchema,
  CreateMenuFromSourcesSchema,
  CreateOrderHolidayHoursSchema,
  CreateOrderShiftSchema,
  CreatePriceListInputSchema,
  CreatePromotionInputSchema,
  CreatePurgeRuleInputSchema,
  CreateRefundInputSchema,
  CreateShiftSchema,
  CreateUserBodySchema,
  CreateWindowOrderShiftSchema,
  CreateWindowShiftSchema,
  CreateZoneSchema,
  CuidSchema,
  CursorSchema,
  CustomerAddressInputSchema,
  CustomerAddressParamsSchema,
  CustomerAddressSchema,
  CustomerAvailabilitySchema,
  CustomerCategorySchema,
  CustomerChoiceSchema,
  CustomerCreateSchema,
  CustomerFavoriteParamsSchema,
  CustomerFavoriteSchema,
  CustomerFranchiseParamsSchema,
  CustomerListQuerySchema,
  CustomerListResponseSchema,
  CustomerLoyaltySchema,
  CustomerMeSchema,
  CustomerMenuQuerySchema,
  CustomerMenuSchema,
  CustomerOptionSchema,
  CustomerParamsSchema,
  CustomerPhoneParamsSchema,
  CustomerPhoneSchema,
  CustomerProductSchema,
  CustomerPromotionSchema,
  CustomerSchema,
  CustomerUpdateSchema,
  DEFAULT_SHIFT_RULES,
  DailyPointSchema,
  DailySalesSummarySchema,
  DateSchema,
  DateTimeSchema,
  DayEndReportSchema,
  DayOfWeekSchema,
  DayOverrideSchema,
  DayOverrideStateSchema,
  DayPartSchema,
  DayPartSlotSchema,
  DeliveryAddressInputSchema,
  DeliveryAddressSchema,
  DeliveryPriceModeSchema,
  DeliveryReadinessCodeSchema,
  DeliveryReadinessProblemSchema,
  DeliveryReadinessSchema,
  DeliveryRouteSchema,
  DeliveryZoneInputSchema,
  DeliveryZoneModeSchema,
  DeliveryZoneParamsSchema,
  DeliveryZoneSchema,
  DeviceHealthSchema,
  DeviceHealthStateSchema,
  DevicePlatformSchema,
  DeviceTokenListQuerySchema,
  DeviceTokenListSchema,
  DeviceTokenParamsSchema,
  DeviceTokenRowSchema,
  DeviceTokenSchema,
  DisconnectPaymentProviderQuerySchema,
  DiscountTypeSchema,
  DistanceCalculationSchema,
  EXCEPTION_CHANGESET_MAX,
  EmailSchema,
  ErrorResponseSchema,
  ExceptionChangeSchema,
  ExceptionChangesSchema,
  ExceptionChangesetStaleSchema,
  ExceptionCreateSchema,
  ExceptionListQuerySchema,
  ExceptionParamsSchema,
  ExceptionSchema,
  ExceptionTypeSchema,
  ExceptionUpdateSchema,
  ExceptionValueSchema,
  ExceptionsChangesetAppliedSchema,
  ExceptionsQuickEditSessionSchema,
  FavoriteTargetTypeSchema,
  FinalizeOnboardingBodySchema,
  FinalizeOnboardingResponseSchema,
  FloorPlanElementSchema,
  FloorPlanLabelElementSchema,
  FloorPlanLayoutSchema,
  FloorPlanSchema,
  FloorPlanTableElementSchema,
  FloorPlanWallElementSchema,
  ForceCloseInputSchema,
  FranchiseHardDeleteResultSchema,
  FranchiseListQuerySchema,
  FranchiseListResponseSchema,
  FranchiseParamsSchema,
  FranchiseRoleSchema,
  FranchiseSchema,
  FranchiseUpdateSchema,
  GetAvailableSlotsQuerySchema,
  GiftCardBalanceSchema,
  GiftCardDetailSchema,
  GiftCardParamsSchema,
  GiftCardRestaurantParamsSchema,
  GiftCardSchema,
  GiftCardStatusSchema,
  GiftCardTransactionSchema,
  GiftCardTransactionTypeSchema,
  GuestInfoSchema,
  GuestReservationParamsSchema,
  HexColorSchema,
  HistoryEntrySchema,
  IdentifySessionInputSchema,
  InboxListQuerySchema,
  InviteWorkerInputSchema,
  IssueGiftCardInputSchema,
  IssuePersonalCouponInputSchema,
  KitchenTicketLineSchema,
  KitchenTicketSchema,
  KitchenTicketStatusSchema,
  LabelParamsSchema,
  LabelRestaurantParamsSchema,
  LabelSchema,
  LanguageSchema,
  LimitSchema,
  LinkSessionCustomerInputSchema,
  ListJobsQuerySchema,
  ListOrdersQuerySchema,
  LocalizedStringOptionalSchema,
  LocalizedStringSchema,
  LocalizedTextSchema,
  LoyaltyBalanceSchema,
  LoyaltyConfigSchema,
  LoyaltyCustomerParamsSchema,
  LoyaltyEntryTypeSchema,
  LoyaltyLedgerEntrySchema,
  LoyaltyRestaurantParamsSchema,
  MENU_CHANGESET_MAX,
  MENU_ORDER_MAX,
  MENU_TEXT_MAX,
  ManualPaymentMethodInputSchema,
  ManualPaymentMethodParamsSchema,
  ManualPaymentMethodSchema,
  MarkAllReadResponseSchema,
  MarkArrivalStatusSchema,
  MeRestaurantAccessSchema,
  MenuActivationParamsSchema,
  MenuActivationSchema,
  MenuChangeSchema,
  MenuChangesSchema,
  MenuChangesetStaleSchema,
  MenuChannelSchema,
  MenuChecklistSchema,
  MenuCreateSchema,
  MenuFromSourcesAcceptedSchema,
  MenuImportFlagSchema,
  MenuImportReviewSchema,
  MenuLanguageSchema,
  MenuOverrideChannelSchema,
  MenuParamsSchema,
  MenuQuickEditSessionSchema,
  MenuRestaurantParamsSchema,
  MenuSchema,
  MenuTreeSchema,
  MenuUpdateSchema,
  MenuUploadUrlBodySchema,
  MenuUploadUrlResponseSchema,
  MergeSessionInputSchema,
  MoveSessionInputSchema,
  NonNegativeIntSchema,
  NotificationListResponseSchema,
  NotificationParamsSchema,
  NotificationSchema,
  OCCASION_VALUES,
  ONBOARDING_MENU_MAX_BYTES,
  OPENING_BUFFER_MINUTES_MAX,
  OnboardingConfigSchema,
  OnboardingErrorSchema,
  OnboardingQueueEntryDetailSchema,
  OnboardingQueueEntryParamsSchema,
  OnboardingQueueEntrySummarySchema,
  OnboardingQueueListQuerySchema,
  OnboardingQueueListSchema,
  OnboardingQueueStatusSchema,
  OnlineOrderPrepStatusSchema,
  OnlineOrderSchema,
  OnlinePaymentDetailSchema,
  OnlinePaymentListQuerySchema,
  OnlinePaymentPageSchema,
  OnlinePaymentParamsSchema,
  OnlinePaymentProviderSchema,
  OnlinePaymentSummarySchema,
  OpenSessionInputSchema,
  OpeningHoursQuerySchema,
  OpeningHoursSchema,
  OpeningHoursServiceParamsSchema,
  OpsEventDetailSchema,
  OpsEventListQuerySchema,
  OpsEventPageSchema,
  OpsEventParamsSchema,
  OptionChoiceCreateSchema,
  OptionChoiceSchema,
  OptionChoiceUpdateSchema,
  OptionParamsSchema,
  OptionRulesSchema,
  OrderAdvancedRulesSchema,
  OrderCapacityModeQuerySchema,
  OrderCapacityModeSchema,
  OrderChannelStatusSchema,
  OrderHistoryQuerySchema,
  OrderHistoryResponseSchema,
  OrderHolidayHoursListQuerySchema,
  OrderHolidayHoursParamsSchema,
  OrderHolidayHoursRestaurantParamsSchema,
  OrderHolidayHoursSchema,
  OrderIdParamsSchema,
  OrderItemChoiceSchema,
  OrderItemInputSchema,
  OrderItemSchema,
  OrderListResponseSchema,
  OrderPauseStateSchema,
  OrderPrepTransitionInputSchema,
  OrderRestaurantParamsSchema,
  OrderRoundItemChoiceSchema,
  OrderRoundItemSchema,
  OrderRoundSchema,
  OrderRoundStatusSchema,
  OrderShiftListQuerySchema,
  OrderShiftParamsSchema,
  OrderShiftRestaurantParamsSchema,
  OrderShiftRulesSchema,
  OrderShiftSchema,
  OrderSimpleRulesSchema,
  OrderStageSchema,
  OrderStatusSchema,
  OrderSummarySchema,
  OrderTimelineEntrySchema,
  OrderTimelineKindSchema,
  OrderTypeConfigInputSchema,
  OrderTypeConfigParamsSchema,
  OrderTypeConfigSchema,
  OrderTypeSchema,
  OrderWithPaymentSchema,
  OwnerOrderParamsSchema,
  PROMOTION_CHANGESET_MAX,
  PageQuerySchema,
  PaginatedResponseSchema,
  PaginationQuerySchema,
  ParseAllergenSheetSchema,
  PartnerAccessModeSchema,
  PartnerActionResultSchema,
  PartnerFranchiseRoleSchema,
  PartnerGroupAiCreditStatusSchema,
  PartnerGroupFranchiseSchema,
  PartnerGroupParamsSchema,
  PartnerGroupRoleSchema,
  PartnerGroupSchema,
  PartnerGroupSelectionSchema,
  PartnerMemberDetailSchema,
  PartnerMemberInviteSchema,
  PartnerMemberParamsSchema,
  PartnerMemberRoleChangeSchema,
  PartnerMemberSchema,
  PartnerMemberStatusSchema,
  PartySizePacingRuleSchema,
  PauseModeSchema,
  PauseOrderInputSchema,
  PauseOrderResultSchema,
  PaymentBreakdownSchema,
  PaymentMethodKindSchema,
  PaymentMethodListSchema,
  PaymentMethodNoteParamsSchema,
  PaymentMethodNoteResultSchema,
  PaymentMethodNoteSchema,
  PaymentMethodRowSchema,
  PaymentMethodsQuerySchema,
  PaymentOrderParamsSchema,
  PaymentProviderConfigSchema,
  PaymentProviderModeSchema,
  PaymentProviderParamsSchema,
  PaymentRestaurantParamsSchema,
  PaymentSchema,
  PaymentStatusSchema,
  PaymentVerificationSchema,
  PeakHourSlotSchema,
  PeakHoursResponseSchema,
  PendingReservationsQuerySchema,
  PeriodComparisonQuerySchema,
  PeriodComparisonSchema,
  PersonalCouponListItemSchema,
  PersonalCouponSchema,
  PhoneSchema,
  PlaceOrderInputSchema,
  PositiveIntSchema,
  PreviewDiscountInputSchema,
  PreviewDiscountLineSchema,
  PreviewDiscountResultSchema,
  PreviewRedemptionInputSchema,
  PriceListEntryParamsSchema,
  PriceListEntrySchema,
  PriceListEntryUpsertSchema,
  PriceListParamsSchema,
  PriceListSchema,
  PriceListWithEntriesSchema,
  PrintDateTimeFormatSchema,
  PrintItemLabelModeSchema,
  PrintJobParamsSchema,
  PrintJobSchema,
  PrintJobSourceKindSchema,
  PrintJobStatusSchema,
  PrintJobTypeSchema,
  PrintOrderTicketInputSchema,
  PrintPageInputSchema,
  PrintPageSchema,
  PrintPayloadSchema,
  PrintPreviewKindSchema,
  PrintPreviewQuerySchema,
  PrintPreviewSchema,
  PrintReceiptInputSchema,
  PrintRestaurantParamsSchema,
  PrintRoutingSchema,
  PrintSettingsSchema,
  PrintTextSizeSchema,
  PrinterConnectionSchema,
  PrinterParamsSchema,
  PrinterSchema,
  PrinterStatusEventSchema,
  PrinterStatusEventsQuerySchema,
  PrinterStatusSchema,
  PrinterTypeSchema,
  ProductCodeSchema,
  ProductCreateSchema,
  ProductLabelSchema,
  ProductOptionCreateSchema,
  ProductOptionSchema,
  ProductOptionUpdateSchema,
  ProductParamsSchema,
  ProductSchema,
  ProductUpdateSchema,
  ProductWithOptionsCreateSchema,
  PromoCodeInputSchema,
  PromoCodeSchema,
  PromotionChangeSchema,
  PromotionChangesSchema,
  PromotionChangesetStaleSchema,
  PromotionConflictSchema,
  PromotionCustomerParamsSchema,
  PromotionLangQuerySchema,
  PromotionListQuerySchema,
  PromotionOrderTypeSchema,
  PromotionParamsSchema,
  PromotionPaymentMethodKindSchema,
  PromotionRestaurantParamsSchema,
  PromotionSchema,
  PromotionTargetInputSchema,
  PromotionTargetSchema,
  PromotionTargetTypeSchema,
  PromotionTriggerSchema,
  PromotionsChangesetAppliedSchema,
  PromotionsQuickEditSessionSchema,
  ProofOrderParamsSchema,
  ProofUploadResultSchema,
  ProofViewSchema,
  ProposedSplitInputSchema,
  ProposedSplitSchema,
  PublicRestaurantSchema,
  PurgeCriteriaSchema,
  PurgeCriteriaSnapshotSchema,
  PurgeExecuteInputSchema,
  PurgeInputSchema,
  PurgeLabelMatchSchema,
  PurgeMetricsSchema,
  PurgePreviewInputSchema,
  PurgePreviewSchema,
  PurgeRecordSchema,
  PurgeRestaurantParamsSchema,
  PurgeRuleParamsSchema,
  PurgeRuleSchema,
  PurgeRunBreakdownSchema,
  PurgeRunListQuerySchema,
  PurgeRunParamsSchema,
  PurgeRunSchema,
  PurgeRunStatusSchema,
  PurgeScheduleKindSchema,
  PurgeSelectionSchema,
  PurgeSkipReasonSchema,
  PurgeSourceSchema,
  PurgeTargetSchema,
  QrResolveResponseSchema,
  QueueEntryStatusBodySchema,
  QueueEntryStatusResponseSchema,
  ReceiptPaymentSnapshotSchema,
  ReceiptSnapshotSchema,
  RedemptionResultSchema,
  RefreshPaymentResponseSchema,
  RefundEntrySchema,
  RefundParamsSchema,
  RefundReasonSchema,
  RefundSettlementStatusSchema,
  RegisterDeviceTokenResponseSchema,
  RegisterDeviceTokenSchema,
  RegisterPrinterInputSchema,
  RejectProofInputSchema,
  RejectReservationSchema,
  ReorderItemReportSchema,
  ReorderLineSchema,
  ReorderParamsSchema,
  ReorderResponseSchema,
  ReorderSchema,
  RequestBillInputSchema,
  RequestOtpInputSchema,
  RequestOtpResponseSchema,
  RequestServiceInputSchema,
  ReservationActorTypeSchema,
  ReservationConfigEditableSchema,
  ReservationConfigParamsSchema,
  ReservationConfigSchema,
  ReservationConfigUpdateSchema,
  ReservationCreateSchema,
  ReservationEventSchema,
  ReservationEventTypeSchema,
  ReservationListQuerySchema,
  ReservationListResponseSchema,
  ReservationListSummarySchema,
  ReservationParamsSchema,
  ReservationRestaurantParamsSchema,
  ReservationSchema,
  ReservationSetupParamsSchema,
  ReservationShiftSchema,
  ReservationSourceSchema,
  ReservationStatusSchema,
  ReservationUpdateSchema,
  ResetUserPasswordBodySchema,
  RestaurantAccessStateSchema,
  RestaurantListQuerySchema,
  RestaurantListResponseSchema,
  RestaurantParamsSchema,
  RestaurantRoleSchema,
  RestaurantSchema,
  RestaurantSlugParamsSchema,
  RestaurantTableParamsSchema,
  RestaurantTableSchema,
  RestaurantUpdateSchema,
  RoundFiringSchema,
  RoundItemInputSchema,
  RouteGeometrySchema,
  SEATING_VALUES,
  SalesTotalsSchema,
  ScheduleWindowSchema,
  SeatReservationInputSchema,
  SeatWaitlistInputSchema,
  SeatWaitlistResultSchema,
  SendSelfTestPushBodySchema,
  SendSelfTestPushResultSchema,
  SendTestPushBodySchema,
  SendTestPushResultSchema,
  ServiceTypeSchema,
  SessionHistoryQuerySchema,
  SessionPaymentSchema,
  SessionSeatSchema,
  SetAiEnabledBodySchema,
  SetAiFeatureModelBodySchema,
  SetAllergenSheetSchema,
  SetAllergenTableSchema,
  SetArrivalStatusSchema,
  SetLabelsInputSchema,
  SetMenuActivationSchema,
  SetPaymentMethodNoteInputSchema,
  SetProviderActivationInputSchema,
  SetSystemRoleBodySchema,
  SetWorkerActiveInputSchema,
  SetZoneTableLayoutSchema,
  SettingAreaSchema,
  SettingChangeActorSchema,
  SettingChangePageSchema,
  SettingChangeSchema,
  SettingChangeTypeSchema,
  SettingChangesParamsSchema,
  SettingChangesQuerySchema,
  ShiftModeSchema2 as ShiftModeSchema,
  ShiftParamsSchema,
  ShiftRulesSchema,
  ShiftSchema,
  ShipPriceInputSchema,
  ShipPriceResponseSchema,
  SimpleShiftRulesSchema,
  SimpleTableCategorySchema,
  SlotCapacityCheckResponseSchema,
  SlotCapacityQuerySchema,
  SpecialRequestSchema,
  SplitBillInputSchema,
  SplitModeSchema,
  StaffMemberSchema,
  StaffOpenSessionInputSchema,
  StaffReservationCreateSchema,
  StaffRestaurantParamsSchema,
  StaffWorkerParamsSchema,
  SubmitRoundInputSchema,
  SuccessSchema,
  SuspendRestaurantBodySchema,
  SystemRoleSchema,
  TableAttributeSchema,
  TableDerivedStateSchema,
  TableItemParamsSchema,
  TableModeSchema,
  TableModeViewSchema,
  TableOrderingConfigInputSchema,
  TableOrderingConfigSchema,
  TableOrderingModeSchema,
  TableOrderingQrParamsSchema,
  TableOrderingRestaurantParamsSchema,
  TableRoundParamsSchema,
  TableServiceRequestKindSchema,
  TableServiceRequestParamsSchema,
  TableServiceRequestSchema,
  TableServiceRequestStatusSchema,
  TableSessionActorSchema,
  TableSessionParamsSchema,
  TableSessionSchema,
  TableSessionStatusSchema,
  TableSessionSummarySchema,
  TableSessionTimelineEntrySchema,
  TableSessionTimelineKindSchema,
  TableStateSchema,
  TableTurnSchema,
  TakePaymentInputSchema,
  TaxBreakdownLineSchema,
  ThemeIdSchema,
  ThreadStatusSchema,
  TimeSchema,
  TopItemRowSchema,
  TopItemsQuerySchema,
  TopItemsRankBySchema,
  TopItemsResponseSchema,
  TopUpAiCreditBodySchema,
  UnpauseOrderInputSchema,
  UnreadCountResponseSchema,
  UnregisterDeviceTokenResponseSchema,
  UpdateAiThreadSchema,
  UpdateCustomerPhoneSchema,
  UpdateDeliveryZoneInputSchema,
  UpdateFloorPlanSchema,
  UpdateLabelInputSchema,
  UpdateManualPaymentMethodInputSchema,
  UpdateMeResponseSchema,
  UpdateMeSchema,
  UpdateOnboardingConfigBodySchema,
  UpdateOrderHolidayHoursSchema,
  UpdateOrderShiftSchema,
  UpdatePrintSettingsInputSchema,
  UpdatePrinterInputSchema,
  UpdatePromotionInputSchema,
  UpdatePurgeRuleInputSchema,
  UpdateRestaurantTableSchema,
  UpdateShiftSchema,
  UpdateZoneSchema,
  UploadProofInputSchema,
  UpsertLoyaltyConfigInputSchema,
  UpsertRoutingInputSchema,
  UserContextSchema,
  UserGroupMembershipSchema,
  UserIdParamsSchema,
  UserListQuerySchema,
  UserListSchema,
  UserSummarySchema,
  UserWorkerMembershipSchema,
  ValidateCodeInputSchema,
  ValidateCodeResultSchema,
  VerificationVerdictSchema,
  VerifyOtpInputSchema,
  VoidGiftCardInputSchema,
  VoidItemInputSchema,
  WaitlistEntryParamsSchema,
  WaitlistEntrySchema,
  WaitlistRestaurantParamsSchema,
  WaitlistStatusSchema,
  WeekOverridesSchema,
  WindowOrderShiftSchema,
  WindowShiftSchema,
  WorkerFranchiseRoleSchema,
  WorkerRoleSchema,
  ZoneParamsSchema,
  ZoneSchema,
  ZoneTableLayoutResultSchema,
  addressPart,
  aiChangesetAppliedSchema,
  aiChangesetSessionSchema,
  isAiAttachmentMimeType,
  isAiVoiceAudioType,
  menuChangeKey,
  menuChangeTarget,
  requiredAddressPart
};
//# sourceMappingURL=index.mjs.map